import React, { useState, useEffect, useRef, useMemo, useCallback, lazy, Suspense } from "react";
import PastDueBanner from "./components/PastDueBanner";
import { createPortal } from "react-dom";
import { Users, Target, Megaphone, Brain, Lightbulb, Lock, ChevronRight, X, Shield, Video, CalendarDays, Plus, ChevronDown, Zap, Play, Flame, Trophy, MessageCircle, Building2, ArrowRight, Film as FilmIcon, Dumbbell, Compass, Link2, GraduationCap, Gift } from "lucide-react";
import { FilmMomentPicker } from "./community/FilmMomentPicker";
import { isNative } from "../utils/platform";
import { haptic, setStatusBar, openExternal } from "../utils/nativeBridge";
import { supabase } from "../supabaseClient";
import { useAdmin } from "./shared/AdminContext";
import { UserAdminPanel } from "./shared/UserAdminPanel";
import { FavListsToolbar, FavCard } from "./shared/FavLists";
import { GOLD, DARK, LIGHT, SBborder, SBtext, SBmid, SBdim, getAC } from "./constants/theme";
import { BAM_LOGO_PNG } from "./constants/branding";
import { Ic } from "./constants/icons";
import { NAV, SECTION_COLORS, CONTENT, sectionLabel } from "./constants/navigation";
import { useNewContent } from "./hooks/useNewContent";
import { useUnexplored } from "./hooks/useUnexplored";

// How far back the community feed reaches. It was 50 with no pagination and
// no "my posts" view, so once the board passed fifty posts a coach's own
// thread — and the replies to it — became unreachable. 300 covers the whole
// history today; the Mine filter below is what keeps it findable regardless.
// One page of the feed. The first page is the pinned posts plus this many;
// "Load more" fetches the next page before the oldest loaded post. It was
// 300 in one call, and 300 cards on first paint is most of why the
// community felt slow on a phone.
const COMMUNITY_FEED_LIMIT = 40;
import { isAllowedBetaUser } from "./constants/addons";
import { BamIQProvider, BamIQOrb, BamIQPanel, BamIQWorkspace, useBamIQShortcut } from "./bamiq/BamIQ";
import { isFeatureOn, mapAvailable, internHubAvailable, bamiqAvailable, filmdeskAvailable } from "./utils/featureFlags";
import ComingSoonScreen from "./components/ComingSoonScreen";
import { logEvent } from "./utils/analytics";
import { assistHeaders } from "./utils/assistHeaders";
import { metaTrack } from "../utils/metaPixel";
import { initIAP, isIAPActive } from "../utils/iap";
import { loadUserData, saveUserData, prefetchUserData, clearUserDataCache } from "./utils/userData";
import { getAllUpcomingCalls, getAllUpcomingCallsMulti, fmtCountdown } from "./utils/calls";
import { PROMPTS, MEMBERS, ALL_CONTENT_ITEMS as STATIC_CONTENT_ITEMS, ALL_TAGS_COMM } from "./utils/prompts";
import { useWeeklyQa, qaSearchItems } from "./masterclass/weeklyQa";
// WeeklyQaFeature is now a candidate for the notice slot rather than a
// strip of its own; only the freshness rule is still read here.
import { isFreshQa } from "./content/WeeklyQaFeature";
import { useAllDrills } from "./hooks/useDrills";
import "./styles/dashboard.css";
import { ContentPage } from "./content/ContentPage";
import { PRO_TRANSITION, PRO_TRANSITION_MS } from "./constants/collections";
import { resolveDarkMode, announceDarkMode } from "./utils/darkMode";
import { parseResearchRoute } from "./research/collectionNav";
import { XOPage } from "./content/XOPage";
import { VocabPage } from "./content/VocabPage";
const FoundationsPage = lazy(() => import("./content/FoundationsPage"));
// Research collections — a curated reading path through the research
// shelf. Three pages behind one `research/` route prefix. Lazy because
// most sessions never open them.
const ResearchCollectionsPage = lazy(() => import("./research/ResearchCollectionsPage"));
const ResearchCollectionPage  = lazy(() => import("./research/ResearchCollectionPage"));
const ResearchStudyPage       = lazy(() => import("./research/ResearchStudyPage"));
const DropRoute               = lazy(() => import("./drops/DropRoute"));
// What's here — the tour. Lazy because most sessions never open it, and the
// ones that do are not in a hurry.
const TourPage = lazy(() => import("./pages/TourPage"));
// PickUpRow was the three-cell strip above the greeting: lab nudge,
// unexplored suggestion, certification. All three are candidates for the
// one notice slot now. The component is kept on disk, unused here.
// The Learning Lab challenge a coach took today, carried to the surface
// they land on. Renders nothing when there isn't one.
// TodayChallenge rendered under the pick-up row while the Lab was open.
// Kept on disk; nothing on the dashboard renders it.
const LearningLabPage = lazy(() => import("./content/LearningLabPage"));
import { hasAnyChat, listMyChats, getProfiles as chatGetProfiles, displayName as chatDisplayName } from "./chat/chatApi";
import { useChatsListRealtime } from "./chat/useChatRealtime";
import { getMyReferralStats } from "./utils/affiliateRef";
import { PostCard } from "./community/PostCard";
import { ProfilePanel } from "./community/ProfilePanel";
import { UserNameWithBadges } from "./components/UserNameWithBadges";
import { useOrg } from "./shared/OrgContext";
import { Glide, CompleteCheck } from "./shared/motion";
import { SkeletonPost } from "./components/Skeleton";
import { SearchBar } from "./components/SearchBar";
import { Globe } from "./map/Globe";
import { SignInScreen } from "./components/SignInScreen";
import { QuickSignup } from "./components/QuickSignup";
import { pendingInvite, pendingTaggerInvite, pendingPurchaseHash, pendingStaffInvite } from "./film/afsInvite";
import { afsInSidebar } from "./film/afsPitch";
import { pendingClaim } from "./blueprint/blueprintClaim";
import { hasOrgOrStaffAccess } from "./utils/orgAccess";
import { useInternAccess, hubVisible } from "./interns/internApi";
import { canUseFilmDesk, isRemoteIntern } from "./filmdesk/filmdeskAccess";
import { ProfileSetup } from "./components/ProfileSetup";
import { TourOverlay } from "./components/TourOverlay";
import { FilmStudioIntroModal } from "./components/FilmStudioIntroModal";
import { DashIntro } from "./components/DashIntro";
import { MyProfilePanel } from "./profile/MyProfilePanel";
import { UploadPortal } from "./components/UploadPortal";
import { Paywall } from "./components/Paywall";
import { needsPaywall, certOwnerRedirect, certComplimentaryActive, iapStoredActive } from "./utils/certAccess";
import { NativeSubscriptionGate } from "./components/NativeSubscriptionGate";
import { MobileBottomNav } from "./components/MobileBottomNav";
import { AskBAM } from "./components/AskBAM";
import { SidebarLive } from "./components/SidebarLive";
import { LayoutTour, markLayoutTourSkipped } from "./components/LayoutTour";
import { FeatureIntro } from "./components/FeatureIntro";
import { ProfileMenu, PROFILE_MENU_ROWS } from "./components/ProfileMenu";
import { buildGroups, groupOf } from "./constants/sidebarGroups";
import { LearnHome } from "./content/LearnHome";
import { PullToRefresh } from "./components/PullToRefresh";
import { SettingsPage, fmtTimeInTz, fmtDayInTz, fmtFullDateInTz } from "./pages/SettingsPage";
import { CERT_UNITS, unitLessonsDone } from "./certification/constants";
import { CommandPalette } from "./components/CommandPalette";
import { WorkoutOfWeek } from "./components/WorkoutOfWeek";
import { startHereRemaining, STEPS as START_HERE_STEPS, stepForArrival, INTRO_TAG, INTRO_STARTER } from "./components/startHereSteps";
import { readProgressIndex, readOpenedMasterclasses, INDEX_KEY as MC_INDEX_KEY } from "./masterclass/useMasterclassProgress";
import { masterclassCover } from "./masterclass/masterclassCovers";
import { avatarSrc } from "./utils/avatar";
import { BrandLoader, BreathingMark } from "./components/BrandMark";
import { NextUp } from "./components/NextUp";
import { ActionPaths } from "./components/ActionPaths";
import { rankNextActions, visibleActions, unexploredAllowed } from "./constants/nextAction";
import { billingState } from "./constants/billing";
// Calls. Lazy: it ticks a clock every second and most visits never open it.
const CallsPage = lazy(() => import("./calls/CallsPage").then(m => ({ default: m.CallsPage })));
import { CreateYourWorkoutModal } from "./components/CreateYourWorkoutModal";
import { DrillOfDay } from "./components/DrillOfDay";
import { SetOfDayCard } from "./content/SetOfDayCard";
import { SetsLaunchModal } from "./components/SetsLaunchModal";
import { ThisWeeksDrops } from "./components/ThisWeeksDrops";
import DropCard, { DropNextLine, DropBar } from "./drops/DropCard";
import { loadLatestDrop } from "./drops/dropsApi";
import { currentDrop, dropCounts } from "./drops/dropModel";

// The weekly coach drop banner is out of place on tool and utility surfaces
// (the Film Desk and the Intern Hub, settings, org/consulting, dev studio).
const DROP_HIDE_NAVS = new Set(["filmdesk", "interns", "settings", "org", "consulting", "dev-studio"]);
import { AdminErrorBoundary } from "./admin/AdminErrorBoundary";

// ── Code-split heavy pages (loaded on-demand) ──
const PlayerDevStudio = lazy(() => import("./dev-studio"));
const ToolboxPage = lazy(() => import("./toolbox"));
const PlaybookBuilder = lazy(() => import("./playbook"));
const CertificationPage = lazy(() => import("./certification"));
const ResourcesPage = lazy(() => import("./pages/ResourcesPageLazy"));
const MemberMapOverlay = lazy(() => import("./map/MemberMapOverlayLazy"));
const FilmStudio = lazy(() => import("./film"));
const PortalPage = lazy(() => import("./portal"));
// Post-checkout "get the app" step. Lazy because it renders for a coach once,
// in the minute after they subscribe, and never again.
const GetTheApp = lazy(() => import("./components/GetTheApp"));
// Only loaded when somebody reaches for a drill to attach.
const DrillPicker = lazy(() => import("./community/DrillPicker"));
const FeaturePicker = lazy(() => import("./community/FeaturePicker"));
const ChatsPage = lazy(() => import("./chat/ChatsPage"));
const ReferralPanel = lazy(() => import("./affiliate/ReferralPanel"));
// Parked — full-video Auto-AFS pipeline (the model-picker "analyze the
// video" direction). Not a current direction: coaches tag a full game
// MANUALLY with the Co-pilot in AFS Mode instead. The lab component files
// (AutoAfsLab.jsx, AfsCompare.jsx) are kept on disk for a possible later
// revisit; to bring the surface back, uncomment these two imports, the
// VALID_ROUTES entries, the parseHash afs-compare clause, and the render
// block below (all tagged "Parked — full-video Auto-AFS").
// const AutoAfsLab = lazy(() => import("./lab/AutoAfsLab"));
// const AfsCompare = lazy(() => import("./lab/AfsCompare"));
// ShotFlow Lab — shooting-fluidity analyzer beta (bam-fluidity engine
// ported to Modal). Same allowlist-lab pattern as Auto-AFS.
const ShotflowLab = lazy(() => import("./lab/ShotflowLab"));
const AfsCopilotAccuracy = lazy(() => import("./lab/AfsCopilotAccuracy"));
const AFSPilot = lazy(() => import("./film/AFSPilot"));
// BAM Consulting (Phase 1) — landing + per-org dashboard.
// Lazy because most coaches aren't org members and won't ever load it.
const ConsultingLanding = lazy(() => import("./consulting/ConsultingLanding"));
const OrgDashboard = lazy(() => import("./consulting/OrgDashboard"));
const InternHub = lazy(() => import("./interns/InternHub"));
const FilmDesk = lazy(() => import("./filmdesk/FilmDesk"));
// OrgManagement is now imported lazily inside PeopleSection.

// Constraint Library — browse / capture / admin review. Self-contained
// router mounted from BAMFull for both #/library/constraints and
// #/admin/constraints (see early-return in the render fn below).
const ConstraintRouter = lazy(() => import("./constraints/ConstraintRouter"));

// Admin — 5 section pages each own the lazy imports of the tools
// they host (Dashboard, Members, ContentManager, …). BAMFull only
// needs the section roots + IntelligencePage (its own shell).
const HomeSection      = lazy(() => import("./admin/HomeSection"));
const WorkspaceSection = lazy(() => import("./admin/WorkspaceSection"));
const ContentSection   = lazy(() => import("./admin/ContentSection"));
const PeopleSection    = lazy(() => import("./admin/PeopleSection"));
const MoneySection     = lazy(() => import("./admin/MoneySection"));
const CertAiSection    = lazy(() => import("./admin/CertAiSection"));
const IntelligencePage = lazy(() => import("./admin/IntelligencePage"));
const AfsSection = lazy(() => import("./admin/AfsSection"));
const OutreachSection = lazy(() => import("./admin/OutreachSection"));
// (AdminErrorBoundary imported above with the other static imports.)

// ── Suspense fallback (minimal, branded) ──
const LazyFallback = () => (
  <div style={{display:"flex",alignItems:"center",justifyContent:"center",padding:60,flex:1}}>
    <div style={{width:28,height:28,border:`3px solid ${GOLD}33`,borderTop:`3px solid ${GOLD}`,borderRadius:"50%",animation:"spin 1s linear infinite"}}/>
  </div>
);

// ── Error boundary ─────────────────────────────────────────────────────
// Two failure modes:
//
//   1. ChunkLoadError (stale-cache-after-deploy). User has a tab open
//      with an old main.*.js; we shipped a new bundle; the moment they
//      navigate to a section that triggers a lazy import, fetching the
//      old chunk hash 404s. Previously this auto-reloaded silently —
//      which felt like a "crash" to the user (they reported it as one).
//      Now we render a tasteful "Update available" banner instead and
//      let them opt in, with a 10s safety auto-reload so they don't get
//      stuck if they don't notice.
//
//   2. Any other thrown render error. Surfaces the generic SOMETHING
//      WENT WRONG card with a Try Again button.
class BAMErrorBoundary extends React.Component {
  constructor(p) {
    super(p);
    this.state = { hasError: false, error: null, isChunkError: false };
    this._autoReloadTimer = null;
  }
  static getDerivedStateFromError(e) {
    const isChunkError = !!(e?.name === "ChunkLoadError"
      || e?.message?.includes("Loading chunk")
      || e?.message?.includes("Failed to fetch dynamically imported module"));
    return { hasError: true, error: e, isChunkError };
  }
  componentDidCatch(e, info) {
    console.error("[BAM Error Boundary]", e, info);
    // Report to Sentry with a chunk_error vs render_error tag so we
    // can build a dashboard showing stale-deploy frequency over time.
    // Without this distinction, chunk errors get drowned in the
    // generic unhandled-error stream and we can't tell whether the
    // version-pill fix is actually reducing them.
    try {
      // Route through the global capture shim (index.js) instead of a
      // static require — requiring @sentry/react here would pull the whole
      // ~1.8MB client back into the main boot chunk and undo Sentry's
      // deferral. The shim buffers until the deferred client loads, so a
      // boundary error this early in boot is still reported, not lost.
      window.__bamCapture?.(e, {
        tags: {
          boundary: "bam_main",
          error_type: this.state.isChunkError ? "chunk_error" : "render_error",
        },
        extra: {
          component_stack: info?.componentStack,
          chunk_request: e?.request || e?.fileName || null,
          // Embed the running main.*.js hash so we can correlate which
          // stale build was open when the error fired. Useful for
          // catching "deploy X was particularly hostile to stale tabs."
          running_main_hash: (function () {
            try {
              const tag = document.querySelector('script[src*="main."][src*=".js"]');
              const m = tag?.src?.match(/main\.([a-f0-9]+)\.js/);
              return m ? m[1] : null;
            } catch { return null; }
          })(),
        },
      });
    } catch { /* Sentry not configured in this environment */ }

    // Stale-cache-after-deploy: schedule a safety auto-reload 10s out.
    // If the user clicks the banner first we cancel it; if they don't
    // notice the banner they still recover automatically.
    if (this.state.isChunkError && !this._autoReloadTimer) {
      this._autoReloadTimer = setTimeout(() => {
        try { window.location.reload(); } catch {}
      }, 10000);
    }
  }
  componentWillUnmount() {
    if (this._autoReloadTimer) clearTimeout(this._autoReloadTimer);
  }
  reloadNow = () => {
    if (this._autoReloadTimer) clearTimeout(this._autoReloadTimer);
    try { window.location.reload(); } catch {}
  };
  resetError = () => {
    if (this._autoReloadTimer) clearTimeout(this._autoReloadTimer);
    this.setState({ hasError: false, error: null, isChunkError: false });
  };
  render() {
    if (!this.state.hasError) return this.props.children;

    // ── Stale deploy: friendly update banner instead of error wall ──
    if (this.state.isChunkError) {
      return (
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",padding:40,textAlign:"center",background:"#0E0E0E",color:"#F5F5F5",fontFamily:"'DM Sans',sans-serif"}}>
          {/* Soft gold halo behind the mark — recycles NativeSplash vibe */}
          <div aria-hidden="true" style={{position:"absolute",width:380,height:380,borderRadius:"50%",background:"radial-gradient(circle, rgba(226,221,159,0.14) 0%, rgba(226,221,159,0.03) 45%, transparent 70%)",filter:"blur(30px)",pointerEvents:"none"}}/>
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#E2DD9F" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{position:"relative"}}>
            <path d="M21 12a9 9 0 1 1-3-6.7L21 8"/><polyline points="21 3 21 8 16 8"/>
          </svg>
          <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:30,color:"#E2DD9F",letterSpacing:3,marginTop:18,position:"relative"}}>NEW VERSION AVAILABLE</div>
          <div style={{fontSize:13.5,color:"#9CA3AF",marginTop:10,maxWidth:380,lineHeight:1.6,position:"relative"}}>
 We shipped an update since you opened this tab. Reload to pick up the latest: your work is safe.
          </div>
          <button onClick={this.reloadNow} style={{marginTop:24,padding:"12px 32px",borderRadius:10,fontSize:13,fontWeight:800,background:"#E2DD9F",color:"#111",border:"none",cursor:"pointer",fontFamily:"'Bebas Neue',sans-serif",letterSpacing:1.5,boxShadow:"0 8px 24px rgba(226,221,159,0.25)",position:"relative"}}>RELOAD NOW</button>
          <div style={{marginTop:14,fontSize:11,color:"#6C6658",position:"relative"}}>Auto-reloading in 10s…</div>
        </div>
      );
    }

    // ── Generic uncaught error ──
    return (
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",padding:40,textAlign:"center",background:"#111",color:"#ccc",fontFamily:"'DM Sans',sans-serif"}}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#E2DD9F" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:28,color:"#E2DD9F",letterSpacing:3,marginTop:16}}>SOMETHING WENT WRONG</div>
        <div style={{fontSize:14,color:"#999",marginTop:8,maxWidth:340,lineHeight:1.6}}>An unexpected error occurred. Try refreshing the page.</div>
        <button onClick={this.resetError} style={{marginTop:20,padding:"10px 28px",borderRadius:8,fontSize:14,fontWeight:800,background:"#E2DD9F",color:"#111",border:"none",cursor:"pointer",fontFamily:"'Bebas Neue',sans-serif",letterSpacing:1}}>Try Again</button>
      </div>
    );
  }
}

// Prompt 9: localStorage version
const BAM_LS_VERSION="1.0.0";
const BAM_LS_KEYS=["bam_dark_mode","bam_settings_notifs","bam_settings_tz","bam_map_visible","bam_dismissed_banners","bam_watched_ids","bam_fav_drills","bam_dismissed_ann","bam_expand_modules","onboardingComplete","bam_ls_version"];
(function bamLsCleanup(){try{const v=localStorage.getItem("bam_ls_version");if(v&&v!==BAM_LS_VERSION){const keys=Object.keys(localStorage);keys.forEach(k=>{if(k.startsWith("bam_")&&!BAM_LS_KEYS.includes(k))localStorage.removeItem(k);});localStorage.setItem("bam_ls_version",BAM_LS_VERSION);}else if(!v){localStorage.setItem("bam_ls_version",BAM_LS_VERSION);}}catch{}})();

// Prompt 10: Mobile Safari viewport fix.
// Drive --app-height from visualViewport.height when available — on iOS with
// the keyboard up, window.innerHeight does NOT reliably shrink, which left a
// black bar between the content and the keyboard. visualViewport.height tracks
// the actual visible area (keyboard excluded), so the app fills exactly the
// space above the keyboard.
(function bamViewportFix(){
  if(typeof window==="undefined")return;
  const vv = window.visualViewport;
  const set=()=>{
    const h = (vv && vv.height) ? vv.height : window.innerHeight;
    document.documentElement.style.setProperty("--app-height", h+"px");
  };
  set();
  window.addEventListener("resize", set);
  if(vv){ vv.addEventListener("resize", set); vv.addEventListener("scroll", set); }
})();

// Format a duration in seconds for the dashboard "Time on Platform"
// stat. Sub-hour times read as minutes ("47m"); hour-and-up as
// "Hh Mm" with the minutes dropped past 24h to keep the cell tight.
function formatTimeOnPlatform(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
 if (s < 60) return s === 0 ? "-" : `${s}s`;
  const totalMins = Math.floor(s / 60);
  if (totalMins < 60) return `${totalMins}m`;
  const hrs = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  if (hrs >= 24) {
    const days = Math.floor(hrs / 24);
    const leftoverHrs = hrs % 24;
    return leftoverHrs > 0 ? `${days}d ${leftoverHrs}h` : `${days}d`;
  }
  return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
}

// Per-device fallback aggregator for session stats. Used when the
// user_dashboard_stats RPC isn't available (migration not run yet, or
// transient DB error). Reads bam_analytics_events from localStorage,
// sums session_end durations, and walks back from today through
// distinct active dates to compute a streak.
function computeLocalSessionStats() {
  let timeSeconds = 0;
  const dateSet = new Set();
  try {
    const raw = localStorage.getItem("bam_analytics_events") || "[]";
    const events = JSON.parse(raw);
    if (Array.isArray(events)) {
      for (const e of events) {
        if (e?.name === "session_end" && typeof e.duration === "number") {
          timeSeconds += e.duration;
        }
        if (typeof e?.ts === "number") {
          const d = new Date(e.ts);
          dateSet.add(d.toISOString().slice(0, 10));
        }
      }
    }
  } catch {}
  // Streak: walk backward from today (or yesterday — one-day grace so
  // a coach pre-coffee doesn't see the streak drop to 0).
  let streak = 0;
  const today = new Date();
  let cursor = new Date(today);
  if (!dateSet.has(cursor.toISOString().slice(0, 10))) {
    cursor.setUTCDate(cursor.getUTCDate() - 1);
    if (!dateSet.has(cursor.toISOString().slice(0, 10))) {
      return { timeSeconds, streak: 0 };
    }
  }
  while (dateSet.has(cursor.toISOString().slice(0, 10))) {
    streak += 1;
    cursor.setUTCDate(cursor.getUTCDate() - 1);
  }
  return { timeSeconds, streak };
}

// Loading state for the community feed: three posts in the shape of the
// real ones (author, two lines, the action strip), shimmering, so the feed
// keeps its shape and the posts land where the placeholders were rather
// than replacing a spinner. The second carries a media block, because a
// feed of only text placeholders reads shorter than the real thing.
function CommunityLoader() {
  return (
    <div role="status" aria-label="Loading the community">
      <SkeletonPost/>
      <SkeletonPost media/>
      <SkeletonPost/>
    </div>
  );
}

// Boot-timing instrumentation. One-shot diagnostic for the 10–15s
// community page load on launch day. Logs ms-since-app-mount at each
// boundary (auth, subscription, community fetch start/end, render). To
// disable later, just remove the calls — `bamPerf` is local to this file.
const bamPerfStart = (typeof performance !== "undefined" ? performance.now() : Date.now());
const bamPerf = (label) => {
  const t = (typeof performance !== "undefined" ? performance.now() : Date.now()) - bamPerfStart;
  // eslint-disable-next-line no-console
  console.log(`[BAMPerf +${t.toFixed(0)}ms] ${label}`);
};

// ── Community feed cache (stale-while-revalidate) ─────────────────────────
// The feed paints from the last successful fetch instantly on boot, then the
// network refresh replaces it in place. Turns every repeat visit's perceived
// community load time into ~0ms — the loader only ever shows on a genuinely
// first visit (or after 24h of staleness, when yesterday's feed would read
// as today's). Posts only; likes/bookmarks are cheap sets that hydrate live.
/** Past this, a feed load is reported as slow (logEvent + Sentry). */
const SLOW_FEED_MS = 4000;
const COMMUNITY_CACHE_KEY = "bam_community_cache_v1";
const COMMUNITY_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
// Local relative-time formatter for re-stamping cached posts' `time` field.
// Deliberately duplicates communityApi.formatRelativeTime — importing it
// statically would pull the whole lazily-chunked communityApi module into
// the main boot bundle, undoing the code-split.
const relTime = (iso) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h`;
  if (mins < 10080) return `${Math.floor(mins / 1440)}d`;
  return new Date(iso).toLocaleDateString();
};
function readCommunityCache() {
  try {
    const raw = localStorage.getItem(COMMUNITY_CACHE_KEY);
    if (!raw) return [];
    const { at, posts } = JSON.parse(raw);
    if (!Array.isArray(posts) || Date.now() - (at || 0) > COMMUNITY_CACHE_MAX_AGE_MS) return [];
    return posts.map(p => ({ ...p, time: relTime(p.createdAt) }));
  } catch { return []; }
}
function writeCommunityCache(posts) {
  try {
    // Cap the stored slice — embeds can be heavy and localStorage quota is
    // ~5MB shared with auth/session state. 30 posts is more than a screen.
    localStorage.setItem(COMMUNITY_CACHE_KEY, JSON.stringify({ at: Date.now(), posts: posts.slice(0, 30) }));
  } catch { /* quota/private-mode — cache is best-effort */ }
}

/* Content attached to a post from the composer: "Share to community" on a
   drill, or the @ / embed list. It used to be saved as {id, sectionId,
   title, tag, duration} with no `type`, and PostEmbeds only draws embeds it
   recognises by type, so every drill shared this way was stored on the post
   and never shown. Same shape as the Drill button's (DrillPicker.drillEmbed),
   with the old fields kept so the composer's chip still reads them. */
function contentEmbed(item){
  const pb=item.muxPlaybackId||item.mux_playback_id||null;
  return {
    type:"drill",
    drill_id:String(item.id),
    title:item.title||"Drill",
    section:item.sectionId||item.section||null,
    category:item.tag||item.category||null,
    duration:item.duration||null,
    thumbnail_url:pb?`https://image.mux.com/${pb}/thumbnail.jpg?width=640&height=400&fit_mode=smartcrop`:null,
    id:item.id, sectionId:item.sectionId||item.section||null, tag:item.tag||item.category||null,
  };
}

// ⌘J / Ctrl+J for BAM IQ; a component so it sits inside BamIQProvider.
function BamIQShortcut({ onIqPage }) { useBamIQShortcut({ onIqPage }); return null; }

export default function BAMFull(){
  // ── Supabase auth state ──
  const [sbUser,setSbUser]=useState(null); // Supabase user object (has .id UUID)
  const [authLoading,setAuthLoading]=useState(true);
  // Set when a token refresh fails (expired/revoked session) so the sign-in
  // screen can say so. Persisted so it survives the reload back to sign-in.
  const [sessionExpired,setSessionExpired]=useState(()=>{ try { return localStorage.getItem("bam_session_expired")==="1"; } catch { return false; } });
  const [showWelcomeBanner,setShowWelcomeBanner]=useState(false);
  const [onboarded,setOnboarded]=useState(false);
  const [showSignIn,setShowSignIn]=useState(false);

  // ── Subscription state ──
  // null = unknown/loading, true = active subscriber, false = not subscribed
  const [isSubscribed,setIsSubscribed]=useState(null);
  // True when a checkout succeeded but the Stripe webhook never flipped the
  // flag within the poll window — we show a reassuring "finalizing" screen
  // instead of dumping the paying coach back on the paywall.
  const [checkoutStuck,setCheckoutStuck]=useState(false);
  // Shown once, right after a subscription activates, on web only.
  const [promptForApp,setPromptForApp]=useState(false);
  const [profileSetupDone, setProfileSetupDone] = useState(false);
  // Cert ownership: the Certification's launch promise is FULL platform
  // access for life, so cert_purchased=true resolves isSubscribed=true in
  // the membership check below (no Stripe sub needed, ever). This flag is
  // still tracked separately for cert-route UI (badges, nav, progress).
  const [hasCert, setHasCert] = useState(false);
  // Past-due billing, for the banner and the read-only gate. Null until the
  // profile resolves, which billingState() reads as "in good standing" —
  // the right default, since flashing a payment warning at every coach on
  // every cold start would be far worse than showing it a second late.
  const [billingProfile, setBillingProfile] = useState(null);

  // Check existing session on mount + listen for auth changes
  useEffect(()=>{
    bamPerf("BAMFull mount, calling auth.getSession");
    let mounted=true;
    // Safety net: getSession() can hang or reject on a flaky WKWebView cold
    // start or a Supabase Web-Locks deadlock (see index.js). Without this the
    // app sticks on "Signing you in…" forever. Fall through to the sign-in
    // screen after 8s, and on any rejection, so the user is never trapped.
 const authTimeout = setTimeout(()=>{ if(mounted){ bamPerf("auth.getSession TIMEOUT: falling through"); setAuthLoading(false); } }, 8000);
    supabase.auth.getSession().then(({data:{session}})=>{
      bamPerf(`auth.getSession resolved (user=${session?.user ? "yes" : "no"})`);
      if(!mounted) return;
      clearTimeout(authTimeout);
      if(session?.user){
        setSbUser(session.user);
        setOnboarded(true);
        // Attach user to Sentry events. No-op if Sentry isn't configured.
        try { window.Sentry?.setUser({ id: session.user.id }); } catch {}
      }
      setAuthLoading(false);
    }).catch((e)=>{
      if(!mounted) return;
      clearTimeout(authTimeout);
      bamPerf(`auth.getSession REJECTED: ${e?.message||e}`);
      setAuthLoading(false);
    });
    const {data:{subscription}}=supabase.auth.onAuthStateChange((event,session)=>{
      if(!mounted) return;
      // Route password recovery to reset page
      if(event === "PASSWORD_RECOVERY"){
        window.location.hash = "#/reset";
        return;
      }
      // Token refresh failed (expired/revoked session — e.g. the app sat
      // backgrounded for days). Flag it so the sign-in screen can explain
      // "your session expired" instead of silently bouncing the user.
      if(event === "TOKEN_REFRESH_FAILED"){
        try { localStorage.setItem("bam_session_expired","1"); } catch {}
        setSessionExpired(true);
      }
      if(session?.user){
        setSbUser(session.user);
        setOnboarded(true);
        try { window.Sentry?.setUser({ id: session.user.id }); } catch {}
        // Welcome banner for first sign-in after email confirmation
        if(event === "SIGNED_IN"){
          try {
            const shown = localStorage.getItem("bam_welcome_shown");
            const justSignedUp = localStorage.getItem("bam_just_signed_up");
            if(justSignedUp && !shown){
              setShowWelcomeBanner(true);
              localStorage.setItem("bam_welcome_shown","1");
              localStorage.removeItem("bam_just_signed_up");
              setTimeout(()=>setShowWelcomeBanner(false), 8000);
            }
          } catch {}
        }
      } else {
        setSbUser(null);
        setOnboarded(false);
        setIsSubscribed(null);
      }
    });
    return ()=>{mounted=false;subscription.unsubscribe();};
  },[]);

  // Check subscription status whenever the user changes
  useEffect(()=>{
    if(!sbUser){setIsSubscribed(null);setHasCert(false);return;}
    bamPerf("subscription check effect fired");
    let cancelled=false;

    // ── PRE-LAUNCH BYPASS ──
    // 1. Founder/dev email allowlist — these users always get in
    // 2. localStorage.bam_dev_bypass = "true" for temporary manual bypass
    // 3. URL ?bypass=1 for one-off access (persists via localStorage)
    // 4. REACT_APP_BYPASS_PAYWALL env var for Vercel-level override
    const FOUNDER_EMAILS = new Set([
      "coleman@byanymeansbball.com",
      "coleman@byanymeanscoaches.com",
      "gabe@byanymeansbball.com",
      "mike@byanymeansbball.com",
      "elijah@byanymeansbball.com",
    ]);
    try{
      const urlParams = new URLSearchParams(window.location.search);
      if(urlParams.get("bypass")==="1"){ localStorage.setItem("bam_dev_bypass","true"); }
    }catch{}
    const emailBypass = sbUser?.email && FOUNDER_EMAILS.has(sbUser.email.toLowerCase());
    let lsBypass = false;
    try{ lsBypass = localStorage.getItem("bam_dev_bypass")==="true"; }catch{}
    const envBypass = process.env.REACT_APP_BYPASS_PAYWALL === "true";
    if(emailBypass || lsBypass || envBypass){
      bamPerf("subscription resolved via bypass");
      setIsSubscribed(true);
      return;
    }

    (async()=>{
      try{
        bamPerf("subscription DB query starting");
        // Read stripe_subscription_active flag from profiles table
        // (backend Stripe webhook flips this true/false based on subscription events).
        // Also read cert_purchased so cert holders can bypass the
        // platform paywall for /#/certified routes.
        // cert_founding ships in the 2026-07-30 migration; retry without it
        // so membership still resolves pre-migration (where every cert owner
        // IS founding, so cert_purchased is the correct stand-in).
        // billing_status / grace_ends_at ship in docs/billing-past-due.sql.
        // Asked for first and retried without on failure, the same shape as
        // the cert_founding fallback below, so this renders correctly
        // whether or not that SQL has been run yet.
        let {data:rows,error}=await supabase
          .from("profiles")
          .select("stripe_subscription_active,stripe_customer_id,stripe_subscription_id,iap_subscription_active,iap_expires_at,cert_purchased,cert_founding,cert_pause_resumes_at,billing_status,grace_ends_at,is_admin")
          .eq("id",sbUser.id)
          .limit(1);
        if(error){
          ({data:rows,error}=await supabase
            .from("profiles")
            .select("stripe_subscription_active,stripe_customer_id,stripe_subscription_id,iap_subscription_active,iap_expires_at,cert_purchased,cert_founding,cert_pause_resumes_at,is_admin")
            .eq("id",sbUser.id)
            .limit(1));
        }
        if(error){
          ({data:rows,error}=await supabase
            .from("profiles")
            .select("stripe_subscription_active,stripe_customer_id,stripe_subscription_id,cert_purchased")
            .eq("id",sbUser.id)
            .limit(1));
        }
        bamPerf(`subscription DB query resolved (active=${!!rows?.[0]?.stripe_subscription_active}, cert=${!!rows?.[0]?.cert_purchased})`);
        if(cancelled) return;
        if(error){
 console.warn("[subscription check] supabase error ", error.message);
          // Transient/network error: do NOT downgrade a coach we previously
          // confirmed as subscribed to the paywall. Preserve a known-true
          // value; only an unknown (null/false) stays gated. A subscriber on
          // flaky network should never be told their payment lapsed.
          setIsSubscribed(prev => prev === true ? true : false);
          return;
        }
        const stripeActive = !!rows?.[0]?.stripe_subscription_active;
        const certOwned = !!rows?.[0]?.cert_purchased;
        // FOUNDING CERT = FULL ACCESS FOR LIFE: coaches who bought the cert
        // BEFORE the platform existed (CoachIQ era) were promised the whole
        // platform free forever. cert_founding marks exactly that cohort, so
        // for them every downstream gate (paywall, native subscription gate)
        // simply never fires. Post-launch cert buyers get cert content via
        // hasCert but still subscribe for the platform. Pre-migration, the
        // column is absent and cert_purchased stands in (at that point every
        // cert owner IS founding).
        const certFounding = rows?.[0]?.cert_founding !== undefined
          ? !!rows[0].cert_founding
          : certOwned;
        if(cancelled) return;
        // Resolve membership immediately off the Stripe flag so boot NEVER hangs
        // on "Verifying membership" if RevenueCat is slow. On iOS, check the Apple
        // IAP entitlement in the BACKGROUND and upgrade to subscribed if active —
        // a fresh purchase unlocks directly via the gate's onSubscribed.
        // The six months the cert page promises: "The first six months of the
        // platform are complimentary while you work through it." The webhook
        // writes cert_pause_resumes_at at purchase and nothing read it, so
        // every post-launch cert buyer was paywalled out of a window they had
        // already paid for.
        const compActive = certComplimentaryActive({
          certPurchased: certOwned,
          pauseResumesAt: rows?.[0]?.cert_pause_resumes_at,
        });
        // An Apple subscription as the webhook last recorded it. Counts on
        // the web too, and on iOS it lets a subscriber straight in instead of
        // showing the paywall until RevenueCat answers.
        const iapStored = iapStoredActive({ active: rows?.[0]?.iap_subscription_active, expiresAt: rows?.[0]?.iap_expires_at });
        const base = stripeActive || iapStored || certFounding || compActive;
        // A client org's staff and BAM admins are in without a subscription
        // (utils/orgAccess.js). Asked only when nothing else lets them in.
        const viaOrg = base ? false : await hasOrgOrStaffAccess(sbUser.id, rows?.[0]);
        if(cancelled) return;
        // Still nothing, on iOS: ask RevenueCat, but for a few seconds only, so
        // boot never hangs on "Verifying membership". A slow answer keeps
        // going in the background; "couldn't tell" is never read as "no".
        let iapLive = null;
        if(!base && !viaOrg && isNative()){
          iapLive = await Promise.race([
            initIAP(sbUser.id).then(()=>isIAPActive()),
            new Promise(r=>setTimeout(()=>r(null),3500)),
          ]).catch(()=>null);
          if(cancelled) return;
        }
        setIsSubscribed(base || viaOrg || iapLive===true);
        setHasCert(certOwned);
        setBillingProfile({
          billing_status: rows?.[0]?.billing_status ?? null,
          grace_ends_at: rows?.[0]?.grace_ends_at ?? null,
        });
        if(!base && !viaOrg && isNative() && iapLive!==true){
          initIAP(sbUser.id).then(()=>isIAPActive()).then(a=>{ if(a===true && !cancelled) setIsSubscribed(true); }).catch(()=>{});
        }
      }catch(e){
        if(cancelled) return;
        console.error("[subscription check] unexpected",e);
        setIsSubscribed(prev => prev === true ? true : false);
      }
    })();
    return ()=>{cancelled=true;};
    // Key on the user ID, not the object: onAuthStateChange re-emits the same
    // user as a NEW object right after getSession, which would otherwise fire
    // this whole check (and the community fetch) a second/third time on boot.
  },[sbUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Configure Apple IAP (RevenueCat) for the signed-in user so the subscribe
  // screen + entitlement checks work. No-op on web / idempotent on native.
  useEffect(()=>{ if(sbUser?.id) initIAP(sbUser.id); },[sbUser?.id]);

  // "Opened the iPhone app", once per launch and on returning to it after
  // half an hour away (week-one app installs, Coleman 2026-10-15). The
  // event is tagged platform "ios" like every event from the app.
  useEffect(()=>{
    if(!sbUser?.id || !isNative()) return;
    let last = 0, listener;
    const opened = () => { const now = Date.now(); if(now - last < 30*60*1000) return; last = now; logEvent("app_opened"); };
    opened();
    (async()=>{ try{ const { App } = await import("@capacitor/app"); listener = await App.addListener("appStateChange", ({isActive})=>{ if(isActive) opened(); }); }catch{} })();
    return ()=>{ try{ listener?.remove?.(); }catch{} };
  },[sbUser?.id]);

  // Dismiss the native splash as soon as auth resolves (app is ready to show),
  // instead of letting it sit until the Capacitor default-timeout. No-op on web.
  useEffect(() => {
    if (authLoading) return;
    (async () => { try { const { SplashScreen } = await import("@capacitor/splash-screen"); await SplashScreen.hide(); } catch {} })();
  }, [authLoading]);

  // Re-verify subscription when the app returns to the foreground (tab refocus
  // or native resume). A cancellation/renewal done elsewhere — or a lapse that
  // happened while the app sat backgrounded — otherwise wouldn't reflect until
  // a full reload: a canceled coach keeps access, a just-paid coach stays
  // gated. Bypass users are skipped so we never downgrade them.
  useEffect(()=>{
    if(!sbUser) return;
    const FOUNDER_EMAILS = new Set(["coleman@byanymeansbball.com","coleman@byanymeanscoaches.com","gabe@byanymeansbball.com","mike@byanymeansbball.com","elijah@byanymeansbball.com"]);
    let bypass = (sbUser?.email && FOUNDER_EMAILS.has(sbUser.email.toLowerCase())) || process.env.REACT_APP_BYPASS_PAYWALL==="true";
    try{ if(localStorage.getItem("bam_dev_bypass")==="true") bypass = true; }catch{}
    if(bypass) return;
    const recheck = async () => {
      if(document.visibilityState !== "visible") return;
      try{
        // The same rule as the boot check, or this downgrades people the boot
        // check let in. It used to read only the Stripe flag, so every return
        // to the foreground (every app switch on a phone) set a founding cert
        // holder to unsubscribed, and the gate below then forced them onto
        // #/certified on every tap. "No matter what I do it only shows me the
        // cert" was this line.
        let {data:rows,error}=await supabase.from("profiles").select("stripe_subscription_active,iap_subscription_active,iap_expires_at,cert_purchased,cert_founding,cert_pause_resumes_at,billing_status,grace_ends_at,is_admin").eq("id",sbUser.id).limit(1);
        if(error){
          ({data:rows,error}=await supabase.from("profiles").select("stripe_subscription_active,iap_subscription_active,iap_expires_at,cert_purchased,cert_founding,cert_pause_resumes_at,is_admin").eq("id",sbUser.id).limit(1));
        }
        if(error) return; // keep last-known on transient error
        const stripeActive = !!rows?.[0]?.stripe_subscription_active;
        const certOwned = !!rows?.[0]?.cert_purchased;
        const certFounding = rows?.[0]?.cert_founding !== undefined ? !!rows[0].cert_founding : certOwned;
        // Same rule as the boot check, or every return to the foreground
        // downgrades a coach the boot check correctly let in.
        const compActive = certComplimentaryActive({
          certPurchased: certOwned,
          pauseResumesAt: rows?.[0]?.cert_pause_resumes_at,
        });
        const iapStored = iapStoredActive({ active: rows?.[0]?.iap_subscription_active, expiresAt: rows?.[0]?.iap_expires_at });
        const base = stripeActive || iapStored || certFounding || compActive;
        const viaOrg = base ? false : await hasOrgOrStaffAccess(sbUser.id, rows?.[0]);
        if(base || viaOrg || !isNative()){
          setIsSubscribed(base || viaOrg);
        } else {
          // iOS with nothing on file: settle it with RevenueCat before
          // changing anything. Setting false first and upgrading after was
          // the paywall flashing on every return to the app.
          const a = await Promise.race([isIAPActive(), new Promise(r=>setTimeout(()=>r(null),4000))]).catch(()=>null);
          setIsSubscribed(prev => a===true ? true : a===null ? prev===true : false);
        }
        setHasCert(certOwned);
        setBillingProfile({
          billing_status: rows?.[0]?.billing_status ?? null,
          grace_ends_at: rows?.[0]?.grace_ends_at ?? null,
        });
      }catch{}
    };
    const onVis = () => { if(document.visibilityState==="visible") recheck(); };
    document.addEventListener("visibilitychange", onVis);
    let appListener;
    (async()=>{ try{ const { App } = await import("@capacitor/app"); appListener = await App.addListener("appStateChange", ({isActive})=>{ if(isActive) recheck(); }); }catch{} })();
    return ()=>{ document.removeEventListener("visibilitychange", onVis); try{ appListener?.remove?.(); }catch{} };
  },[sbUser]);

  // Handle post-Stripe-checkout success redirect (?checkout=success in URL)
  //
  // This effect keys on sbUser, which is an object: a token refresh or a
  // re-auth gives it a new identity and re-runs the effect. The URL is
  // only cleaned inside the success branch, up to 45 seconds later, so
  // every re-run during that window started ANOTHER poll loop and every
  // loop that reached success fired its own Purchase. That is the
  // clusters of three. A ref, not state, because it must be true for
  // the next run of this same effect rather than after a render.
  const checkoutHandledRef = useRef(false);
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    if(params.get("checkout")==="success" && sbUser && !checkoutHandledRef.current){
      checkoutHandledRef.current = true;
      logEvent("checkout_completed");
      // session_id is stamped on the Stripe success_url ({CHECKOUT_SESSION_ID});
      // we reuse it as the Meta Pixel eventID so the browser Purchase event
      // dedupes against the server-side CAPI Purchase fired from the webhook.
      const checkoutSessionId = params.get("session_id") || undefined;
      // Stripe webhook delivery is async and occasionally takes 20-40s in
      // production (especially right after a checkout.session.completed
      // for a $0 sub via promo code). We poll up to ~45s with 1.5s gaps
      // before giving up.
      let attempts=0;
      const maxAttempts=30; // 30 × 1.5s ≈ 45s
      const poll=async()=>{
        attempts++;
        let rows=null;
        try{
          ({data:rows}=await supabase
            .from("profiles")
            .select("stripe_subscription_active")
            .eq("id",sbUser.id)
            .limit(1));
        }catch(e){
          // A single network blip mid-poll must not kill the retry loop —
          // keep polling until we exhaust attempts.
          if(attempts<maxAttempts){ setTimeout(poll,1500); return; }
        }
        if(rows?.[0]?.stripe_subscription_active){
          setIsSubscribed(true);
          // Meta Pixel Purchase conversion (subscription campaign). Fires once
          // on confirmed activation; eventID = Stripe session id so it dedupes
          // with the server-side CAPI Purchase. Web-only / opt-out aware.
          // Only with a session id. Without one there is no event_id,
          // so this cannot dedupe against the Conversions API Purchase
          // and Meta counts the sale twice. The consulting checkout
          // lands on the same ?checkout=success without a session_id
          // and is a different product at a different price - it was
          // reporting itself here as a $34.99 platform subscription.
          if (checkoutSessionId) {
            const planValue = Number(params.get("plan_value"));
            metaTrack(
              "Purchase",
              { value: Number.isFinite(planValue) && planValue > 0 ? planValue : 34.99, currency: "USD" },
              checkoutSessionId,
            );
          }
          // Flag so the dashboard shows a one-time "trial started" /
          // "subscription active" celebration banner on the next render.
          // Cleared by the user dismissing the banner.
          try { localStorage.setItem("bam_just_subscribed", "1"); } catch {}
          // Ask for the install now, while intent is at its peak. Every push
          // we send can only land on the app, so a coach who never installs
          // it is unreachable no matter what we write.
          import("./components/GetTheApp")
            .then(m => { if (m.shouldPromptForApp()) setPromptForApp(true); })
            .catch(() => {});
          // Clean ?checkout=success from URL
          window.history.replaceState({},"",window.location.pathname+window.location.hash);
        } else if(attempts<maxAttempts){
          setTimeout(poll,1500);
        } else {
          // Poll exhausted — webhook hasn't fired in 45s. Clear the
          // ?checkout=success param and show a dedicated "payment received,
          // finalizing" screen (NOT the raw paywall — a coach who just paid
          // must never be told it failed).
          try { window.history.replaceState({}, "", window.location.pathname + window.location.hash); } catch {}
          setCheckoutStuck(true);
        }
      };
      poll();
    }
  },[sbUser]);

  const handleSignOut=useCallback(async()=>{
    // Don't let a failed network call (offline) abort the local cleanup —
    // otherwise "Sign out" silently does nothing and the user looks stuck
    // signed in. Always clear local state + redirect regardless.
    try { await supabase.auth.signOut(); } catch (e) { console.warn("signOut network error (clearing locally anyway):", e?.message||e); }
    setSbUser(null);setOnboarded(false);
    try{localStorage.removeItem("bam_auth_token");}catch{}
    // Clear the in-memory user_data cache. Without this, a fast
    // user-switch on the same browser tab (sign out → sign in as
    // someone else, no hard reload) would leak previous user's
    // cert_progress / video_notes / fav_drills to the new session.
    try{clearUserDataCache();}catch{}
    try { window.Sentry?.setUser(null); } catch {}
    // The local analytics buffer is this person's recent activity; the next
    // person on this browser shouldn't inherit it.
    try{localStorage.removeItem("bam_analytics_events");}catch{}
    window.location.hash="";
  },[]);

  // Prompt 2: dark mode with localStorage persistence + OS default
  // Shared with the shell in dashboard/index.js, which mounts the
  // announcement banner outside this tree and needs the same answer. The
  // two used to compute it separately and disagreed: this wrote
  // String(boolean), that compared against "1".
  const [dark,setDark]=useState(resolveDarkMode);
  const [tourActive,setTourActive]=useState(false);
  // Taking "What's here" means the new layout has been shown: no "We've
  // made things a bit easier" card on top of it (Coleman, 2026-10-07).
  useEffect(() => { if (tourActive) markLayoutTourSkipped("tour"); }, [tourActive]);
  // Film Studio first-look modal. Fires once per coach after auth +
  // onboarding completes, gated by the `film_studio_intro_seen` flag
  // in user_data. The trigger effect lives just below the onboarding
  // tour effect so both run on the same condition window. Suppressed
  // if the onboarding tour is also queued for this session, so new
  // sign-ups don't get hit with two modals back to back.
  const [filmIntroOpen,setFilmIntroOpen]=useState(false);
  const [loading,setLoading]=useState(true);
  useEffect(()=>{ if(!authLoading&&onboarded){const t=setTimeout(()=>setLoading(false),1800);return ()=>clearTimeout(t);}setLoading(false); },[authLoading,onboarded]);
  // Analytics: session tracking
  const sessionStartRef=useRef(Date.now());
  useEffect(()=>{
    logEvent("session_start",{});
    logEvent("page_view",{page:"dashboard"});
    const onUnload=()=>logEvent("session_end",{duration:Math.round((Date.now()-sessionStartRef.current)/1000)});
    window.addEventListener("beforeunload",onUnload);
    return ()=>window.removeEventListener("beforeunload",onUnload);
  },[]);

  // Auto-trigger onboarding tour once after ProfileSetup completes
  useEffect(() => {
    if (authLoading || !sbUser || !onboarded) return;
    try {
      // The tour used to fire itself here, 800ms after the first dashboard
      // paint. That is four seconds into the product, before a coach has a
      // single question, and it was our one shot: it never came back unless
      // they went hunting in Settings. One of them told us as much, a week
      // in, asking for "an intro course on how to use the platform" that
      // already existed and had been spent on them before they arrived.
      //
      // It is the first item on the Start Here card now, which waits.
      // Clearing the flag still matters so it cannot fire on some later
      // load; taking the tour is now a thing they choose to do.
      localStorage.removeItem("bam_should_start_tour");
    } catch {}
  }, [authLoading, sbUser, onboarded]);

  // Post-auth redirect for cert-purchase intent. The /#/buy-cert page
  // sets sessionStorage.bam_cert_purchase_intent before sending an
  // anon visitor through signup/signin. As soon as auth completes and
  // they land on the dashboard, we route them back so they continue
  // straight into Stripe Checkout instead of getting parked here.
  useEffect(() => {
    if (authLoading || !sbUser) return;
    try {
      if (sessionStorage.getItem("bam_cert_purchase_intent") === "1") {
        sessionStorage.removeItem("bam_cert_purchase_intent");
        window.location.hash = "#/buy-cert";
      }
    } catch {}
  }, [authLoading, sbUser]);

  // Film Studio first-look modal trigger. Fires once per coach the
  // first time they reach the dashboard after Film Studio launched.
  // Gated by:
  //   - auth ready + onboarded (same gate as the onboarding tour)
  //   - flag not yet set in user_data (loadUserData with fallback)
  //   - the onboarding tour isn't also queued (avoid stacking modals)
  // Delayed 1200ms so the dashboard has painted first; a modal that
  // pops before the user sees what's behind it reads as a mistake.
  useEffect(() => {
    if (authLoading || !sbUser || !onboarded) return;
    // Skip if the new-user onboarding tour is about to run — that's
    // a much bigger UX moment and shouldn't compete.
    try {
      if (localStorage.getItem("bam_should_start_tour") === "1") return;
    } catch {}
    let cancelled = false;
    (async () => {
      // localStorage mirror first (cheap, instant). If we already
      // marked it locally we trust it and skip the network call.
      let seen = false;
      try { seen = localStorage.getItem("bam_film_studio_intro_seen") === "1"; } catch {}
      if (seen) return;
      try {
        const v = await loadUserData("film_studio_intro_seen", false);
        if (v === true) {
          try { localStorage.setItem("bam_film_studio_intro_seen", "1"); } catch {}
          return;
        }
      } catch {}
      if (cancelled) return;
      const t = setTimeout(() => { if (!cancelled) setFilmIntroOpen(true); }, 1200);
      return () => clearTimeout(t);
    })();
    return () => { cancelled = true; };
  }, [authLoading, sbUser, onboarded]);
  // Command palette (⌘K / Ctrl+K) + global keyboard enhancements
  const [cmdPaletteOpen, setCmdPaletteOpen] = useState(false);
  useEffect(()=>{
    const onKey = (e)=>{
      if((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")){
        e.preventDefault();
        setCmdPaletteOpen(o => !o);
      }
      // Enter/Space on focused .btn divs (keyboard accessibility)
      if((e.key === "Enter" || e.key === " ") && document.activeElement?.classList?.contains("btn") && document.activeElement.tagName !== "BUTTON" && document.activeElement.tagName !== "A"){
        e.preventDefault();
        document.activeElement.click();
      }
    };
    window.addEventListener("keydown", onKey);
    // Make all .btn divs keyboard-reachable with role + tabindex (accessibility)
    const enhanceButtons = () => {
      document.querySelectorAll(".btn:not([tabindex])").forEach(el => {
        if (el.tagName === "BUTTON" || el.tagName === "A") return;
        el.setAttribute("tabindex", "0");
        if (!el.getAttribute("role")) el.setAttribute("role", "button");
      });
    };
    enhanceButtons();
    const observer = new MutationObserver(enhanceButtons);
    observer.observe(document.body, { childList: true, subtree: true });
    return ()=>{ window.removeEventListener("keydown", onKey); observer.disconnect(); };
  },[]);

  // dashIntro = true only on very first visit this session
  const [dashIntro,setDashIntro]=useState(()=>{
    try{ return !sessionStorage.getItem("bam_dash_seen"); }catch(e){ return false; }
  });
  useEffect(()=>{
    if(dashIntro){
      try{ sessionStorage.setItem("bam_dash_seen","1"); }catch(e){}
      const t=setTimeout(()=>setDashIntro(false),2800);
      return ()=>clearTimeout(t);
    }
  },[dashIntro]);
  // Map admin sub-route slugs to human labels for the browser tab
  // title. Falls back to a title-cased version of the slug when not
  // listed (so a new tab still gets *some* label).
  const ADMIN_SUB_LABELS = {
    plan: "Content Plan",
    uploads: "Pending Uploads",
    "drill-review": "Drill Review",
    resources: "Resources",
    workout: "Workout of the Week",
    announcements: "Announcements",
    "group-calls": "Group Calls",
    leads: "Masterclass Leads",
    orgs: "Organizations",
    cohort: "Cohort Sender",
    audit: "Audit Log",
    doctrine: "Doctrine Studio",
    ai: "AI Training",
    instagram: "Instagram",
  };
  function prettySubTab(sub) {
    if (ADMIN_SUB_LABELS[sub]) return ADMIN_SUB_LABELS[sub];
    // Fallback: replace dashes with spaces, title-case each word
    return sub.split("-").map(w => w[0].toUpperCase() + w.slice(1)).join(" ");
  }
  // Prompt 1: hash-based routing
  const VALID_ROUTES=new Set([
    "dashboard","community","calls","chats","referrals","resources","playbook","settings",
    "pd","team","insights","ti","xo","vocab","foundations","learn","plans","workouts","master","movement",
    // Learn's own page (LearnHome), opened by the Learn heading.
    "learn-home",
    // Pro Development — a curated collection, reached from the chip on
    // Drills & Games. Not in NAV (no sidebar row) but a real route,
    // so the link is shareable.
    "pro",
    // Research collections index. `research/<slug>` and
    // `research/<slug>/<n>` carry a slug and an index, so they cannot be
    // enumerated here and pass through parseHash's prefix branch instead.
    "research",
    // The Wednesday Drop: this week's (#/drop) and the archive (#/drops).
    // One drop is `drops/<id>`, through parseHash's prefix branch.
    "drop","drops",
    "dev-studio","toolbox","film","portal","consulting","org","interns","filmdesk","tour",
    // BAM IQ's page; resolveNav and the page itself show it only where bamiqAvailable() allows.
    "iq",
    // The AFS pilot's dashboard; the page asks the server whether this account is in the pilot.
    "afs",
    // Parked — full-video Auto-AFS pipeline routes disabled so neither
    // coaches nor admin land on the model-picker surface. The manual
    // Co-pilot flow lives in AFS Mode. Re-enable with the imports + render
    // block (search "Parked — full-video Auto-AFS").
    // "lab/auto-afs",
    // "lab/afs-compare",
    "lab/shotflow",
    // Private beta — AFS Co-pilot accuracy readout (scores the feedback
    // corpus). Allowlist-gated page; admin-gated RPC underneath.
    "lab/afs-copilot",
    // Constraint Library — coach-facing browse/capture + admin review queue.
    // Self-contained ConstraintRouter mounted from BAMFull's render fn.
    "library/constraints","library/constraints/add",
    "admin/constraints","admin/constraints/review",
    // Admin (post-restructure) — 7 section roots; sub-tabs handled
    // via `startsWith("admin/<section>/")` in parseHash below.
    "admin/home","admin/workspace","admin/content","admin/people","admin/money","admin/cert-ai","admin/intelligence","admin/afs","admin/outreach",
    // Legacy admin routes — still accepted so old bookmarks/email links
    // resolve, but parseHash() rewrites them to the new section/tab on
    // the way out.
    "admin/dashboard","admin/content-plan",
    "admin/content-manager","admin/uploads","admin/drill-review","admin/resources","admin/announcements","admin/workout-of-week","admin/group-calls",
    "admin/members","admin/orgs","admin/subscriptions","admin/audit",
    "admin/analytics","admin/ai-training","admin/doctrine-studio","admin/cert-content","admin/cohort-send","admin/leads",
  ]);

  // Legacy → new mapping. Anything in this map gets rewritten and the
  // URL hash is updated in-place so the address bar reflects the new
  // route (and back/forward stays consistent).
  const ADMIN_LEGACY_REDIRECTS = {
    "admin/dashboard":       "admin/home",
    "admin/content-plan":    "admin/home/plan",
    "admin/content-manager": "admin/content",
    "admin/uploads":         "admin/content/uploads",
    "admin/drill-review":    "admin/content/drill-review",
    "admin/resources":       "admin/content/resources",
    "admin/workout-of-week": "admin/content/workout",
    "admin/announcements":   "admin/content/announcements",
    "admin/group-calls":     "admin/content/group-calls",
    "admin/leads":           "admin/content/leads",
    "admin/members":         "admin/people",
    "admin/orgs":            "admin/people/orgs",
    "admin/cohort-send":     "admin/people/cohort",
    "admin/subscriptions":   "admin/money/subscriptions",
    "admin/audit":           "admin/money/audit",
    "admin/cert-content":    "admin/cert-ai",
    "admin/doctrine-studio": "admin/cert-ai/doctrine",
    "admin/ai-training":     "admin/cert-ai/ai",
    "admin/analytics":       "admin/intelligence",
  };

  // Routes outside VALID_ROUTES that still pass through (instead of
  // falling back to "dashboard"): the `certified/*` cert sub-routes
  // (handled by CertificationPage's internal router), `film/*`, and
  // any sub-route under one of our 6 admin section roots.
  const ADMIN_SECTION_ROOTS = ["admin/home","admin/workspace","admin/content","admin/people","admin/money","admin/cert-ai","admin/intelligence","admin/afs","admin/outreach"];
  const parseHash=()=>{
    // Internal nav uses `#/<route>` (e.g. `#/community`). External links
    // people compose by hand sometimes include the `platform/` prefix
    // (`#/platform/community`) — accept both shapes so shared URLs work.
    let h = window.location.hash.replace(/^#\/?/,"");
    if (h.startsWith("platform/")) h = h.slice("platform/".length);
    if (!h || h === "platform") return "dashboard";
    // #/afs?guide=1 (a link with options but no tab) is Advantage Flow's overview, options kept (2026-10-29: it landed
    // on Home, since "afs?guide=1" isn't a route).
    if (h.startsWith("afs?")) h = "afs/overview" + h.slice(3);
    // Strip a trailing slash so admin/content/ and admin/content
    // resolve to the same thing.
    if (h.length > 1 && h.endsWith("/")) h = h.slice(0, -1);
    // The Film Desk's tabs (#/filmdesk/library, #/filmdesk/review) are one page: FilmDesk
    // reads its tab from the hash and keeps it there with replaceState, as Advantage Flow does.
    if (h.startsWith("filmdesk/") || h.startsWith("filmdesk?")) return "filmdesk";
    // Legacy admin → new admin redirect (in-place rewrite).
    if (ADMIN_LEGACY_REDIRECTS[h]) {
      const next = ADMIN_LEGACY_REDIRECTS[h];
      if (typeof window !== "undefined") {
        try { window.history.replaceState(null, "", "#/" + next); } catch {}
      }
      h = next;
    }
    const inAdminSection = ADMIN_SECTION_ROOTS.some(root => h === root || h.startsWith(root + "/"));
    if (VALID_ROUTES.has(h) || inAdminSection || h === "certified" || h.startsWith("certified/") || h.startsWith("film/") || h.startsWith("afs/") || h.startsWith("research/") || h.startsWith("drops/")) return h;
    return "dashboard";
  };
  const adminHomeSyncedRef = useRef(false);
  const [nav,setNav]=useState(parseHash);
  const [pageKey,setPageKey]=useState(0);
  const isCertRoute = (r) => r==="certified" || r.startsWith("certified/");
  // The URL is the single source of truth for which mode we're in.
  //
  // This used to seed from a `bam_mode` localStorage key, which could
  // contradict the route: land on #/dashboard with bam_mode="certified" and
  // you got the Certification sidebar — a unit list with no Dashboard link —
  // rendered over dashboard content. Nothing reconciled it, because
  // `hashchange` doesn't fire on page load. That's a coach with no visible
  // way back to the platform.
  //
  // Deriving it means the two can't disagree, and it makes cert URLs
  // shareable: open one and you're in cert mode, whatever you were in last.
  const [appMode, setAppMode] = useState(()=>isCertRoute(parseHash())?"certified":"platform");
  const [modeTransition, setModeTransition] = useState(false);

  // Whether this user belongs to ≥1 chat (drives the Chats nav entry's
  // visibility). Cert members + anyone invited to an event/group chat.
  const [hasChats, setHasChats] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  // Where the coach is, readable from async callbacks without re-running them.
  const chatNavRef = useRef(nav);
  chatNavRef.current = nav;
  const [chatToast, setChatToast] = useState(null); // { name, body }
  useEffect(() => {
    let cancelled = false;
    if (!sbUser?.id) { setHasChats(false); setChatUnread(0); return; }
    hasAnyChat(sbUser.id).then(v => { if (!cancelled) setHasChats(!!v); }).catch(() => {});
    // Seed the unread badge from the user's chats. Not when they landed ON
    // Chats: the page reports its own fresher count, and this slower login
    // fetch used to resolve after it and write a stale number back.
    listMyChats(sbUser.id).then(list => {
      if (!cancelled && chatNavRef.current !== "chats") setChatUnread(list.reduce((a, c) => a + (c.unread || 0), 0));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [sbUser?.id]);
  // While on Chats the badge is whatever the page last loaded (see
  // onUnreadTotal below), so it drops the moment a chat is read rather than
  // being blanked on arrival and wrong again on reload.
  // Opening Chats (e.g. from a profile's Message button, which may have just
  // started this coach's first chat) means they have one: show the tile.
  useEffect(() => { if (nav === "chats") setHasChats(true); }, [nav]);
  // And re-read the real count on the way OUT of Chats. The badge used to be
  // seeded once at login and only ever bumped by realtime after that, so it
  // could disagree with the server for the rest of the session.
  const prevNavRef = useRef(nav);
  useEffect(() => {
    const left = prevNavRef.current === "chats" && nav !== "chats";
    prevNavRef.current = nav;
    if (!left || !sbUser?.id) return;
    let cancelled = false;
    listMyChats(sbUser.id).then(list => {
      if (!cancelled) setChatUnread(list.reduce((a, c) => a + (c.unread || 0), 0));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [nav, sbUser?.id]);

  // Whether this coach has an affiliate code (drives the Referrals nav entry).
  const [hasReferralCode, setHasReferralCode] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!sbUser?.id) { setHasReferralCode(false); return; }
    getMyReferralStats().then(s => { if (!cancelled) setHasReferralCode(!!s?.code); }).catch(() => {});
    return () => { cancelled = true; };
  }, [sbUser?.id]);
  // Global new-message signal: bump badge + toast when not already in Chats.
  useChatsListRealtime(async (msg) => {
    if (!msg || !sbUser?.id || msg.sender_id === sbUser.id) return;
    if (window.location.hash === "#/chats") return;
    setChatUnread(n => n + 1);
    try {
      const profs = await chatGetProfiles([msg.sender_id]);
      const name = chatDisplayName(profs[msg.sender_id]);
      const body = msg.body || (msg.media_type === "image" ? "📷 Photo" : "New message");
      setChatToast({ name, body, chatId: msg.chat_id || null });
      setTimeout(() => setChatToast(t => (t && t.name === name && t.body === body) ? null : t), 5000);
    } catch {}
  });

  // Cert state (lifted up so sidebar can access progress)
  const [certPurchased, setCertPurchased] = useState(()=>{try{return localStorage.getItem("bam_cert_purchased")==="true";}catch{return false;}});
  // Entitlement follows the server: if the DB says this coach owns the cert
  // (cert_purchased=true — bought here, granted by an admin, or signing in on a
  // new device), unlock it locally even when localStorage was never set. Only
  // ever upgrades, so an optimistic local purchase is never re-locked.
  useEffect(()=>{
    if(hasCert){ setCertPurchased(true); try{localStorage.setItem("bam_cert_purchased","true");}catch{} }
  },[hasCert]);
  const [certProgress, setCertProgress] = useState(()=>{try{return JSON.parse(localStorage.getItem("bam_cert_progress")||"{}");} catch{return {};}});
  const saveCertProgress = (update) => {
    setCertProgress(prev=>{const n={...prev,...update,__last_at:new Date().toISOString()};try{localStorage.setItem("bam_cert_progress",JSON.stringify(n));}catch{}saveUserData("cert_progress",n).catch(e=>console.error("cert_progress write failed:",e));return n;});
  };
  // Batch-prefetch every user_data key the dashboard reads on mount.
  // The individual loadUserData calls below (cert_progress, video_notes,
  // watched_ids, fav_drills, fav_lists, fav_list_membership) used to be
  // 6 separate round-trips on every dashboard load — fine at 1 user,
  // a connection-pool fire at 100 concurrent. With the prefetch they
  // collapse into a single .in() query; the per-key calls become cache
  // hits. Sentry was emitting ~3-4 user_data events per dashboard load
  // (27 events / 8 users) before this.
  useEffect(()=>{
    if(!sbUser) return;
    prefetchUserData([
      "cert_progress", "video_notes", "watched_ids",
      "fav_drills", "fav_lists", "fav_list_membership",
    ]).catch(()=>{});
  },[sbUser]);
  // Hydrate cert progress from Supabase on mount
  useEffect(()=>{
    if(!sbUser) return;
    loadUserData("cert_progress",{}).then(d=>{if(d&&Object.keys(d).length>0){setCertProgress(prev=>{const merged={...prev,...d};try{localStorage.setItem("bam_cert_progress",JSON.stringify(merged));}catch{}return merged;});}});
  },[sbUser]);
  const certIsLessonComplete = (id) => !!certProgress[id];
  // Same rule as CertificationPage: a lesson added later (`added`) never
  // re-locks a unit whose quiz was already passed.
  const certIsUnitComplete = (unit) => unitLessonsDone(unit, certProgress);
  const certIsQuizPassed = (unitId) => !!certProgress[`quiz-${unitId}`];
  // Mirrors the unlock logic in CertificationPage.isUnitUnlocked. The
  // __unlock_all flag is set on EUFP signups (and Coleman manually) so
  // sidebar units don't all show as LOCKED for coaches who were partway
  // through the cert on CoachIQ. See CertificationPage for full notes.
  const certIsUnitUnlocked = (idx) => {
    if (certProgress.__unlock_all === true) return true;
    if(idx===0) return true;
    const prev=CERT_UNITS[idx-1];
    return certIsUnitComplete(prev)&&certIsQuizPassed(prev.id);
  };
  const certCompletedUnits = CERT_UNITS.filter(u=>certIsUnitComplete(u)&&certIsQuizPassed(u.id)).length;
  const certIsCertified = !!certProgress["final-exam-passed"];
  const certCurrentUnit = CERT_UNITS.findIndex((u,i)=>certIsUnitUnlocked(i)&&!certIsQuizPassed(u.id));

  useEffect(()=>{
    const onHash=()=>{setNav(parseHash());setPageKey(k=>k+1);};
    window.addEventListener("hashchange",onHash);
    return ()=>window.removeEventListener("hashchange",onHash);
  },[]);
  // Mode follows the route wherever the route came from — a hash change, a
  // link, the back button, or a fresh load. Doing it here rather than inside
  // the hashchange handler is what fixes the on-load case, since that handler
  // never runs for the first render.
  useEffect(()=>{
    setAppMode(isCertRoute(nav)?"certified":"platform");
  },[nav]);
  // Dynamic page titles
  useEffect(()=>{
    const titles={
      dashboard:"Home","learn-home":"Learn",community:"Community",calls:"Calls",pd:"Drills & Games",insights:"Insights",
      team:"Team Drills",ti:"Team Insights",xo:"X & O Breakdowns",plans:"Practice Plans",
      master:"Masterclasses",workouts:"Full Workouts",playbook:"Playbook Builder",
      resources:"Resources",settings:"Settings",drop:"This week's drop",drops:"Past drops",consulting:"BAM Consulting",org:"My Organization",interns:"Intern Hub",
      "admin/home":"Admin · Home",
      "admin/content":"Admin · Content",
      "admin/people":"Admin · People",
      "admin/money":"Admin · Money",
      "admin/cert-ai":"Admin · Cert & AI",
      "admin/intelligence":"Admin · Intelligence",
      "admin/afs":"Admin · Advantage Flow",
      "admin/outreach":"Admin · Outreach",
      certified:"BAM Certified",
      "certified/lesson":"BAM Certified | Lesson",
      "certified/quiz":"BAM Certified | Quiz",
      "certified/exam":"BAM Certified | Final Exam",
      "certified/certificate":"BAM Certified | Certificate",
    };
    // Sub-routes (admin/content/uploads) fall back to the section root
    // title — but we also append a human label for the sub-tab so the
    // browser tab actually tells you which tab you're on. Examples:
    //   admin/content          → "Admin · Content | BAM"
    //   admin/content/uploads  → "Admin · Content · Pending Uploads | BAM"
    let resolved = titles[nav];
    if (!resolved && nav.startsWith("admin/")) {
      const parts = nav.split("/");
      const root = parts.slice(0, 2).join("/");      // admin/content
      const sub  = parts.slice(2).join("/");          // uploads
      const rootTitle = titles[root];
      if (rootTitle) {
        const subLabel = sub ? prettySubTab(sub) : "";
        resolved = subLabel ? `${rootTitle} · ${subLabel}` : rootTitle;
      }
    }
    document.title=(resolved||"Home")+" | BAM";
  },[nav]);
  // Prompt 2: mobile sidebar
  const [mobileMenuOpen,setMobileMenuOpen]=useState(false);
  // Keep the floating admin shield out of the nav drawer's way (it was
  // overlapping drawer content). Same pattern as chat threads/sheets.
  useEffect(()=>{
    document.body.classList.toggle("bam-drawer-open", mobileMenuOpen);
    return ()=>document.body.classList.remove("bam-drawer-open");
  },[mobileMenuOpen]);
  // Swipe-to-close gesture for the mobile sidebar drawer. drawerOffset
  // tracks the in-progress drag (negative = leftward). drawerSwipeRef
  // holds gesture metadata that doesn't need to trigger re-renders.
  // Threshold: 60px of leftward drag commits to a close; anything less
  // snaps back. iOS users expect this gesture on any slide-in panel.
  const [drawerOffset,setDrawerOffset]=useState(0);
  const drawerSwipeRef=useRef({startX:0,dx:0,active:false});
  // Safety reset: whenever the drawer opens or closes, snap the offset
  // back to 0. Without this, an interrupted touch sequence (touchcancel
  // not firing, system gesture taking over) could leave the drawer
  // visibly translated leftward — see the "BY ANY MEANS → ANY MEANS"
  // clipping bug from the May 21 screenshot.
  useEffect(() => {
    if (drawerOffset !== 0) setDrawerOffset(0);
    drawerSwipeRef.current = { startX: 0, dx: 0, active: false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileMenuOpen]);
  const onDrawerTouchStart=(e)=>{ const t=e.touches?.[0]; if(!t) return; const now=Date.now(); drawerSwipeRef.current={startX:t.clientX,startY:t.clientY,dx:0,lastX:t.clientX,lastT:now,vx:0,active:true,decided:false,locked:false}; };
  const onDrawerTouchMove=(e)=>{ const s=drawerSwipeRef.current; if(!s.active||s.locked) return; const t=e.touches?.[0]; if(!t) return; const dx=t.clientX-s.startX; const dy=t.clientY-s.startY; if(!s.decided){ if(Math.abs(dx)<6&&Math.abs(dy)<6) return; s.decided=true; if(Math.abs(dy)>Math.abs(dx)){ s.locked=true; return; } } const now=Date.now(); const dt=Math.max(now-s.lastT,1); s.vx=(t.clientX-s.lastX)/dt; s.lastX=t.clientX; s.lastT=now; if(dx>0){ s.dx=0; setDrawerOffset(0); return; } s.dx=dx; setDrawerOffset(Math.max(dx,-300)); };
  // App-like, decisive snap: commit to CLOSE on a quick left flick OR once
  // dragged ~a third of the 260px drawer. Direction is decided in the first
  // few px — a vertical scroll of the nav list locks OUT the horizontal drag
  // so the drawer doesn't jitter/bounce in-and-out while you scroll it.
  const onDrawerTouchEnd=()=>{ const s=drawerSwipeRef.current; if(!s.active) return; s.active=false; if(s.locked){ setDrawerOffset(0); return; } const flick=s.vx<-0.4; const past=s.dx<-85; if(flick||past){ setMobileMenuOpen(false); haptic("light"); setTimeout(()=>setDrawerOffset(0),220); } else { setDrawerOffset(0); } };

  // Swipe-from-left-edge to OPEN drawer. iOS standard gesture (matches
  // Mail, Messages, etc.). Listens on the document at the capture
  // phase so it fires before per-page touch handlers steal the event.
  //
  // Conditions for activation:
  //   - touch starts within 30px of the left edge (wider than the
  //     20px Apple uses because BAM isn't deep-nested and we'd rather
  //     err toward "discovers the gesture")
  //   - drawer is currently closed
  //   - viewport is mobile-sized (<700px — desktop uses the sidebar)
  //
  // Opens on >45px rightward drag (smaller than the typical 60px so a
  // slow short swipe still commits). Fires a light haptic on commit.
  // Also ignores swipes that are mostly vertical — coaches scrolling
  // a long list shouldn't accidentally open the drawer.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let state = { active: false, startX: 0, startY: 0, dx: 0, dy: 0 };
    const onStart = (e) => {
      if (mobileMenuOpen) return;
      if (window.innerWidth >= 700) return;
      const t = e.touches?.[0]; if (!t) return;
      if (t.clientX > 30) return;
      state = { active: true, startX: t.clientX, startY: t.clientY, dx: 0, dy: 0 };
    };
    const onMove = (e) => {
      if (!state.active) return;
      const t = e.touches?.[0]; if (!t) return;
      state.dx = t.clientX - state.startX;
      state.dy = t.clientY - state.startY;
      // If the user is clearly scrolling vertically, abort the gesture.
      if (Math.abs(state.dy) > 30 && Math.abs(state.dy) > Math.abs(state.dx) * 1.3) {
        state.active = false;
      }
    };
    const onEnd = () => {
      if (!state.active) return;
      const dx = state.dx;
      state.active = false;
      if (dx > 45) { haptic("light"); setMobileMenuOpen(true); }
    };
    document.addEventListener("touchstart", onStart, { passive: true, capture: true });
    document.addEventListener("touchmove",  onMove,  { passive: true, capture: true });
    document.addEventListener("touchend",   onEnd,   { passive: true, capture: true });
    document.addEventListener("touchcancel",onEnd,   { passive: true, capture: true });
    return () => {
      document.removeEventListener("touchstart", onStart, { capture: true });
      document.removeEventListener("touchmove",  onMove,  { capture: true });
      document.removeEventListener("touchend",   onEnd,   { capture: true });
      document.removeEventListener("touchcancel",onEnd,   { capture: true });
    };
  }, [mobileMenuOpen]);

  // Keyboard-up class on <body> — used by CSS to hide the bottom tab
  // nav and the AskBAM floater while the soft keyboard is showing. The
  // Capacitor Keyboard plugin emits these events on iOS; on web the
  // Native deep-link handler — routes bamcoaches:// links opened from outside
  // the app (password-reset / magic-link emails) into the right screen. Without
  // this, tapping the reset link in the email did nothing in the native app.
  useEffect(() => {
    if (!isNative()) return;
    let listener;
    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        listener = await App.addListener("appUrlOpen", ({ url }) => {
          try {
            if (!url) return;
            // Supabase recovery/magic links carry the tokens in the URL hash.
            // Route to the reset page + preserve the token fragment so
            // ResetPasswordPage can parse it.
            if (url.includes("access_token=") || url.includes("type=recovery") || url.includes("recovery=1")) {
              const hi = url.indexOf("#");
              const frag = hi >= 0 ? url.slice(hi + 1) : "";
              window.location.hash = "#/reset" + (frag ? "#" + frag : "");
              return;
            }
            // Generic in-app deep link: bamcoaches://host#/route
            const hi = url.indexOf("#/");
            if (hi >= 0) window.location.hash = url.slice(hi);
          } catch {}
        });
      } catch { /* @capacitor/app unavailable */ }
    })();
    return () => { try { listener?.remove?.(); } catch {} };
  }, []);

  // dynamic import resolves to a no-op so listeners simply never fire.
  useEffect(() => {
    if (!isNative()) return;
    let detach = () => {};
    (async () => {
      try {
        const { Keyboard } = await import("@capacitor/keyboard");
        // Stop iOS from scroll-assisting the WHOLE webview up to reveal a
        // focused input — that's what shoved the fixed header/strip off-screen
        // and left a black bar. With native resize the webview already shrinks
        // to the keyboard, and inputs that need it call scrollIntoView on focus.
        try { await Keyboard.setScroll({ isDisabled: true }); } catch {}
        const onShow = Keyboard.addListener("keyboardWillShow", () => {
          document.body.classList.add("bam-keyboard-up");
        });
        const onHide = Keyboard.addListener("keyboardWillHide", () => {
          document.body.classList.remove("bam-keyboard-up");
        });
        detach = async () => {
          try { (await onShow).remove(); } catch {}
          try { (await onHide).remove(); } catch {}
        };
      } catch { /* plugin not available */ }
    })();
    return () => { detach(); };
  }, []);
  // Prompt 3: page transition
  const [pageFade,setPageFade]=useState(true);
  useEffect(()=>{setPageFade(false);const t=requestAnimationFrame(()=>setPageFade(true));return ()=>cancelAnimationFrame(t);},[pageKey]);
  // Prompt 8: offline detection
  const [isOffline,setIsOffline]=useState(!navigator.onLine);
  const [backOnline,setBackOnline]=useState(false);
  useEffect(()=>{
    const goOff=()=>setIsOffline(true);
    const goOn=()=>{setIsOffline(false);setBackOnline(true);setTimeout(()=>setBackOnline(false),3000);};
    window.addEventListener("offline",goOff);window.addEventListener("online",goOn);
    return ()=>{window.removeEventListener("offline",goOff);window.removeEventListener("online",goOn);};
  },[]);
  // Prompt 5: multi-tab logout sync (Supabase handles cross-tab via onAuthStateChange, keep storage fallback)
  useEffect(()=>{
    const onStorage=(e)=>{if(e.key==="bam_auth_token"&&!e.newValue){handleSignOut();}};
    window.addEventListener("storage",onStorage);
    return ()=>window.removeEventListener("storage",onStorage);
  },[handleSignOut]);
  // Prompt 7: API error toast
  const [apiError,setApiError]=useState(null);
  const apiErrorTimer=useRef(null);
  const showApiError=useCallback((msg)=>{
    console.error("[BAM API Error]",msg);
    setApiError(msg);
    if(apiErrorTimer.current)clearTimeout(apiErrorTimer.current);
    apiErrorTimer.current=setTimeout(()=>setApiError(null),5000);
  },[]);
  const [notifOn,setNotifOn]=useState(true);
  const [notif,setNotif]=useState(null);
  const [pIdx,setPIdx]=useState(0);
  const [pVis,setPVis]=useState(true);
  const [compose,setCompose]=useState("");
  // Composer post-type chip (Drill Drop / Win / Question / Insight). Lives
  // separately from `compose` so picking a type doesn't overwrite text the
  // coach already typed and doesn't insert visible "[Question]" prefixes.
  // The post handler reads this directly instead of regex-parsing the body.
  const [composeTag,setComposeTag]=useState(null);
  const [focused,setFocused]=useState(false);
  const [attachments,setAttachments]=useState([]);
  const [posting,setPosting]=useState(false);
  const lastPostRef=useRef({text:"",time:0});
  const attachRef=useRef(null);
  // @-mention / content embed system
  const [mentionOpen,setMentionOpen]=useState(false);
  const [mentionQuery,setMentionQuery]=useState("");
  /* @ tags coaches as well as content. "@" was only ever an embed picker,
     so there was no way to point a post at somebody. Typing @ and a letter
     now lists coaches first (the content results stay below it); "/" is
     content only. Picking one writes their plain name, no @. Picked
     coaches are kept here and, on posting, any whose name is still in the
     text are saved on the post as mention embeds and notified. */
  const [mentionTrigger,setMentionTrigger]=useState("@");
  const [coachMatches,setCoachMatches]=useState([]);
  const [taggedCoaches,setTaggedCoaches]=useState([]);
  const [embeddedContent,setEmbeddedContent]=useState([]);
  const [showDrillPicker,setShowDrillPicker]=useState(false);
  const [showFeaturePicker,setShowFeaturePicker]=useState(false);
  const composeRef=useRef(null);
  const composeMirrorRef=useRef(null);
  // The coaches come from the member directory (fetch_directory_members),
  // loaded once the first time @ is typed: one database call, the same list
  // the Member Map shows. Profiles are readable only by their owner, so a
  // direct profile search would come back empty for a coach. The directory
  // used to list Stripe subscribers only, which left Jude Shepherd-Hills
  // (a founding cert member) out; Coleman ran the SQL on 2026-10-07 that
  // widens it to every kind of member.
  const directoryRef=useRef(null);
  useEffect(()=>{
    if(!mentionOpen||mentionTrigger!=="@"||mentionQuery.length<1){setCoachMatches([]);return;}
    let alive=true;
    const q=mentionQuery.toLowerCase();
    const filter=(list)=>list.filter(m=>m.id!==sbUser?.id&&(
      (m.name||"").toLowerCase().split(/\s+/).some(w=>w.startsWith(q)) ||
      (m.name||"").toLowerCase().replace(/\s+/g,"").startsWith(q) ||
      (m.handle||"").toLowerCase().startsWith(q))).slice(0,5);
    if(directoryRef.current){ setCoachMatches(filter(directoryRef.current)); return; }
    import("./utils/communityApi").then(api=>api.fetchMembers({limit:500})).then(list=>{
      directoryRef.current=Array.isArray(list)?list:[];
      if(alive) setCoachMatches(filter(directoryRef.current));
    }).catch(()=>{ if(alive) setCoachMatches([]); });
    return ()=>{alive=false;};
  },[mentionOpen,mentionTrigger,mentionQuery,sbUser?.id]);
  const [composePlaceholder, setComposePlaceholder] = useState("Share a drill, win, question, or insight...");
  const [showFilmPicker, setShowFilmPicker] = useState(false);
  // Auto-detect YouTube URLs (with optional &t= timestamp) typed/pasted
  // into the composer body and add them as film_clip embeds. Existing
  // embedded videos are skipped via youtube_id dedupe so editing the
  // body doesn't keep stacking duplicates.
  useEffect(() => {
    if (!compose) return;
    const re = /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})(?:[?&][^\s]*)?/g;
    const urls = [];
    let m;
    while ((m = re.exec(compose)) !== null) {
      const tMatch = m[0].match(/[?&](?:t|start)=(\d+)/);
      urls.push({ url: m[0], id: m[1], timestamp: tMatch ? parseInt(tMatch[1], 10) : 0 });
    }
    if (urls.length === 0) return;
    const existing = new Set((embeddedContent || []).map(e => e.youtube_id).filter(Boolean));
    const toAdd = urls.filter(u => !existing.has(u.id));
    if (toAdd.length === 0) return;
    const t = setTimeout(async () => {
      const api = await import("./utils/communityApi");
      const newEmbeds = [];
      for (const u of toAdd) {
        const meta = await api.fetchYouTubeOEmbed(u.url);
        newEmbeds.push({
          type: "film_clip",
          youtube_url: u.url,
          youtube_id: u.id,
          thumbnail_url: meta?.thumbnail_url || `https://i.ytimg.com/vi/${u.id}/hqdefault.jpg`,
          timestamp: u.timestamp,
          title: meta?.title || "YouTube clip",
          body: meta?.author_name ? `via ${meta.author_name}` : "",
          snapshot_url: null,
          audio_url: null,
          source_film_kind: "general",
          source_note_id: null,
        });
      }
      setEmbeddedContent(prev => [...(prev || []), ...newEmbeds]);
    }, 600);
    return () => clearTimeout(t);
  }, [compose, embeddedContent]);
  // Video notes system
  const [videoNotes,setVideoNotes]=useState(()=>{try{return JSON.parse(localStorage.getItem("bam_video_notes")||"{}");} catch{return {};}});
  const saveVideoNotes=(key,notes)=>{setVideoNotes(prev=>{const n={...prev,[key]:notes};try{localStorage.setItem("bam_video_notes",JSON.stringify(n));}catch{}saveUserData("video_notes",n).catch(e=>console.error("video_notes write failed:",e));return n;});};
  useEffect(()=>{if(!sbUser)return;loadUserData("video_notes",{}).then(d=>{if(d&&Object.keys(d).length>0)setVideoNotes(prev=>({...prev,...d}));});},[sbUser]);

  // Admin landing preference, synced down from the account.
  //
  // The boot redirect (App.js) has to decide before anything renders, so
  // it reads localStorage synchronously — which means a device that has
  // never seen the preference can't honour it on the very first launch.
  // Mirroring it here fixes every launch after that, and the redirect
  // below covers the first one.
  useEffect(() => {
    if (!sbUser) return;
    let cancelled = false;
    loadUserData("admin_home", null).then((v) => {
      if (cancelled || v === null || v === undefined) return;
      const want = v === true || v === "1";
      let had = false;
      try {
        had = localStorage.getItem("bam_admin_home") === "1";
        localStorage.setItem("bam_admin_home", want ? "1" : "0");
      } catch { return; }
      // First launch on this device: localStorage said no, the account
      // says yes. Honour it now rather than making them cold-start twice.
      // Only from the landing route, and only once — this must never
      // yank someone out of a screen they navigated to themselves.
      if (want && !had && !adminHomeSyncedRef.current) {
        adminHomeSyncedRef.current = true;
        const h = window.location.hash;
        if (!h || h === "#/" || h === "#/platform") {
          try { window.location.hash = "#/admin/home"; } catch {}
        }
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [sbUser]);
  // Notification panel
  const [notifPanelOpen,setNotifPanelOpen]=useState(false);
  // Mobile-only search overlay. Topbar search input is hidden below 700px
  // so we expose a search-icon button next to notifications that opens
  // the same SearchBar component inside a fullscreen overlay. Lets phone
  // users actually find drills via search instead of having to scroll
  // the full library.
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  // Notifications — empty until real notification system is built.
  // Will populate from Supabase `notifications` table in the future.
  const NOTIF_SEED = [];
  const [notifications,setNotifications]=useState(()=>{
    try{
      const readIds=new Set(JSON.parse(localStorage.getItem("bam_notifications_read")||"[]"));
      return NOTIF_SEED.map(n=>({...n, read: n.read || readIds.has(n.id)}));
    }catch{ return NOTIF_SEED; }
  });
  const persistReadIds = (next) => {
    try{ localStorage.setItem("bam_notifications_read", JSON.stringify(next.filter(n=>n.read).map(n=>n.id))); }catch{}
  };
  const unreadCount=notifications.filter(n=>!n.read).length;
  const markRead=(id)=>setNotifications(prev=>{ const next=prev.map(n=>n.id===id?{...n,read:true}:n); persistReadIds(next); return next; });
  const markAllRead=()=>setNotifications(prev=>{ const next=prev.map(n=>({...n,read:true})); persistReadIds(next); return next; });

  // Poll for real notifications (announcements + replies to my posts)
  useEffect(() => {
    if (!sbUser?.id) return;
    let cancelled = false;
    const relTime = (iso) => {
      const diff = Date.now() - new Date(iso).getTime();
      const m = Math.floor(diff / 60000);
      if (m < 1) return "just now";
      if (m < 60) return `${m}m ago`;
      const h = Math.floor(m / 60);
      if (h < 24) return `${h}h ago`;
      const d = Math.floor(h / 24);
      if (d < 7) return `${d}d ago`;
      return new Date(iso).toLocaleDateString();
    };
    const pull = async () => {
      try {
        const readIds = new Set(JSON.parse(localStorage.getItem("bam_notifications_read") || "[]"));
        // Announcements. Production schema uses `body` not `content`.
        // Title might not exist — select defensively.
        const { data: anns } = await supabase.from("announcements")
          .select("id,body,created_at,sent")
          .eq("sent", true).order("created_at", { ascending: false }).limit(20);
        // My post IDs
        const { data: myPosts } = await supabase.from("community_posts")
          .select("id,content").eq("author_id", sbUser.id);
        const postIds = (myPosts || []).map(p => p.id);
        let replies = [];
        if (postIds.length > 0) {
          const { data } = await supabase.from("community_replies")
            .select("id,post_id,content,created_at,user_id")
            .in("post_id", postIds)
            .neq("user_id", sbUser.id)
            .order("created_at", { ascending: false }).limit(20);
          replies = data || [];
        }
        // Replies to MY COMMENTS (threading) — the bell only covered replies
        // to my posts, so being answered on someone else's post was invisible
        // in-app. Best-effort: pre-migration the parent column doesn't exist
        // and this quietly stays empty.
        let commentReplies = [];
        try {
          const { data: mine } = await supabase.from("community_replies")
            .select("id").eq("user_id", sbUser.id)
            .order("created_at", { ascending: false }).limit(200);
          const myReplyIds = (mine || []).map(r => r.id);
          if (myReplyIds.length > 0) {
            const seen = new Set(replies.map(r => r.id));
            const { data } = await supabase.from("community_replies")
              .select("id,post_id,content,created_at,user_id")
              .in("parent_reply_id", myReplyIds)
              .neq("user_id", sbUser.id)
              .order("created_at", { ascending: false }).limit(20);
            // A reply to my comment on my OWN post already shows in `replies`.
            commentReplies = (data || []).filter(r => !seen.has(r.id));
          }
        } catch { /* threaded column not migrated yet */ }
        if (cancelled) return;
        const merged = [
          ...(anns || []).map(a => ({
            id: `ann-${a.id}`, type: "platform", title: "Announcement",
            desc: (a.body || "").slice(0, 100),
            time: relTime(a.created_at),
            read: readIds.has(`ann-${a.id}`),
          })),
          ...replies.map(r => ({
            id: `rep-${r.id}`, type: "community",
            title: "New reply on your post",
            desc: (r.content || "").slice(0, 100),
            time: relTime(r.created_at),
            read: readIds.has(`rep-${r.id}`),
            postId: r.post_id,
          })),
          ...commentReplies.map(r => ({
            id: `rep-${r.id}`, type: "community",
            title: "New reply to your comment",
            desc: (r.content || "").slice(0, 100),
            time: relTime(r.created_at),
            read: readIds.has(`rep-${r.id}`),
            postId: r.post_id,
          })),
        ].sort((a, b) => {
          // Recent unread first
          if (a.read !== b.read) return a.read ? 1 : -1;
          return 0;
        });
        setNotifications(merged);
      } catch (e) { console.warn("notifications poll failed:", e.message); }
    };
    pull();
    const t = setInterval(pull, 60000);
    const onOpen = () => pull();
    window.addEventListener("bam-notif-open", onOpen);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener("bam-notif-open", onOpen); };
  }, [sbUser?.id]);
  // Practice Plan focus mode
  const [planFocusMode,setPlanFocusMode]=useState(false);
  // Listen for share-to-community events from content detail
  useEffect(()=>{
    const handler=(e)=>{
      const d=e.detail;
      if(d){setFocused(true);setEmbeddedContent(prev=>[...prev,contentEmbed(d)]);}
    };
    window.addEventListener("bam-share-content",handler);
    return ()=>window.removeEventListener("bam-share-content",handler);
  },[]);
  const [ptsVis,setPtsVis]=useState(false);
  const [activeTag,setActiveTag]=useState("All");
  // Community data — loaded from Supabase (replaces static POSTS_DATA/MEMBERS/LEADERBOARD).
  // Seeded from the last successful fetch so the feed paints instantly on
  // repeat visits; the live fetch replaces it in place (see loadCommunity).
  const [communityPosts, setCommunityPosts] = useState(readCommunityCache);
  // When a reply notification is tapped, we stash the post id here, jump to the
  // community feed, then scroll to + briefly highlight that post once it's in
  // the rendered list (see the effect below).
  const [pendingPostScroll, setPendingPostScroll] = useState(null);
  // Holds the retry timer for the jump-to-post scroll. A ref, not an
  // effect local, so consuming pendingPostScroll can't cancel it.
  const postScrollTimerRef = useRef(null);
  const [communityLeaderboard, setCommunityLeaderboard] = useState([]);
  // Blocked-user ids (Apple 1.2). Filter blocked authors out of the feed; sync
  // when the user blocks/unblocks someone elsewhere in the UI.
  const [communityBlocked, setCommunityBlocked] = useState([]);
  useEffect(() => {
    let cleanup = () => {};
    import("./community/blocklist").then(({ getBlockedIds, syncBlocklist }) => {
      const sync = () => setCommunityBlocked(getBlockedIds());
      sync();
      // Blocks follow the account: pull the list made on other devices.
      syncBlocklist().catch(() => {});
      window.addEventListener("bam-blocklist-changed", sync);
      cleanup = () => window.removeEventListener("bam-blocklist-changed", sync);
    });
    return () => cleanup();
  }, []);
  // Deep-link from a reply notification: once we're on the community feed and
  // the target post is in the rendered list, scroll to it and pulse a gold
  // ring so the coach sees which post the reply was on. Clears itself after.
  useEffect(() => {
    if (nav !== "community" || !pendingPostScroll) return;
    if (!communityPosts.some(p => String(p.id) === String(pendingPostScroll))) return;
    // Retry until the card is actually in the DOM — arriving from the
    // dashboard (trending click, push deep link) races the feed render +
    // page transition; a single miss used to silently drop the jump.
    const id = pendingPostScroll;
    // Consume the id BEFORE scheduling the retry loop, and keep the timer
    // in a ref rather than an effect-scoped local.
    //
    // The previous version stored the timer locally and returned
    // `() => clearTimeout(t)`. Clearing pendingPostScroll re-rendered
    // immediately, which ran that cleanup and killed the pending timeout
    // ~120ms before it could fire — so the jump-to-post never happened,
    // from a trending card OR a notification tap. The retry loop added in
    // #60 never got a chance to run either. The timer now outlives the
    // dep change and is only cancelled on unmount (effect below).
    setPendingPostScroll(null);
    let tries = 0;
    const attempt = () => {
      const el = document.getElementById(`bam-post-${id}`);
      if (!el) { if (++tries < 15) postScrollTimerRef.current = setTimeout(attempt, 150); return; }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.style.transition = "box-shadow .3s ease";
      el.style.borderRadius = "16px";
      el.style.boxShadow = `0 0 0 2px ${GOLD}, 0 0 28px ${GOLD}66`;
      setTimeout(() => { el.style.boxShadow = "none"; }, 2600);
    };
    postScrollTimerRef.current = setTimeout(attempt, 120);
  }, [nav, pendingPostScroll, communityPosts]);
  // Cancel any in-flight jump-to-post retry when the shell unmounts.
  useEffect(() => () => {
    if (postScrollTimerRef.current) clearTimeout(postScrollTimerRef.current);
  }, []);
  // Push-notification deep link. Cold starts are the hard case: the tap
  // handler (nativeInit) sets the hash, but boot re-parses/rewrites it and
  // the app lands on the dashboard — so the handler ALSO stashes the route
  // in localStorage, and we consume it here once the shell is mounted.
  // Warm taps arrive on the bam-push-route event and apply instantly.
  useEffect(() => {
    const apply = (route, postId, drillId, sectionId, qaId) => {
      if (postId) { setActiveTag("All"); setPendingPostScroll(String(postId)); }
      // A Weekly Q&A push opens the episode itself; the Resources page
      // consumes this key once the episodes have loaded.
      if (qaId) {
        try { sessionStorage.setItem("bam_open_qa", JSON.stringify({ id: String(qaId) })); } catch {}
      }
      // Deep link to one drill/workout: ContentPage consumes this key on
      // mount (same path as search + bookmarks), so stash it BEFORE the
      // hash change navigates there.
      if (drillId) {
        try {
          sessionStorage.setItem("bam_open_drill", JSON.stringify({
            sectionId: sectionId || "workouts", id: drillId,
          }));
        } catch {}
      }
      const target = typeof route === "string" && route.startsWith("#/") ? route : "#/community";
      try { window.location.hash = target; } catch {}
    };
    // Admin landing preference: open straight to Admin · Today instead of
    // the coach dashboard. Opt-in (Settings), per-device, and it yields to
    // everything else — a push tap, a deep link, or any hash the user
    // arrived with. Only fires on a bare app open, so it can't hijack a
    // shared URL, and only for admins.
    try {
      const wantsAdminHome = localStorage.getItem("bam_admin_home") === "1";
      const bareOpen = !window.location.hash || window.location.hash === "#/" || window.location.hash === "#/platform";
      // NB: do NOT reference `isAdmin` here — it's declared far below this
      // effect and would be in its temporal dead zone (that threw "Cannot
      // access 'isAdmin' before initialization" and crashed the shell on
      // every route). The preference is admin-gated at the point it can be
      // SET (the toggle only renders for admins), and the route itself is
      // admin-gated on arrival, so reading the flag alone is sufficient.
      if (wantsAdminHome && bareOpen && !localStorage.getItem("bam_push_route")) {
        window.location.hash = "#/admin/home";
      }
    } catch { /* storage unavailable — just don't redirect */ }
    try {
      const raw = localStorage.getItem("bam_push_route");
      if (raw) {
        localStorage.removeItem("bam_push_route");
        const j = JSON.parse(raw);
        // Only honor a fresh stash — a tap from yesterday shouldn't teleport
        // a normal app open into the community feed.
        if (j?.route && Date.now() - (j.at || 0) < 3 * 60 * 1000) apply(j.route, j.postId, j.drillId, j.sectionId, j.qaId);
      }
    } catch { /* corrupt stash — ignore */ }
    // An email link to one Community post: ?post=<id>#/community (the
    // Wednesday Drop's Saturday nudge uses it). Applied once, then cleared
    // from the address bar so a reload doesn't jump again.
    try {
      const qs = new URLSearchParams(window.location.search);
      const post = qs.get("post");
      if (post && /^[0-9a-f-]{36}$/i.test(post)) {
        qs.delete("post");
        const rest = qs.toString();
        window.history.replaceState(null, "", window.location.pathname + (rest ? `?${rest}` : "") + window.location.hash);
        apply("#/community", post);
      }
    } catch { /* no history API */ }
    const onEvt = (e) => apply(e?.detail?.route, e?.detail?.postId, e?.detail?.drillId, e?.detail?.sectionId, e?.detail?.qaId);
    window.addEventListener("bam-push-route", onEvt);
    // Admin panel → coach's public profile (panel state lives here).
    const onProfile = (e) => { if (e?.detail?.name) setProfileName(e.detail.name); };
    window.addEventListener("bam-open-profile", onProfile);
    return () => {
      window.removeEventListener("bam-push-route", onEvt);
      window.removeEventListener("bam-open-profile", onProfile);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Distinguishes "still fetching" from "fetched and genuinely empty" —
  // without this, an empty `communityPosts` array immediately renders the
  // "No posts yet" empty state during the brief load window, which reads
  // as a dead community. While true, the feed renders <CommunityLoader/>.
  const [communityLoading, setCommunityLoading] = useState(true);
  // True when the feed fetch failed (vs. genuinely empty) so we can show a
  // "couldn't load — retry" state instead of the "no posts yet" empty state,
  // which otherwise makes a network blip look like a dead community.
  const [communityError, setCommunityError] = useState(false);
  // Post ids the signed-in coach has liked / bookmarked — hydrates the heart
  // and Save states so they survive reloads (they used to be local-only).
  const [myLikedIds, setMyLikedIds] = useState(() => new Set());
  const [myBookmarkIds, setMyBookmarkIds] = useState(() => new Set());
  // Whether the last page came back full, so there may be another.
  const [communityHasMore, setCommunityHasMore] = useState(true);
  const [communityLoadingMore, setCommunityLoadingMore] = useState(false);
  const loadCommunity = useCallback(async () => {
    setCommunityError(false);
    setCommunityLoading(true);
    try {
      bamPerf("community fetch starting (importing communityApi)");
      const api = await import("./utils/communityApi");
      bamPerf("communityApi imported, calling fetchCommunityPosts");
      // ONLY the posts fetch gates the loader. Leaderboard, likes and
      // bookmarks used to sit inside the same awaited Promise.all, so the
      // slowest of four request chains decided when the feed painted —
      // now they hydrate behind the first paint (PostCard already re-syncs
      // liked/bookmarked props when they arrive late).
      const feedStart = Date.now();
      const posts = await api.fetchCommunityPosts({ limit: COMMUNITY_FEED_LIMIT });
      bamPerf(`fetchCommunityPosts resolved (posts=${posts.length})`);
      // An alarm, so a slow feed is seen in the numbers before a coach says
      // so. The thirty second feed of 2026-10-07 was 20 MB of profile photos
      // stored inline, and nothing in the app noticed.
      const feedMs = Date.now() - feedStart;
      if (feedMs > SLOW_FEED_MS) {
        try { logEvent("slow_load", { surface: "community_feed", ms: feedMs, posts: posts.length }); } catch { /* best effort */ }
        try { window.Sentry?.captureMessage?.(`Slow community feed: ${feedMs} ms`, "warning"); } catch { /* not configured */ }
      }
      setCommunityPosts(posts);
      setCommunityHasMore(posts.length >= COMMUNITY_FEED_LIMIT);
      setCommunityLoading(false);
      writeCommunityCache(posts);
      api.fetchLeaderboard({ limit: 10 })
        .then(leaders => { bamPerf(`fetchLeaderboard resolved (${leaders.length})`); setCommunityLeaderboard(leaders); })
        .catch(() => {});
      api.fetchMyLikes().then(setMyLikedIds).catch(() => {});
      api.fetchMyBookmarks().then(setMyBookmarkIds).catch(() => {});
    } catch (e) {
      console.error("Community load failed:", e);
      setCommunityError(true);
      setCommunityLoading(false);
    }
  }, []);
  // Only once there's a session. This used to fire on mount too, before
  // the session resolved, so the whole feed was fetched twice at boot.
  useEffect(() => { if (sbUser?.id) loadCommunity(); }, [sbUser?.id, loadCommunity]); // eslint-disable-line react-hooks/exhaustive-deps

  // The next page: everything unpinned and older than the oldest loaded
  // post. Appended, deduped by id.
  const loadMoreCommunity = useCallback(async () => {
    if (communityLoadingMore) return;
    const unpinned = communityPosts.filter(p => !p.pinnedAt);
    const oldest = unpinned[unpinned.length - 1];
    if (!oldest?.createdAt) { setCommunityHasMore(false); return; }
    setCommunityLoadingMore(true);
    try {
      const api = await import("./utils/communityApi");
      const more = await api.fetchCommunityPosts({ limit: COMMUNITY_FEED_LIMIT, before: oldest.createdAt });
      setCommunityHasMore(more.length >= COMMUNITY_FEED_LIMIT);
      setCommunityPosts(prev => {
        const seen = new Set(prev.map(p => String(p.id)));
        return [...prev, ...more.filter(p => !seen.has(String(p.id)))];
      });
    } catch (e) {
      console.error("Community load more failed:", e);
      setCommunityHasMore(false);
    } finally { setCommunityLoadingMore(false); }
  }, [communityPosts, communityLoadingMore]);

  // A push or a link can point at a post older than the loaded pages.
  // Fetch that one and slot it in by date so the scroll has a target.
  useEffect(() => {
    if (nav !== "community" || !pendingPostScroll || communityLoading) return;
    if (communityPosts.some(p => String(p.id) === String(pendingPostScroll))) return;
    let alive = true;
    import("./utils/communityApi").then(api => api.fetchCommunityPosts({ limit: 1, id: pendingPostScroll })).then(rows => {
      const post = rows?.[0];
      if (!alive || !post) return;
      setCommunityPosts(prev => {
        if (prev.some(p => String(p.id) === String(post.id))) return prev;
        const next = [...prev];
        let at = next.findIndex(p => !p.pinnedAt && new Date(p.createdAt).getTime() < new Date(post.createdAt).getTime());
        if (at < 0) at = next.length;
        next.splice(at, 0, post);
        return next;
      });
    }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav, pendingPostScroll, communityLoading]);
  // Real trending — ranked by likes + replies over the loaded feed. Replaces
  // the static TRENDING_POSTS mock, which ranked fabricated authors above the
  // real feed and wasn't tappable.
  const trendingPosts = useMemo(() => (
    [...communityPosts]
      .map(p => ({ p, score: (p.likes || 0) + (Array.isArray(p.replies) ? p.replies.length : 0) }))
      .filter(x => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map(x => x.p)
  ), [communityPosts]);
  const openPostInFeed = useCallback((postId) => {
    setActiveTag("All");
    setPendingPostScroll(postId);
    go("community");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Open a SPECIFIC drill from a dashboard widget — stash the deep link
  // ContentPage already consumes (same path as bookmarks + search), so the
  // widget lands on the item instead of the top of its section.
  const openDrillItem = useCallback((d) => {
    try { sessionStorage.setItem("bam_open_drill", JSON.stringify({ sectionId: d.section || "pd", id: d.id })); } catch {}
    go(d.section || "pd");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [profileName,setProfileName]=useState(null);
  const [favDrills,setFavDrills]=useState([]);
  // Favorite Lists — user-defined groupings of favorite drills.
  // lists: [{id, name, createdAt}]; listMembership: { [favKey]: string[] (list ids) }
  const [favLists, setFavLists] = useState([]);
  const [favListMembership, setFavListMembership] = useState({});
  const [favListFilter, setFavListFilter] = useState("all"); // "all" or listId
  const [favSort, setFavSort] = useState("recent"); // "recent" | "oldest" | "title" | "section"
  const [favBucket, setFavBucket] = useState("practice"); // "practice" | "learning"
  // Load persisted favorites + lists
  useEffect(() => {
    if(!sbUser?.id) return;
    (async () => {
      try {
        const { loadUserData } = await import("./utils/userData");
        const [saved, lists, membership] = await Promise.all([
          loadUserData("fav_drills", []),
          loadUserData("fav_lists", []),
          loadUserData("fav_list_membership", {}),
        ]);
        if (Array.isArray(saved)) setFavDrills(saved);
        if (Array.isArray(lists)) setFavLists(lists);
        if (membership && typeof membership === "object") setFavListMembership(membership);
      } catch {}
    })();
  }, [sbUser?.id]);
  // List mutations — all sync back to Supabase via userData.
  const persistLists = (next) => {
    setFavLists(next);
    import("./utils/userData").then(({ saveUserData }) => saveUserData("fav_lists", next)).catch(()=>{});
  };
  const persistMembership = (next) => {
    setFavListMembership(next);
    import("./utils/userData").then(({ saveUserData }) => saveUserData("fav_list_membership", next)).catch(()=>{});
  };
  const createFavList = (name) => {
    const n = (name || "").trim();
    if (!n) return;
    const id = "list_" + Date.now().toString(36);
    persistLists([...favLists, { id, name: n, createdAt: new Date().toISOString() }]);
    return id;
  };
  const renameFavList = (listId, name) => {
    const n = (name || "").trim();
    if (!n) return;
    persistLists(favLists.map(l => l.id === listId ? { ...l, name: n } : l));
  };
  const deleteFavList = (listId) => {
    persistLists(favLists.filter(l => l.id !== listId));
    const m = { ...favListMembership };
    Object.keys(m).forEach(k => { m[k] = (m[k] || []).filter(id => id !== listId); });
    persistMembership(m);
    if (favListFilter === listId) setFavListFilter("all");
  };
  // The roster, by name, purely to suggest collection names. Read once and
  // ignored if the table isn't reachable: a failed roster fetch must not
  // cost a coach the ability to group her bookmarks.
  const [rosterNames, setRosterNames] = useState([]);
  useEffect(() => {
    if (!sbUser?.id) return undefined;
    let alive = true;
    supabase.from("players").select("name").eq("user_id", sbUser.id).limit(60)
      .then(({ data }) => {
        if (!alive || !Array.isArray(data)) return;
        setRosterNames(data.map(r => r?.name).filter(Boolean));
      }, () => {});
    return () => { alive = false; };
  }, [sbUser?.id]);

  const toggleFavInList = (favKey, listId) => {
    const current = favListMembership[favKey] || [];
    const next = current.includes(listId) ? current.filter(x => x !== listId) : [...current, listId];
    persistMembership({ ...favListMembership, [favKey]: next });
  };
  const [myProfileOpen,setMyProfileOpen]=useState(false);
  const [profileMenuOpen,setProfileMenuOpen]=useState(false);
  const [showUpload,setShowUpload]=useState(false);
  // #/submit opens the upload form, so a notification can send a coach
  // straight there (an upload Mux couldn't read: mux-webhook asks them to
  // send it again). The address settles back on Home underneath it.
  useEffect(() => {
    const open = () => {
      if (!/^#\/?submit\/?$/.test(window.location.hash || "")) return;
      setShowUpload(true);
      try { window.history.replaceState(null, "", "#/dashboard"); } catch { /* old browser */ }
      setNav("dashboard");
    };
    open();
    window.addEventListener("hashchange", open);
    return () => window.removeEventListener("hashchange", open);
  }, []);
  const [showWorkoutBuilder,setShowWorkoutBuilder]=useState(false);
  const [mapOpen,setMapOpen]=useState(false);
  const [callBanner,setCallBanner]=useState(null);
  const [callsOpen,setCallsOpen]=useState(false);
  const [now,setNow]=useState(Date.now());
  const toggleFav=(item,sectionId)=>setFavDrills(prev=>{
    const key=`${sectionId}-${item.id}`;
    const removing=prev.find(f=>f.key===key);
    if(!removing) logEvent("content_save",{contentId:item.id,section:sectionId,title:item.title});
    const next = removing
      ? prev.filter(f=>f.key!==key)
      : [...prev,{...item,key,sectionId,savedAt:new Date().toISOString()}];
    import("./utils/userData").then(({ saveUserData }) => saveUserData("fav_drills", next)).catch(()=>{});
    return next;
  });
  const ptsRef=useRef(null);
  const scrollRef=useRef(null);
  const toggleDark=()=>setDark(d=>{const n=!d;try{localStorage.setItem("bam_dark_mode",String(n));}catch{}announceDarkMode(n);
    // Native: flip status bar style so the icons stay legible on the new
    // background. light:true → light icons (for dark backgrounds);
    // light:false → dark icons (for light backgrounds). No-op on web.
    setStatusBar({ light: n });
    return n;});

  // Sync the status bar on every mount so a cold launch into the
  // dashboard picks up the user's current theme (the boot-time Capacitor
  // config defaults to dark; light-mode users need this to flip).
  useEffect(() => { setStatusBar({ light: dark }); }, [dark]);

  // Settings state
  const [settingsNotifs, setSettingsNotifs] = useState(()=>{
    try{return JSON.parse(localStorage.getItem("bam_settings_notifs")||'{"inApp":true,"email":false,"callReminder":true}');}catch{return{inApp:true,email:false,callReminder:true};}
  });
  const updateNotifSetting=(key,val)=>{
    setSettingsNotifs(prev=>{const n={...prev,[key]:val};try{localStorage.setItem("bam_settings_notifs",JSON.stringify(n));}catch{}return n;});
  };
  const [settingsTz, setSettingsTz] = useState(()=>{
    try{return localStorage.getItem("bam_settings_tz")||Intl.DateTimeFormat().resolvedOptions().timeZone;}catch{return "America/New_York";}
  });
  const updateTz=(tz)=>{setSettingsTz(tz);try{localStorage.setItem("bam_settings_tz",tz);}catch{}};
  // Recurring call configs (one row per call: platform + cert). Loaded once on
  // mount. The cert call is only shown to coaches who hold the cert; everyone
  // sees the platform call. Empty until the fetch resolves, so the calls helper
  // falls back to the constants in calls.js until the DB answers.
  const [recurringCalls, setRecurringCalls] = useState([]);
  useEffect(()=>{
    let cancelled=false;
    supabase.from("recurring_call_config").select("*")
      .then(({data})=>{ if(!cancelled && Array.isArray(data)) setRecurringCalls(data); });
    return ()=>{ cancelled=true; };
  },[]);
  const visibleCalls = useMemo(
    ()=> recurringCalls.filter(c => c.audience !== "cert" || hasCert || certPurchased),
    [recurringCalls, hasCert, certPurchased]
  );
  const [mapVisible, setMapVisible] = useState(()=>{
    try{const v=localStorage.getItem("bam_map_visible");return v===null?true:v==="true";}catch{return true;}
  });
  // Saved to the account too (user_data map_visible), which is what the map
  // reads server-side: this used to stay on the device, so turning it off
  // never actually took anyone off the map.
  const toggleMapVisible=(v)=>{setMapVisible(v);try{localStorage.setItem("bam_map_visible",String(v));}catch{}
    Promise.resolve(saveUserData("map_visible", !!v)).catch(()=>{});};
  useEffect(()=>{
    if(!sbUser?.id) return;
    let live=true;
    loadUserData("map_visible", null).then(remote=>{
      if(!live) return;
      if(typeof remote==="boolean"){ setMapVisible(remote); try{localStorage.setItem("bam_map_visible",String(remote));}catch{} }
      else {
        // First time on this account: carry this device's choice up.
        let local=null; try{const v=localStorage.getItem("bam_map_visible"); local=v===null?null:v==="true";}catch{}
        if(local===false) Promise.resolve(saveUserData("map_visible", false)).catch(()=>{});
      }
    }).catch(()=>{});
    return ()=>{live=false;};
  },[sbUser?.id]);

  // Email preferences (profiles.email_prefs jsonb). Missing keys default to
  // opted-in, so we initialize with nothing and layer writes on top.
  const [emailPrefs, setEmailPrefs] = useState({});
  useEffect(() => {
    if (!sbUser?.id) return;
    // Array select (not maybeSingle) to avoid 406 on missing profile rows.
    supabase.from("profiles").select("email_prefs").eq("id", sbUser.id).limit(1)
      .then(({ data }) => { const row = data?.[0]; if (row?.email_prefs && typeof row.email_prefs === "object") setEmailPrefs(row.email_prefs); });
  }, [sbUser?.id]);
  const updateEmailPref = (key, val) => {
    setEmailPrefs(prev => {
      const next = { ...prev, [key]: val };
      if (sbUser?.id) {
        supabase.from("profiles").update({ email_prefs: next }).eq("id", sbUser.id)
          .then(({ error }) => { if (error) console.warn("email_prefs save:", error); });
      }
      return next;
    });
  };

  // Prompt 7: upgrade banner dismissals
  const [dismissedBanners, setDismissedBanners] = useState(()=>{
    try{return JSON.parse(localStorage.getItem("bam_dismissed_banners")||"[]");}catch{return[];}
  });
  const dismissBanner=(id)=>{setDismissedBanners(prev=>{const n=[...prev,id];try{localStorage.setItem("bam_dismissed_banners",JSON.stringify(n));}catch{}return n;});};

  // Prompt 2: Lifted profile state for app-wide propagation
  const [appProfile, setAppProfile] = useState({
    name:"Coach", handle:"", role:"",
    org:"", location:"",
    bio:"",
    ig:"", x:"", website:"",
    email:sbUser?.email||"", password:"",
    certs:[],
  });
  const [appPhoto, setAppPhoto] = useState(null);
  // Identity-stable props for the (memoized) PostCard feed — inline objects
  // and closures at the call site would defeat React.memo on every render.
  // MUST live below the appProfile/appPhoto declarations above (a TDZ crash
  // shipped when this sat earlier in the file).
  const meForPost = useMemo(() => ({ id: sbUser?.id, name: appProfile.name, photo: appPhoto }), [sbUser?.id, appProfile.name, appPhoto]);
  const onPostTagClick = useCallback((t) => setActiveTag(t), []);
  const onPostDelete = useCallback((id) => { setCommunityPosts(posts => posts.filter(x => x.id !== id)); import("./utils/communityApi").then(api => api.deletePost(id)).catch(e => console.error(e)); }, []);
  const communityCountRef = useRef(0);
  communityCountRef.current = communityPosts.length;
  const onPostAdminChanged = useCallback(() => { import("./utils/communityApi").then(api => api.fetchCommunityPosts({ limit: Math.max(COMMUNITY_FEED_LIMIT, communityCountRef.current) }).then(setCommunityPosts)); }, []);
  const [profileIsEmpty, setProfileIsEmpty] = useState(false);
  const appFirstName = appProfile.name?.split(" ")[0] || "Coach";
  const appInitials = appProfile.name?.split(" ").map(w=>w[0]||"").join("").toUpperCase()||"C";

  // Admin flag + "jump-to-user" panel state from AdminContext.
  const { isAdmin, setIsAdmin: setAdminFlag } = useAdmin();
  // profiles.allowlist_features, for doors given by hand (the Film Desk's contributors).
  const [profileAllowlist, setProfileAllowlist] = useState([]);
  // Cert access: purchased OR admin. Admins (Coleman + team) must never see
  // the cert locked — the DB purchase flag doesn't apply to them. NOTE: this
  // must stay declared AFTER certPurchased and isAdmin (declaration order).
  const certAccess = certPurchased || isAdmin;
  // Pre-launch staged-rollout gates. Allowlisted accounts and admins
  // always see real features; everyone else gets a Coming Soon screen.
  const askbamOn      = isFeatureOn("askbam",      sbUser?.email) || isAdmin;
  const bamiqOn       = bamiqAvailable(sbUser?.id);
  const memberMapOn   = mapAvailable(sbUser?.email) || isAdmin;
  const filmStudioOn  = isFeatureOn("filmStudio",  sbUser?.email) || isAdmin;
  // Advantage Flow: the pilot's accounts, by the server's list (/api/afs-narrate says), on the web.
  const [afsOn, setAfsOn] = useState(false);
  const [afsTagger, setAfsTagger] = useState(false);
  const [afsPaid, setAfsPaid] = useState(false);       // a paid plan, not a trial: the platform's $19.99 offer
  useEffect(() => {
    if (!sbUser?.id || isNative()) { setAfsOn(false); return undefined; }
    let gone = false;
    (async () => {
      try {
        const r = await fetch("/api/afs-narrate", { headers: await assistHeaders() });
        const out = r.ok ? await r.json().catch(() => ({})) : {};
        // Joined a team by a staff invite to this email just now (2026-11-03): open on that team.
        if (out.joined && /^[0-9a-f-]{36}$/i.test(out.joined)) { try { localStorage.setItem("bam_afs_org", out.joined); } catch { /* private window */ } }
        if (!gone) { setAfsOn(!!out.allowed); setAfsTagger(!!out.tagger); setAfsPaid(!!out.paid); }
      } catch { /* not a pilot account, or offline: no row */ }
    })();
    return () => { gone = true; };
  }, [sbUser?.id]);
  // The Blueprint's book owners (2026-10-26): an account with only the book opens on its resources, not the paywall.
  const [blueprintOwner, setBlueprintOwner] = useState(null);       // null: still asking
  useEffect(() => {
    if (!sbUser?.id || isNative() || isSubscribed !== false) { setBlueprintOwner(false); return undefined; }
    let gone = false;
    setBlueprintOwner(null);
    (async () => {
      try {
        const r = await fetch("/api/blueprint", { headers: await assistHeaders() });
        const out = r.ok ? await r.json().catch(() => ({})) : {};
        if (!gone) setBlueprintOwner(!!out.access);
      } catch { if (!gone) setBlueprintOwner(false); /* offline */ }
    })();
    return () => { gone = true; };
  }, [sbUser?.id, isSubscribed]);
  const devStudioOn   = isFeatureOn("devStudio",   sbUser?.email) || isAdmin;
  const constraintsOn = isFeatureOn("constraints", sbUser?.email) || isAdmin;
  const howICoachOn   = isFeatureOn("howICoach",   sbUser?.email) || isAdmin;

  // Fetch profile from Supabase when user is available
  useEffect(()=>{
    if(!sbUser) return;
    let mounted=true;
    supabase.from("profiles").select("*").eq("id",sbUser.id).limit(1).then(({data:rows,error})=>{
      const data=rows?.[0];
      if(!mounted) return;
      if(error){
        // Network / RLS error — DON'T trap the user in ProfileSetup. We
        // can't tell from an error alone whether the profile is empty,
        // and forcing onboarding on a transient failure is worse than
        // letting them in. Capacitor's WKWebView occasionally drops a
        // request on cold start; this defensive bail handles it.
        return;
      }
      if(!data){
        // No profile row → genuinely a fresh signup, route to ProfileSetup.
        setProfileIsEmpty(true);
        return;
      }
      if (mounted) setAdminFlag(!!data.is_admin);
      if (mounted) setProfileAllowlist(Array.isArray(data.allowlist_features) ? data.allowlist_features : []);
      // "Empty" really means "missing any of the fields ProfileSetup
      // requires" — first name, coaching role, city, and country. This
      // mirrors ProfileSetup.canStep1 so a profile that doesn't satisfy
      // the gate's submit button can't satisfy this gate's "you're done"
      // check either. Keeping them out of sync produces the original
      // ProfileSetup-leak bug: paid coaches with first_name set but no
      // role/location escape onboarding and post as anonymous-looking.
      // First AND last name (Coleman, 2026-10-13: no one is just "Coach").
      // A coach missing either gets ProfileSetup on next open, prefilled.
      const hasName = !!(data.first_name && data.first_name.trim() && data.last_name && data.last_name.trim());
      const hasRole = !!(data.role && data.role.trim());
      const hasCity = !!(data.city && data.city.trim());
      const hasCountry = !!(data.country && data.country.trim());
      // Experience and age groups are required too (2026-11-02), mirroring ProfileSetup.canStep1: a paying coach
      // missing either sees setup once more, prefilled, to add them.
      const hasExperience = !!(data.experience && data.experience.trim());
      const hasAges = Array.isArray(data.age_groups) && data.age_groups.length > 0;
      setProfileIsEmpty(!hasName || !hasRole || !hasCity || !hasCountry || !hasExperience || !hasAges);
      const name=[data.first_name,data.last_name].filter(Boolean).join(" ")||"Coach";
      setAppProfile(prev=>({...prev,
        name,
        handle:data.handle||`@${(data.first_name||"").toLowerCase()}${(data.last_name||"").toLowerCase()}`,
        role:data.role||prev.role,
        org:data.org||prev.org,
        location:[data.city,data.country].filter(Boolean).join(", ")||prev.location,
        bio:data.bio||prev.bio,
        ig:data.ig||prev.ig, x:data.x||prev.x, website:data.website||prev.website,
        email:sbUser.email||prev.email,
        certs:data.certs||prev.certs,
      }));
      if(data.avatar_url) setAppPhoto(avatarSrc(data.avatar_url, 64) || null);
      // Sync cert status from Supabase
      if(data.cert_status==="certified") saveCertProgress({"final-exam-passed":true});
      if(data.cert_status==="exam_submitted") saveCertProgress({"exam-submitted":true});
    });
    return ()=>{mounted=false;};
  },[sbUser]);

  // Clear the "profile empty" flag as soon as the user has a real name + role.
  // This hides the "Complete your profile" banner without a reload once they save.
  useEffect(()=>{
    const hasName = appProfile.name && appProfile.name !== "Coach" && appProfile.name.trim();
    const hasRole = appProfile.role && appProfile.role.trim();
    if (hasName && hasRole) setProfileIsEmpty(false);
  },[appProfile.name, appProfile.role]);

  // Prompt 1: greeting with user name
  const [userName, setUserName] = useState(null);
  useEffect(()=>{
    const t=setTimeout(()=>{ setUserName(appFirstName); },600);
    return ()=>clearTimeout(t);
  },[appFirstName]);
  const getGreeting=()=>{
    const h=new Date().getHours();
    if(h<12) return "Good morning";
    if(h<17) return "Good afternoon";
    return "Good evening";
  };

  // Prompt 6: announcements
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState(()=>{
    try{ return JSON.parse(localStorage.getItem("bam_dismissed_announcements")||"[]"); }catch{ return []; }
  });
  const dismissAnnouncement=(id)=>{
    setDismissedAnnouncements(prev=>{
      const next=[...prev,id];
      try{localStorage.setItem("bam_dismissed_announcements",JSON.stringify(next));}catch{}
      return next;
    });
  };
  const ANNOUNCEMENTS_DATA=[];
  const visibleAnnouncements=ANNOUNCEMENTS_DATA.filter(a=>!dismissedAnnouncements.includes(a.id));
  const [expandedAnn, setExpandedAnn] = useState(visibleAnnouncements[0]?.id||null);

  // Prompt 5: stats loading
  const [statsLoaded, setStatsLoaded] = useState(false);
  useEffect(()=>{
    const t=setTimeout(()=>setStatsLoaded(true),1400);
    return ()=>clearTimeout(t);
  },[]);

  // Real user stats for the dashboard "Your Stats" card. Härmo flagged
  // 2026-05-05 that the card showed all zeros despite real activity.
  //
  // Strategy: ONE round-trip through user_dashboard_stats RPC for
  // cross-device totals (Time on Platform, Day Streak, Posts, Comments).
  // The RPC reads analytics_events server-side so the numbers reflect
  // every device the coach has signed in on, not just localStorage on
  // this one. Plans count stays local — practice plans are stored in
  // localStorage + user_data and don't have a server-side aggregate
  // table. Falls back to per-device counts if the RPC isn't deployed
  // yet, so this commit is safe to ship before running the migration.
  const [userStats, setUserStats] = useState({
    posts: 0, plans: 0, comments: 0, timeSeconds: 0, streak: 0,
  });
  useEffect(() => {
    if (!sbUser) return;
    let cancelled = false;
    (async () => {
      // Plans: localStorage (canonical store for practice plans).
      let plansCount = 0;
      try {
        const raw = localStorage.getItem("bam_saved_plans");
        if (raw) {
          const arr = JSON.parse(raw);
          if (Array.isArray(arr)) plansCount = arr.length;
        }
      } catch {}

      // Cross-device totals via RPC.
      try {
        const { data, error } = await supabase.rpc("user_dashboard_stats");
        if (!error && Array.isArray(data) && data[0]) {
          if (cancelled) return;
          setUserStats({
            posts:        Number(data[0].posts_count)    || 0,
            plans:        plansCount,
            comments:     Number(data[0].comments_count) || 0,
            timeSeconds:  Number(data[0].total_seconds)  || 0,
            streak:       Number(data[0].day_streak)     || 0,
          });
          return;
        }
        // RPC missing → fall back to direct queries for posts/comments
        // and a localStorage-only Time on Platform / Day Streak. Those
        // are per-device but better than zeros.
        const [postsRes, repliesRes] = await Promise.all([
          supabase.from("community_posts")
            .select("*", { count: "exact", head: true })
            .eq("author_id", sbUser.id)
            .is("deleted_at", null),
          supabase.from("community_replies")
            .select("*", { count: "exact", head: true })
            .eq("user_id", sbUser.id),
        ]);
        const localFallback = computeLocalSessionStats();
        if (cancelled) return;
        setUserStats({
          posts:       postsRes.count || 0,
          plans:       plansCount,
          comments:    repliesRes.count || 0,
          timeSeconds: localFallback.timeSeconds,
          streak:      localFallback.streak,
        });
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          console.warn("[Your Stats]", e?.message || e);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [sbUser]);

  // Live content from Supabase (with static fallback)
  const { items: allDrills } = useAllDrills();
  // Weekly Q&A questions search like drills do, so "how do I run a 5-out
  // with kids who can't shoot" finds the answer and not just the episode.
  const qaRowsForSearch = useWeeklyQa(true);
  // The newest episode sits on the dashboard for its first days, once per
  // episode: dismiss it and it stays gone until the next one lands.
  const latestQa = qaRowsForSearch[0] || null;
  const [qaSeen, setQaSeen] = useState(() => { try { return localStorage.getItem("bam_qa_seen") || ""; } catch { return ""; } });
  const openLatestQa = () => {
    if (!latestQa) return;
    try { sessionStorage.setItem("bam_open_qa", JSON.stringify({ id: latestQa.id })); } catch {}
    go("resources");
  };
  // The newest Wednesday Drop, for Home's lead card. Seen is per device: the
  // drop page writes it when it opens. The card leads Home until the drop is
  // opened or closed with its X, however long that takes (Coleman,
  // 2026-10-22), so it follows the newest drop, not only this week's.
  const [latestDrop, setLatestDrop] = useState(null);
  const [dropSeenId, setDropSeenId] = useState(() => { try { return localStorage.getItem("bam_drop_seen") || ""; } catch { return ""; } });
  const [dropClosedId, setDropClosedId] = useState(() => { try { return localStorage.getItem("bam_drop_closed") || ""; } catch { return ""; } });
  // Loaded on the first page a coach lands on, whichever it is (the drop bar shows on every page),
  // and again on each visit to Home.
  const dropLoaded = useRef(false);
  useEffect(() => {
    if (nav !== "dashboard" && dropLoaded.current) return;
    dropLoaded.current = true;
    let off = false;
    loadLatestDrop().then(d => { if (!off) setLatestDrop(d || null); }).catch(() => {});
    try { setDropSeenId(localStorage.getItem("bam_drop_seen") || ""); } catch { /* storage off */ }
    return () => { off = true; };
  }, [nav]);
  const closeDropReminder = useCallback((action) => {
    const id = String(action.id || "").replace(/^drop:/, "");
    setDropClosedId(id);
    try { localStorage.setItem("bam_drop_closed", id); } catch { /* storage off */ }
    logEvent("home_drop_closed", { id });
  }, []);
  const ALL_CONTENT_ITEMS = useMemo(() => {
    const base = allDrills.length > 0 ? allDrills : STATIC_CONTENT_ITEMS;
    const qa = qaSearchItems(qaRowsForSearch);
    return qa.length ? [...base, ...qa] : base;
  }, [allDrills, qaRowsForSearch]);

  // Prompt 3: watched content tracking
  const [watchedIds, setWatchedIds] = useState(()=>{
    try{ return JSON.parse(localStorage.getItem("bam_watched_ids")||"[]"); }catch{ return []; }
  });
  useEffect(()=>{if(!sbUser)return;loadUserData("watched_ids",[]).then(d=>{if(Array.isArray(d)&&d.length>0)setWatchedIds(prev=>{const merged=[...new Set([...prev,...d])];try{localStorage.setItem("bam_watched_ids",JSON.stringify(merged));}catch{}return merged;});});},[sbUser]);

  const C=dark?DARK:LIGHT;

  // Fake notification removed — will use real notifications from Supabase
  // useEffect(()=>{ ... setNotif("New drill drop: ...") ... },[]);

  useEffect(()=>{
    const ob=new IntersectionObserver(([e])=>{ if(e.isIntersecting) setPtsVis(true); },{threshold:.3});
    if(ptsRef.current) ob.observe(ptsRef.current);
    return ()=>ob.disconnect();
  },[nav]);

  // Call notification timer. Dismissed calls are tracked in sessionStorage
  // so a user's X-out sticks for the rest of the session (was getting re-
  // surfaced on every tick of this interval).
  const callDismissedKey = (c) => `${c.id || c.title || ""}:${c.dateTime}`;
  const [dismissedCalls, setDismissedCalls] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem("bam_dismissed_calls") || "[]")); }
    catch { return new Set(); }
  });
  useEffect(()=>{
    const iv=setInterval(()=>{
      const n=Date.now();
      setNow(n);
      const calls=getAllUpcomingCallsMulti(visibleCalls);
      const upcoming=calls.find(c=>{
        const diff=c.dateTime-n;
        if (dismissedCalls.has(callDismissedKey(c))) return false;
        return diff>0 && (diff<=86400000); // within 24h
      });
      if(upcoming){
        const diff=upcoming.dateTime-n;
        setCallBanner({...upcoming, diff});
      } else {
        setCallBanner(null);
      }
    },1000);
    return ()=>clearInterval(iv);
  },[dismissedCalls]);

  const dismissCall = (call) => {
    if (!call) return;
    const key = callDismissedKey(call);
    setDismissedCalls(prev => {
      const next = new Set(prev);
      next.add(key);
      try { sessionStorage.setItem("bam_dismissed_calls", JSON.stringify([...next])); } catch {}
      return next;
    });
    setCallBanner(null);
  };

  const go=(id)=>{ if(id===nav){setMobileMenuOpen(false);return;} haptic("selection"); window.location.hash="#/"+id; setPtsVis(false); setActiveTag("All"); setMobileMenuOpen(false); logEvent("page_view",{page:id}); };
  // The menu under your name (ProfileMenu), on the desktop card and in the
  // phone drawer alike.
  const pickProfileMenu=(id)=>{
    setMobileMenuOpen(false);
    if(id==="profile"){ setMyProfileOpen(true); return; }
    if(id==="submit"){ setShowUpload(true); return; }
    go(id);
  };
  // Timers tracked so an unmount mid-transition can't strand the sidebar.
  // sb-nav-exit animates to opacity:0 with fill-mode both, so a lost timer
  // leaves the whole nav invisible with no way to get it back.
  const modeTimers = useRef([]);
  useEffect(()=>()=>{ modeTimers.current.forEach(clearTimeout); }, []);

  const switchMode = (mode) => {
    // Whatever happens, never leave the nav mid-animation.
    modeTimers.current.forEach(clearTimeout);
    modeTimers.current = [];
    if(mode===appMode){
      // Already in this mode — still navigate to its home. Without this,
      // tapping "Platform" while parked on the cert landing route (same
      // appMode) silently did nothing. Applies on mobile and desktop.
      setModeTransition(false);
      go(mode==="certified"?"certified":"dashboard");
      setMobileMenuOpen(false);
      return;
    }
    setModeTransition(true);
    modeTimers.current.push(setTimeout(()=>{
      // appMode is derived from the route now, so navigating is the whole
      // job — the effect on `nav` sets the mode.
      go(mode==="certified"?"certified":"dashboard");
      modeTimers.current.push(setTimeout(()=>setModeTransition(false),250));
    },150));
  };
  const cyclePrompt=()=>{ setPVis(false); setTimeout(()=>{ setPIdx(i=>(i+1)%PROMPTS.length); setPVis(true); },360); };

  // Org membership, from OrgContext (provider mounted in dashboard/index.js).
  // Members of a consulting org get a "My Organization" tile above Calls and
  // Chats; everyone else sees nothing extra. Admins reach every org's portal
  // from Admin, People, Orgs ("Open portal"), not from the sidebar.
  // "New" markers: has anything landed in a section since this coach last
  // opened it. Guardrails (a 14-day floor, nothing on a first visit) live in
  // content_new_counts and useNewContent, so a badge that's showing always
  // means something actually arrived recently.
  const { counts: newCounts, lastSeen: sectionLastSeen } = useNewContent(nav, !!sbUser?.id);
  // Which pages this coach has actually opened, and the one worth pointing at
  // next. See hooks/useUnexplored — it stays quiet for the first week and for
  // anything they've waved off.
  // One answer to "is the Learning Lab open", shared by the dashboard nudge
  // and the pinned-challenge banner. The Lab page and Foundations ask the
  // same question of the same flag.
  const labOpen = isAdmin || isFeatureOn("learningLab");
  const NewDot = ({ id }) => {
    const n = newCounts?.[id] || 0;
    if (!n || nav === id) return null;
    return (
      <span title={`${n} added since you last looked`}
        style={{fontSize:8.5,fontWeight:800,color:"#0A0A0A",background:GOLD,
          minWidth:16,height:16,borderRadius:8,padding:"0 5px",display:"flex",
          alignItems:"center",justifyContent:"center",letterSpacing:.3,flexShrink:0}}>
        {n > 9 ? "9+" : n}
      </span>
    );
  };

  // memberOrgs is a real org_members row in an org that isn't an intern
  // location: an admin's view of every org doesn't count. The tile opens the
  // org it names, so if the portal was last left on another org (an admin
  // opening one from Admin), the tap switches back to theirs.
  const { memberOrgs, currentOrg, setCurrentOrgId } = useOrg();
  const tileOrg = (memberOrgs || []).find(o => o.id === currentOrg?.id) || (memberOrgs || [])[0] || null;
  const openMyOrg = () => { if (tileOrg && tileOrg.id !== currentOrg?.id) setCurrentOrgId(tileOrg.id); go("org"); };
  // The Intern Hub: interns, named directors and location leads (hubVisible).
  const internAccess = useInternAccess(sbUser?.id);
  const showInternTile = hubVisible(internAccess) && internHubAvailable();
  // Film Desk: admins (to review), active remote film interns, and contributors given
  // "filmdesk" in allowlist_features (filmdesk/filmdeskAccess.js). The server enforces
  // the same rule (api/_filmdesk/access.js); the tile is the door.
  const showFilmDeskTile = filmdeskAvailable() && canUseFilmDesk({ admin: isAdmin, enrollment: internAccess?.intern, allowlist: profileAllowlist });
  // Tiles in the sidebar; on phones they also stay in Coach's sheet, where
  // they were rows before.
  const tileEntries = [
    ...(tileOrg ? [{ id: "org", label: "My Organization", Icon: Building2 }] : []),
    ...(showInternTile ? [{ id: "interns", label: "Intern Hub", Icon: GraduationCap }] : []),
    ...(showFilmDeskTile ? [{ id: "filmdesk", label: "Film Desk", Icon: FilmIcon }] : []),
  ];

  // The sidebar is grouped (constants/sidebarGroups.js): Home and Community,
  // then Learn, Coach and Drill Library. Chats and Calls are tiles at the
  // foot of the sidebar, with My Organization and the Intern Hub above them;
  // My Portal is in the menu under your name. Everything that decides
  // whether a row shows for this account is here, in one resolver.
  const navById = Object.fromEntries(NAV.map(n => [n.id, n]));
  const resolveNav = (id) => {
    const n = navById[id];
    if (!n || n.hidden) return null;
    // The Lab is gated while it's in review. A sidebar entry onto a locked
    // preview is a door onto an empty room, which is the thing the tour rules
    // exist to stop.
    if (id === "learn" && !labOpen) return null;
    // BAM Film Studio is hidden in the native app for v1 (Coleman's call:
    // heavy upload/AFS UX isn't ready for mobile); stays on web.
    if (id === "film") return isNative() ? null : (filmStudioOn ? n : { ...n, comingSoon: true });
    // Advantage Flow (2026-11-03): every signed-in account on the web. With it, the app; without it, #/afs is the
    // locked preview (AFSLockedPreview.jsx). Never in the iOS app (CLAUDE.md B, anti-steering). afsPitch.afsInSidebar.
    if (id === "afs") return afsInSidebar({ signedIn: !!sbUser?.id, native: isNative() }) ? n : null;
    // Pre-launch staged: shown to everyone, marked SOON until it opens.
    if (id === "dev-studio") return devStudioOn ? n : { ...n, comingSoon: true, pro: false };
    // Remaining beta items (consulting) stay allowlist-only.
    if (n.beta && !isAllowedBetaUser(sbUser?.email)) return null;
    return n;
  };
  const navGroups = buildGroups(resolveNav);
  const phoneGroups = tileEntries.length
    ? navGroups.map(g => g.id === "g-coach" ? { ...g, items: [...g.items, ...tileEntries] } : g)
    : navGroups;
  // Home and Community are rows; Referrals follows them for coaches with a code.
  const topRows = [{ ...navById.dashboard, label: "Home" }, navById.community,
    ...(hasReferralCode ? [{ id:"referrals", label:"Referrals", Icon: Megaphone, group:"main" }] : [])];

  const NAV_SECTIONS = [
    { label: null, items: topRows, subs: navGroups },
  ];
  // Every surface this account can actually reach, by visitKey. The tour and
  // the "you haven't opened this" nudge both filter on it, so neither can
  // point a coach at a door the sidebar hides for them: the gated Lab, the
  // allowlisted consulting page, Chats for someone in no chats.
  const availableSurfaces = (() => {
    const out = new Set(["dashboard","settings","resources","portal","community","toolbox","tour","certified","dev-studio","ti"]);
    NAV_SECTIONS.forEach(sec => {
      (sec.items || []).forEach(n => { if (!n.comingSoon) out.add(n.id); });
      (sec.subs || []).forEach(sub => (sub.items || []).forEach(n => { if (!n.comingSoon) out.add(n.id); }));
      (sec.standalone || []).forEach(n => { if (!n.comingSoon) out.add(n.id); });
    });
    // Collections, one tap from the library's chips.
    ["movement","pro"].forEach(id => out.add(id));
    // Not sidebar rows, but tiles in the sidebar.
    out.add("calls");
    if (hasChats) out.add("chats");
    tileEntries.forEach(t => out.add(t.id));
    if (!labOpen) out.delete("learn");
    // Advantage Flow is in everyone's sidebar, but for an account without it the page is a locked preview: the tour
    // and the nudge keep treating it as gated, as before.
    if (!afsOn) out.delete("afs");
    return out;
  })();
  const unexplored = useUnexplored(nav, { enabled: !!sbUser?.id, joinedAt: sbUser?.created_at,
    available: availableSurfaces });

  /* The house phone breakpoint, --bp-md in dashboard.css. The dashboard
     needs it in JS rather than CSS now, because the three action paths are
     three cards above it and one switcher below: a different component
     tree, not a different set of styles. */
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 700
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const on = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener("resize", on);
    window.addEventListener("orientationchange", on);
    return () => {
      window.removeEventListener("resize", on);
      window.removeEventListener("orientationchange", on);
    };
  }, []);

  /* ── The one thing to do next ───────────────────────────────────────────
     Eight blocks used to compete for the top of this page and three of them
     sat above the coach's own name. They are candidates now: the ranking is
     in constants/nextAction.js, the card is components/NextUp.jsx, and all
     this does is gather the facts those two need. Every fact here was
     already being read for the block it used to render.

     A missing fact produces fewer cards rather than a wrong one, so this can
     run on the first paint without waiting for anything. */

  const [startHereState, setStartHereState] = useState(null);
  useEffect(() => {
    if (!sbUser?.id) return;
    let alive = true;
    loadUserData("start_here", {})
      .then(v => { if (alive) setStartHereState(v && typeof v === "object" && !Array.isArray(v) ? v : {}); })
      .catch(() => { if (alive) setStartHereState({}); });
    return () => { alive = false; };
  }, [sbUser?.id]);

  /* Arriving at the page IS doing the step. StartHere.jsx did this marking
     while it was a card on this page; when it became a candidate for the
     notice slot the marking left with it, and the card sat on "Open the
     Learning Lab" however many times a coach opened the Learning Lab. */
  useEffect(() => {
    if (!startHereState || startHereState.__dismissed) return;
    const step = stepForArrival(nav);
    if (!step || startHereState[step.id]) return;
    const next = { ...startHereState, [step.id]: new Date().toISOString() };
    setStartHereState(next);
    saveUserData("start_here", next).catch(() => {});
  }, [nav, startHereState]);

  /* What is half finished. The reader keeps a small index of pages read
     per masterclass (useMasterclassProgress.js), in this browser and on the
     account; the larger of the two wins, so neither device can shrink the
     other's count. */
  const [mcIndexRemote, setMcIndexRemote] = useState(null);
  useEffect(() => {
    if (!sbUser?.id) return;
    let alive = true;
    loadUserData(MC_INDEX_KEY, {})
      .then(v => { if (alive) setMcIndexRemote(v && typeof v === "object" && !Array.isArray(v) ? v : {}); })
      .catch(() => {});
    return () => { alive = false; };
  }, [sbUser?.id]);

  const resumableMasterclasses = useMemo(() => {
    const local = readProgressIndex();
    const remote = mcIndexRemote || {};
    // Only the index counts. The older browser-only percentages
    // (bam_master_progress) were tried as a fallback and are not safe: the
    // reader used to save anything opened outside the shelf under item 9001,
    // the AAU masterclass, so coaches who never opened AAU were offered
    // "Finish The Development-First AAU Model". A half-read masterclass
    // shows here again the next time a page of it is read.
    // Only what a coach can actually open: not held back, not shelved, and
    // not still counting down to its release.
    const shelf = (CONTENT.master?.items || []).filter(it =>
      it && it.moduleKey && !it.comingSoon && !it.shelved &&
      !(it.availableAt && new Date(it.availableAt).getTime() > Date.now()));
    const out = [];
    for (const it of shelf) {
      const a = local[it.moduleKey], b = remote[it.moduleKey];
      const best = [a, b].filter(e => e && Number(e.total) > 0)
        .sort((x, y) => Number(y.read) - Number(x.read))[0];
      const base = { key: it.moduleKey, moduleKey: it.moduleKey, title: it.title,
        art: masterclassCover(it), to: "master" };
      if (best) {
        out.push({ ...base, pagesRead: Number(best.read),
          pagesLeft: Number(best.total) - Number(best.read), at: best.at });
      }
    }
    // Most recently read first, so the one they were just in leads.
    return out.sort((x, y) => String(y.at || "").localeCompare(String(x.at || "")));
  }, [mcIndexRemote, nav]);

  /* The two newest masterclasses a coach can open, for the cover of Learn's
     Masterclasses card: the second stands in when the first is already the
     banner above it. Same shelf rules as above. */
  const recentMasterclasses = useMemo(() => (CONTENT.master?.items || [])
    .filter(x => x && x.moduleKey && !x.comingSoon && !x.shelved && x.date_added &&
      !(x.availableAt && new Date(x.availableAt).getTime() > Date.now()))
    .sort((x, y) => String(y.date_added).localeCompare(String(x.date_added)))
    .slice(0, 2)
    .map(it => ({ key: it.moduleKey, title: it.title, art: masterclassCover(it) })), []);

  /* Skipping lasts the day, not the session and not forever. A card skipped
     on Tuesday should come back on Wednesday with a different number in it,
     and a card skipped before lunch should not reappear after it. */
  const [skippedActions, setSkippedActions] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("bam_skipped_actions") || "{}");
      return raw.day === new Date().toDateString() && Array.isArray(raw.ids) ? raw.ids : [];
    } catch { return []; }
  });
  const skipAction = useCallback((id) => {
    setSkippedActions(prev => {
      const next = prev.includes(id) ? prev : [...prev, id];
      try {
        localStorage.setItem("bam_skipped_actions",
          JSON.stringify({ day: new Date().toDateString(), ids: next }));
      } catch { /* private window */ }
      logEvent("home_next_up_skip", { id });
      return next;
    });
  }, []);

  /* What landed recently that this coach has not opened. Masterclasses come
     off the shelf (openable, and never opened here: the progress index or
     the opened stamp in useMasterclassProgress). X and O and the drill
     libraries come from the sidebar's own "new since you last looked"
     counts, so the card and the badge can never disagree. */
  const freshContent = useMemo(() => {
    const local = readProgressIndex();
    const remote = mcIndexRemote || {};
    const opened = readOpenedMasterclasses();
    const masterclasses = (CONTENT.master?.items || [])
      .filter(it => it && it.moduleKey && !it.comingSoon && !it.shelved && it.date_added &&
        !(it.availableAt && new Date(it.availableAt).getTime() > Date.now()) &&
        !local[it.moduleKey] && !remote[it.moduleKey] && !opened[it.moduleKey])
      .sort((x, y) => String(y.date_added).localeCompare(String(x.date_added)))
      .map(it => ({ key: it.moduleKey, title: it.title, sub: it.sub, art: masterclassCover(it), addedAt: it.date_added }));
    const DRILL_SECTIONS = ["pd", "team", "movement", "workouts"];
    let drillCount = 0, drillTo = "pd", drillMost = 0;
    for (const id of DRILL_SECTIONS) {
      const n = Number(newCounts?.[id]) || 0;
      drillCount += n;
      if (n > drillMost) { drillMost = n; drillTo = id; }
    }
    return { masterclasses, xo: Number(newCounts?.xo) || 0,
      drills: { count: drillCount, to: drillTo, label: sectionLabel(drillTo) } };
  }, [newCounts, mcIndexRemote, nav]);

  /* The masterclass added most recently, opened or not, for Learn's banner:
     it announces the newest thing until the coach opens it or something
     newer lands, and an opened newest one must not hand the banner to an
     older unopened one. */
  const newestMasterclass = useMemo(() => {
    const local = readProgressIndex();
    const remote = mcIndexRemote || {};
    const opened = readOpenedMasterclasses();
    const it = (CONTENT.master?.items || [])
      .filter(x => x && x.moduleKey && !x.comingSoon && !x.shelved && x.date_added &&
        !(x.availableAt && new Date(x.availableAt).getTime() > Date.now()))
      .sort((x, y) => String(y.date_added).localeCompare(String(x.date_added)))[0];
    if (!it) return null;
    return { key: it.moduleKey, title: it.title, sub: it.sub, art: masterclassCover(it), addedAt: it.date_added,
      opened: !!(local[it.moduleKey] || remote[it.moduleKey] || opened[it.moduleKey]) };
  }, [mcIndexRemote, nav]);

  /* Masterclasses this coach hasn't opened that landed in the last ten days:
     Learn's banner leads with the newest, and its tile counts them. */
  const learnFreshMasterclasses = (freshContent.masterclasses || []).filter(m => {
    const t = new Date(m.addedAt).getTime();
    return Number.isFinite(t) && Date.now() - t <= 10 * 86400000;
  });

  /* The never-opened card gets a day, then rests a few (unexploredAllowed).
     The day it last showed is kept here, stamped below once it appears. */
  const unexploredDayKey = "bam_unexplored_day";
  const unexploredOk = (() => {
    try { return unexploredAllowed(localStorage.getItem(unexploredDayKey)); } catch { return true; }
  })();

  // A remote film intern's "Start on the Film Desk" card stays on Home until they've opened the Film Desk once.
  const [filmdeskStarted, setFilmdeskStarted] = useState(() => { try { return localStorage.getItem("bam_filmdesk_opened") === "1"; } catch { return false; } });
  useEffect(() => {
    if (nav !== "filmdesk" || filmdeskStarted) return;
    setFilmdeskStarted(true);
    try { localStorage.setItem("bam_filmdesk_opened", "1"); } catch { /* private window */ }
  }, [nav, filmdeskStarted]);

  const nextActions = useMemo(() => {
    const soonest = getAllUpcomingCallsMulti(visibleCalls)[0] || null;
    const tourDone = (() => {
      try { return localStorage.getItem("bam_tour_completed") === "1"; } catch { return false; }
    })();
    const sh = startHereRemaining(startHereState, { favCount: favDrills.length, tourDone });
    const unit = certPurchased && !certIsCertified && certCurrentUnit >= 0
      ? CERT_UNITS[certCurrentUnit] : null;

    const ranked = rankNextActions({
      joinedAt: sbUser?.created_at,
      call: soonest ? {
        id: soonest.id, title: soonest.title,
        startsAt: soonest.dateTime ? new Date(soonest.dateTime).toISOString() : null,
        joinUrl: soonest.meetLink,
      } : null,
      billing: billingProfile ? billingState(billingProfile) : "ok",
      profileIsEmpty,
      startHere: sh.loaded ? { ...sh, total: START_HERE_STEPS.length } : null,
      fresh: freshContent,
      resumable: resumableMasterclasses,
      cert: unit ? {
        purchased: true, certified: certIsCertified, unitTitle: unit.title,
        unitsDone: certCompletedUnits, unitsTotal: CERT_UNITS.length,
        lastAt: certProgress.__last_at || null,
      } : null,
      qa: latestQa && isFreshQa(latestQa, 10) ? {
        id: latestQa.id, title: latestQa.title,
        seen: qaSeen === latestQa.id, publishedAt: latestQa.created_at,
      } : null,
      unexplored: unexploredOk ? unexplored.suggestion : null,
      filmTeam: isRemoteIntern(internAccess?.intern) ? { started: filmdeskStarted } : null,
      drop: latestDrop ? {
        id: latestDrop.id, title: (latestDrop.title || "").trim(), summary: dropCounts(latestDrop.items).join(" · "),
        publishedAt: latestDrop.publish_at, seen: dropSeenId === latestDrop.id, dismissed: dropClosedId === latestDrop.id,
      } : null,
    });
    return visibleActions(ranked, skippedActions);
  }, [visibleCalls, startHereState, favDrills.length, certPurchased, certIsCertified,
      certCurrentUnit, certCompletedUnits, certProgress.__last_at, billingProfile, profileIsEmpty,
      latestQa, qaSeen, unexplored.suggestion, unexploredOk, freshContent,
      sbUser?.created_at, skippedActions, resumableMasterclasses, latestDrop, dropSeenId, dropClosedId,
      internAccess?.intern, filmdeskStarted]);

  // The drop leads the deck (drawn as the drop card) until it's opened or closed.
  const dropAction = nextActions.find(a => a.kind === "drop") || null;

  useEffect(() => {
    if (!nextActions.some(a => a.kind === "unexplored")) return;
    try {
      const today = new Date().toDateString();
      if (localStorage.getItem(unexploredDayKey) !== today) localStorage.setItem(unexploredDayKey, today);
    } catch { /* private window */ }
  }, [nextActions]);

  /* The line under the greeting. Only things that are true today and that
     this page already knows: the date, and the next call if it is this
     week. A call further out is trivia; one tomorrow is a plan. */
  const todayLine = useMemo(() => {
    try { return new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }); }
    catch { return new Date().toDateString(); }
  }, []);
  const nextCallLine = useMemo(() => {
    const c = getAllUpcomingCallsMulti(visibleCalls)[0];
    if (!c || !c.dateTime) return null;
    const at = new Date(c.dateTime);
    if (Number.isNaN(at.getTime())) return null;
    const startOf = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.getTime(); };
    const days = Math.round((startOf(at) - startOf(Date.now())) / 86400000);
    if (days < 0 || days > 6) return null;
    // The device's own clock, always. This used the Settings time zone,
    // while `days` above counts the device's midnights, so a coach whose
    // saved zone wasn't where they were standing got a time from one place
    // and a day from another.
    let time = "";
    try { time = at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); } catch { time = ""; }
    if (days === 0) return time ? `Call today at ${time}` : "Call today";
    if (days === 1) return time ? `Call tomorrow at ${time}` : "Call tomorrow";
    try { return `Next call ${at.toLocaleDateString(undefined, { weekday: "long" })}`; }
    catch { return null; }
  }, [visibleCalls]);

  /* Where a card's button goes. Most are a nav id; the rest are the few
     things that are an action rather than a page. */
  /* Start Here's intro step: the Community composer, open, with the two
     starter lines and the Intro tag. Posting it is what ticks the step. */
  const openIntroComposer = useCallback(() => {
    go("community");
    setCompose(c => (c && c.trim() ? c : INTRO_STARTER));
    setComposeTag(INTRO_TAG);
    setFocused(true);
    // Cursor at the end of "Who I coach: ", once the page has drawn.
    setTimeout(() => {
      const el = composeRef.current;
      if (!el) return;
      try { el.focus({ preventScroll: false }); const at = el.value.indexOf("\n"); el.setSelectionRange(at < 0 ? el.value.length : at, at < 0 ? el.value.length : at); } catch { /* not focusable yet */ }
    }, 350);
  }, [go]);

  /* Any first post ticks the intro step; an Intro tells Coleman (server
     side: it checks the post is theirs and sends one email per member). */
  const afterPost = useCallback(async (created) => {
    if (startHereState && !startHereState.__dismissed && !startHereState.intro) {
      const next = { ...startHereState, intro: new Date().toISOString() };
      setStartHereState(next);
      saveUserData("start_here", next).catch(() => {});
    }
    if (created?.id && created.tag === INTRO_TAG) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          fetch("/api/admin-actions?action=intro-posted", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
            body: JSON.stringify({ postId: created.id }),
          }).catch(() => {});
        }
      } catch { /* the alert is best effort */ }
    }
  }, [startHereState]);

  const doNextAction = useCallback((action) => {
    if (!action) return;
    logEvent("home_next_up_go", { id: action.id, kind: action.kind });
    const to = action.to;
    if (typeof to === "string" && /^https?:/.test(to)) { openExternal(to); return; }
    if (to === "tour") { setTourActive(true); go("dashboard"); return; }
    if (to === "intro") { openIntroComposer(); return; }
    if (to === "profile") { setMyProfileOpen(true); return; }
    if (to === "cert") { switchMode("certified"); return; }
    if (to === "qa") { openLatestQa(); return; }
    if (to === "billing") { go("settings"); return; }
    // A masterclass opens its reader, not the shelf it sits on. The shelf
    // picks this up on arrival (ContentPage, bam_open_module).
    if (action.moduleKey) {
      try { sessionStorage.setItem("bam_open_module", action.moduleKey); } catch { /* storage blocked */ }
    }
    if (action.kind === "unexplored" && unexplored.dismiss) unexplored.dismiss(action.id.replace(/^unexplored:/, ""));
    go(to || "dashboard");
  }, [go, switchMode, unexplored, openIntroComposer]);

  // Which sidebar row lights up for the current route. Normally the one
  // whose id matches, but Team Insights has no row of its own any more: it
  // is the other half of Insights, reached from the toggle on that page, so
  // it lights Insights rather than nothing at all.
  const navIsActive = (id) => nav === id || (id === "insights" && nav === "ti");

  // Sidebar groups (Learn, Coach, Drill Library) start open and remember
  // being folded. Going to a page inside a folded group unfolds it, so the
  // row you're on is never hidden.
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("bam_sidebar_folded") || "{}") || {}; } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem("bam_sidebar_folded", JSON.stringify(collapsed)); } catch { /* private window */ }
  }, [collapsed]);
  const groupIsOpen = (sub) => !collapsed[sub.id];
  const toggleCollapse = (sub) => setCollapsed(p => ({ ...p, [sub.id]: !p[sub.id] }));
  useEffect(() => {
    const g = groupOf(nav);
    if (!g) return;
    setCollapsed(p => { if (p[g] === undefined) return p; const next = { ...p }; delete next[g]; return next; });
  }, [nav]);
  // Routes that are real pages but deliberately absent from the sidebar
  // (the Pro Development collection is reached from a chip on Drills &
  // Games) still need a topbar label and a way back, so they get a
  // synthetic nav entry rather than a blank title bar.
  const OFF_NAV = { pro: { id: "pro", label: sectionLabel("pro"), parent: "pd" },
    "learn-home": { id: "learn-home", label: "Learn", Icon: GraduationCap },
    drop: { id: "drop", label: "This week's drop", Icon: Gift },
    drops: { id: "drops", label: "Past drops", Icon: Gift } };
  // The Pro Development doorway plays on ARRIVAL, not on departure.
  //
  // It used to run on the way out: the chip held you on Drills & Games
  // for 420ms behind a darkening overlay, then swapped the route, then
  // played a half-second page animation. Nearly a second between the
  // click and anything readable, and all of the wait happened before
  // the new page existed - which is the definition of feeling slow
  // rather than feeling cinematic. The overlay also never exited; it
  // was unmounted at full opacity, so the reveal was a hard pop.
  //
  // Now the route changes on the click and this overlay covers the
  // arriving page and lifts off it. Same theatre, no waiting, and the
  // reveal is the animation instead of the absence of one.
  const [proArriving, setProArriving] = useState(false);
  useEffect(() => {
    if (nav !== "pro") { setProArriving(false); return; }
    const reduced = typeof window !== "undefined" && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    setProArriving(true);
    const t = setTimeout(() => setProArriving(false), PRO_TRANSITION_MS[PRO_TRANSITION] ?? 380);
    return () => clearTimeout(t);
  }, [nav, pageKey]);
  const current=NAV.find(n=>n.id===nav) || OFF_NAV[nav];
  const isContentPage = CONTENT[nav] !== undefined;

  if (loading) return (
    <div style={{fontFamily:"'DM Sans',sans-serif",background:"#0A0A0A",height:"100dvh",minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
      <BreathingMark size={72}/>
      <div style={{marginTop:30,fontSize:18,fontWeight:900,letterSpacing:4,color:GOLD,textTransform:"uppercase"}}>YOU'RE IN.</div>
    </div>
  );

  // Branded boot loader (auth, membership checks, the first load). It is
  // BrandLoader now, declared at module scope in components/BrandMark.jsx:
  // declared here, it was a new component type on every render, so the
  // breathing restarted each time loading moved to its next state.
  const BootLoader = BrandLoader;

  // Auth loading
  if(authLoading) return <BootLoader label="Signing you in"/>;

  // Signed in but subscription status unknown — quick loader
  if(sbUser && isSubscribed===null) return <BootLoader label="Verifying membership"/>;

  // Just returned from a successful Stripe checkout but the webhook
  // hasn't flipped stripe_subscription_active yet. The polling effect
  // above is running; show an explicit "activating" loader so the user
  // doesn't see the paywall again and think their payment failed.
  // The poll runs for up to 45s — if it's still false after that the
  // webhook is broken and they should hit support.
  const justCheckedOut = (() => {
    try { return new URLSearchParams(window.location.search).get("checkout") === "success"; }
    catch { return false; }
  })();
  if (sbUser && isSubscribed === false && justCheckedOut) {
    return <BootLoader label="Activating your membership… this can take up to a minute" />;
  }
  // Checkout succeeded but the webhook is slow/stuck. Reassure the paying
  // coach instead of showing the paywall, and give them a clean retry.
  if (sbUser && checkoutStuck && isSubscribed !== true) {
    return (
      <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:"24px",paddingTop:"calc(24px + env(safe-area-inset-top,0px))",background:"#0A0A0A",textAlign:"center"}}>
        <div style={{maxWidth:380}}>
          <div style={{fontSize:40,marginBottom:14}}>✓</div>
          <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:26,letterSpacing:1.5,color:"#E2DD9F",marginBottom:10}}>PAYMENT RECEIVED</div>
 <div style={{fontSize:14,color:"#bbb",lineHeight:1.6,marginBottom:22}}>Your payment went through. We're just finalizing your account: this can take a minute. Tap below to refresh, and if it doesn't clear, email <span style={{color:"#E2DD9F"}}>coleman@byanymeansbball.com</span> and we'll sort it instantly.</div>
          <button onClick={()=>{ setCheckoutStuck(false); window.location.reload(); }}
            style={{padding:"12px 28px",borderRadius:10,background:"#E2DD9F",color:"#111",border:"none",fontFamily:"'Bebas Neue',sans-serif",fontSize:16,letterSpacing:1.4,fontWeight:400,cursor:"pointer"}}>REFRESH</button>
        </div>
      </div>
    );
  }

  // Signed in but NOT subscribed → paywall on web, web-redirect screen
  // on native. App Store rules forbid in-app subscription purchases via
  // Stripe, so we don't render the Paywall component on iOS/Android.
  //
  // The note that used to live here said cert holders never reach this gate,
  // because cert_purchased resolved isSubscribed=true. That stopped being
  // true the day cert_founding split the cohorts: a coach who buys the cert
  // NOW is not founding, so they land here on every platform route, by
  // design. What was not by design is that the web paywall had no link to
  // the certification, so the only door to a thing they owned was the link
  // in their receipt, and that account settings sat behind it, so they could
  // not change their password or delete their account (Apple §5.1.1(v)).
  // See utils/certAccess.js for the rule and the coach who reported it.
  // Advantage Flow on its own (2026-10-24): a coach with an AFS plan and no platform membership lands on AFS, not the paywall.
  const paywalled = needsPaywall({ signedIn: !!sbUser, isSubscribed, hasCert, nav, hasAfs: afsOn && !isNative(), afsCoach: !afsTagger,
    hasFilmDesk: showFilmDeskTile && !isNative() });
  // A tagger with no membership (2026-10-25) opens on the Tagger hub. A coach on a paid plan who opens a platform page
  // (2026-10-28) gets the paywall's $19.99 offer; from the home page, or on a free trial, they land on Advantage Flow.
  const afsOffer = paywalled && afsOn && afsPaid && !afsTagger && !isNative() && !["", "dashboard", "platform", "home"].includes(String(nav || ""));
  if (paywalled && afsOn && !isNative() && !afsOffer) {
    const home = afsTagger ? "#/afs/tagger" : "#/afs";
    if (typeof window !== "undefined" && window.location.hash !== home) window.location.hash = home;
    return <BootLoader label="Opening Advantage Flow…" />;
  }
  // A Film Desk contributor on a free account (2026-11-02) opens on the Film Desk, not the paywall. Web only.
  if (paywalled && showFilmDeskTile && !isNative() && !afsOn) {
    if (typeof window !== "undefined" && window.location.hash !== "#/filmdesk") window.location.hash = "#/filmdesk";
    return <BootLoader label="Opening the Film Desk…" />;
  }
  // A book owner (2026-10-26): their resources, or the form they were filling in before confirming their email.
  if (paywalled && !isNative() && blueprintOwner === null && isSubscribed === false) return <BootLoader label="Verifying membership" />;
  // ...unless they asked to see the platform from their resources (the upsell): then the plans, as for anyone.
  const showPlans = (() => { try { return sessionStorage.getItem("bam_show_plans") === "1"; } catch { return false; } })();
  if (paywalled && !isNative() && !showPlans && (blueprintOwner || pendingClaim())) {
    window.location.hash = blueprintOwner ? "#/blueprint/resources" : "#/blueprint";
    return <BootLoader label="Opening your Blueprint resources…" />;
  }
  // A staff invite they haven't finished (2026-11-03): back to it, and on to their team's Advantage Flow.
  if (paywalled && !isNative() && pendingStaffInvite()) {
    window.location.hash = `#/afs-start?staff=${pendingStaffInvite()}`;
    return <BootLoader label="Opening your invite…" />;
  }
  // A tagger's invite they haven't finished (2026-10-25): back to it, never the coaches' paywall.
  if (paywalled && !isNative() && pendingTaggerInvite()) {
    window.location.hash = `#/afs-start?tagger=${pendingTaggerInvite()}`;
    return <BootLoader label="Opening your invite…" />;
  }
  // A free-trial invite they haven't finished (2026-10-25): back to it, not the paywall.
  if (paywalled && !isNative() && pendingInvite()) {
    window.location.hash = `#/afs-start?invite=${pendingInvite()}`;
    return <BootLoader label="Opening your invite…" />;
  }
  // An Advantage Flow plan they picked before confirming their email (2026-11-03): back to its checkout, not the paywall.
  if (paywalled && !isNative() && pendingPurchaseHash()) {
    window.location.hash = pendingPurchaseHash();
    return <BootLoader label="Opening your checkout…" />;
  }
  if(paywalled) {
    if (isNative()) {
      // A cert owner with no platform sub still owns the cert. Send them to
      // it from anywhere they may not be, and leave them alone anywhere they
      // may: this used to redirect unconditionally, which bounced them back
      // to #/certified on every tap and sealed off their own settings.
      if (hasCert) {
        const to = certOwnerRedirect(nav);
        if (to && typeof window !== "undefined" && window.location.hash !== to) {
          window.location.hash = to;
        }
        return <BootLoader label="Opening your certification…" />;
      }
      return <NativeSubscriptionGate userEmail={sbUser.email} onSignOut={handleSignOut} onSubscribed={()=>setIsSubscribed(true)}/>;
    }
    return (
      <Paywall
        userEmail={sbUser.email}
        userId={sbUser.id}
        onSignOut={handleSignOut}
        hasCert={hasCert}
        onOpenCert={()=>{ window.location.hash = "#/certified"; }}
        afsOffer={afsOffer}
      />
    );
  }

  // Subscribed but profile is incomplete → show ProfileSetup overlay
  // (collected AFTER payment to minimize pre-payment friction).
  //
  // Forced for ALL incomplete non-admin users — including legacy ones
  // whose accounts pre-date the requirement. Previously we bypassed
  // the modal for accounts >48h old to avoid trapping existing users
  // on a transient WKWebView profile-fetch glitch. That bypass also
  // let partially-onboarded users (first_name set but missing role /
  // location) wander the platform and post as anonymous-looking, so
  // we removed it. Transient profile-fetch errors are still handled
  // — the fetch above bails early on `error`, leaving profileIsEmpty
  // at its initial false, so a network blip doesn't trigger the modal.
  //
  // Admins bypass the gate so they can test, sign in to throwaway
  // accounts, and inspect partially-completed user states without
  // being modal'd into onboarding every time. Real incomplete coaches
  // (which is what this gate is for) never have is_admin set.
  //
  // ProfileSetup itself has an `escapeHatch` (sign out + reload) for
  // anyone genuinely stuck in a wrong-account / weird-auth state.
  const showProfileSetup = sbUser && isSubscribed === true && profileIsEmpty && !profileSetupDone && !isAdmin;

  if (showProfileSetup) {
    return (
      <ProfileSetup
        sbUser={sbUser}
        onComplete={(profileData) => {
          setProfileSetupDone(true);
          setProfileIsEmpty(false);
          dismissBanner("profile-setup-done");
          dismissBanner("complete-profile");
          if (profileData.name) {
            setAppProfile((prev) => ({
              ...prev,
              name: profileData.name,
              role: profileData.role || prev.role,
              location: profileData.location || prev.location,
            }));
          }
          // Flag so we launch the tour after ProfileSetup flashes to dashboard
          try { localStorage.setItem("bam_should_start_tour", "1"); } catch {}
          // Welcome email is normally fired from supabase/functions/stripe-webhook
          // on checkout.session.completed (so it lands AFTER the user is actually
          // paying). For users who skip the paywall — admin-granted access,
          // manually-flipped stripe_subscription_active flag, comp accounts,
          // anyone whose checkout webhook never fires — we still need to fire
          // the welcome here. The `isSubscribed` guard means standard signups
          // (who hit the paywall, then Stripe, then webhook) don't get a
          // duplicate email from this path.
          if (isSubscribed && sbUser?.id) {
            supabase.functions.invoke("send-email", {
              body: { kind: "welcome", user_id: sbUser.id },
            }).catch((e) => console.warn("welcome email failed:", e));
          }
        }}
      />
    );
  }

  // Not authenticated — redirect to landing page unless they explicitly
  // want to sign up or sign in.
  //
  // EXCEPTION: when running inside the Capacitor iOS/Android shell, App
  // Store rules ban us from showing pricing or external checkout (the
  // landing page = a hard rejection). So on native we skip landing
  // entirely and open the sign-in screen straight away. Existing coaches
  // sign in; new sign-ups happen on the web at platform.byanymeanscoaches.com.
  if(!sbUser&&!onboarded){
    const currentHash = window.location.hash;
    const wantsSignup = currentHash === "#/signup";
    // Invite-only surfaces (an onboarding link we send an intern): a signed-out
    // visitor here is someone we invited, not a marketing lead, so send them to
    // sign-in, which restores this stashed destination after login, rather than
    // the marketing/paywall landing.
    const INVITE_ONLY = /^#\/(filmdesk|interns)(?:$|[/?])/;
    const wantsSignin = currentHash === "#/signin" || showSignIn || isNative() || INVITE_ONLY.test(currentHash);
    // Deep-link survival: if the user landed on a real platform route
    // (e.g. `#/platform/community`) without a session, stash the hash so we
    // can restore it after sign-in. Skip routes that are themselves entry
    // points (landing/signin/signup) so we don't loop. Read with a fallback
    // to `#/platform` in onSuccess below.
    const SIGNIN_REENTRY = new Set(["", "#", "#/", "#/landing", "#/signin", "#/signup"]);
    if (!SIGNIN_REENTRY.has(currentHash) && !sessionStorage.getItem("bam_post_signin_hash")) {
      try { sessionStorage.setItem("bam_post_signin_hash", currentHash); } catch {}
    }
    const restoreAfterSignin = (user)=>{
      setSbUser(user); setOnboarded(true);
      let next = "#/platform";
      try {
        const stashed = sessionStorage.getItem("bam_post_signin_hash");
        if (stashed) { next = stashed; sessionStorage.removeItem("bam_post_signin_hash"); }
      } catch {}
      window.location.hash = next;
    };
    if (!wantsSignup && !wantsSignin) {
      // Redirect to the landing page — App.js will render <LandingPage /> for #/landing
      window.location.hash = "#/landing";
      return <BootLoader label="Loading your dashboard…"/>;
    }
    if (wantsSignin) return (
      <SignInScreen
        // On native there is no landing page to go back to — hide the
        // back affordance by passing null. SignInScreen renders nothing
        // for a missing onBack handler.
        onBack={isNative() ? null : (()=>{setShowSignIn(false);window.location.hash="#/landing";})}
        onSuccess={restoreAfterSignin}
 notice={sessionExpired ? "Your session expired: please sign in again." : undefined}
      />
    );
    // Quick signup — email + password only, then paywall
    return (
      <QuickSignup
        onSuccess={restoreAfterSignin}
        onSignInClick={()=>setShowSignIn(true)}
      />
    );
  }

  // ── Admin full-screen takeover ───────────────────────────────────────
  // Every `admin/*` route renders WITHOUT the platform chrome (top bar
  // search, primary sidebar, mode toggles). The redesigned admin shell
  // brings its own sidebar + topbar via AdminLayout.
  //
  // Admin is ALWAYS dark, regardless of the platform's light/dark
  // setting. Mixing the platform's LIGHT theme tokens with the black
  // admin canvas produced near-white card backgrounds (the legacy
  // admin had the same constraint).
  //
  // Constraint Library — self-contained router that renders its own full-page
  // layout for both the coach-facing browse/capture routes and the admin
  // review queue. Mount it before the admin early-return so admin/constraints
  // reaches the router (the admin early-return dispatches only on the fixed
  // ADMIN_SECTION_ROOTS set below).
  //
  // Behind the `constraints` flag while it's parked. The routes still resolve
  // for the allowlist so the mining cron keeps having somewhere to land and
  // the work isn't lost — a coach who deep-links in just falls through to the
  // normal shell rather than hitting a dead page.
  if (nav && constraintsOn
      && (nav === "library/constraints" || nav.startsWith("library/constraints/")
      || nav === "admin/constraints" || nav.startsWith("admin/constraints/"))) {
    return (
      <Suspense fallback={<LazyFallback/>}>
        <ConstraintRouter/>
      </Suspense>
    );
  }

  // We mount UserAdminPanel here so the jump-to-user side panel works
  // from admin pages.
  if (nav && nav.startsWith("admin/")) {
    const adminC = DARK;
    const adminDark = true;
    // Route to the right section component based on the prefix. Each
    // section internally resolves its active tab from the rest of the
    // path (e.g. admin/content/uploads → ContentSection tab=uploads).
    const inHome  = nav === "admin/home"  || nav.startsWith("admin/home/");
    const inWorkspace = nav === "admin/workspace" || nav.startsWith("admin/workspace/");
    const inContent = nav === "admin/content" || nav.startsWith("admin/content/");
    const inPeople  = nav === "admin/people"  || nav.startsWith("admin/people/");
    const inMoney   = nav === "admin/money"   || nav.startsWith("admin/money/");
    const inCertAi  = nav === "admin/cert-ai" || nav.startsWith("admin/cert-ai/");
    const inIntel   = nav === "admin/intelligence" || nav.startsWith("admin/intelligence/");
    const inAfs     = nav === "admin/afs" || nav.startsWith("admin/afs/");
    const inOutreach = nav === "admin/outreach" || nav.startsWith("admin/outreach/");
    // Pick which section label to label the error boundary with
    // (so the fallback UI says "Content tab errored" not "unknown").
    const sectionLabel = inHome ? "Home" : inWorkspace ? "Workspace"
      : inContent ? "Content"
      : inPeople ? "People" : inMoney ? "Money"
      : inCertAi ? "Cert & AI" : inIntel ? "Intelligence" : inAfs ? "Advantage Flow"
      : inOutreach ? "Outreach"
      : "Admin";
    return (
      <div style={{fontFamily:"'DM Sans',sans-serif",height:"var(--app-height,100vh)",width:"100vw",display:"flex",overflow:"hidden",background:"#050505",color:"#F5F5F5"}}>
        <AdminErrorBoundary label={sectionLabel}>
          <Suspense fallback={<LazyFallback/>}>
            {inHome      && <HomeSection      C={adminC} dark={adminDark} go={go} route={nav}/>}
            {inWorkspace && <WorkspaceSection C={adminC} dark={adminDark} go={go} route={nav}/>}
            {inContent   && <ContentSection   C={adminC} dark={adminDark} go={go} route={nav}/>}
            {inPeople    && <PeopleSection    C={adminC} dark={adminDark} go={go} route={nav}/>}
            {inMoney     && <MoneySection     C={adminC} dark={adminDark} go={go} route={nav}/>}
            {inCertAi    && <CertAiSection    C={adminC} dark={adminDark} go={go} route={nav}/>}
            {inIntel     && <IntelligencePage C={adminC} dark={adminDark} go={go}/>}
            {inAfs       && <AfsSection       C={adminC} dark={adminDark} go={go} route={nav} me={sbUser?.id || null}/>}
            {inOutreach  && <OutreachSection  C={adminC} dark={adminDark} go={go} route={nav}/>}
          </Suspense>
        </AdminErrorBoundary>
        <UserAdminPanel C={adminC} dark={adminDark}/>
      </div>
    );
  }

  return (
    <BamIQProvider enabled={bamiqOn}>
    <BamIQShortcut onIqPage={nav==="iq"}/>
    {/* --bamiq-push: BAM IQ's panel makes room beside the page on a wide screen (BamIQPanel). */}
    <div style={{fontFamily:"'DM Sans',sans-serif",background:C.bg,height:"var(--app-height,100vh)",display:"flex",color:C.text,transition:"background .25s ease,color .25s ease,padding-right var(--bamiq-push-t,.28s) cubic-bezier(.2,.8,.2,1)",overflow:"hidden",paddingRight:"var(--bamiq-push,0px)",boxSizing:"border-box"}}>


      {/* Prompt 8: offline banner */}
      {isOffline&&(
        <div style={{position:"fixed",top:0,left:0,right:0,zIndex:10000,background:GOLD,color:"#111",padding:"8px 0",paddingTop:"calc(env(safe-area-inset-top, 0px) + 8px)",textAlign:"center",fontSize:13,fontWeight:700,letterSpacing:.5,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round"><line x1="1" y1="1" x2="23" y2="23"/><path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55"/><path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39"/><path d="M10.71 5.05A16 16 0 0 1 22.56 9"/><path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>
 You're offline: some features may be unavailable
        </div>
      )}
      {backOnline&&!isOffline&&(
        <div style={{position:"fixed",top:0,left:0,right:0,zIndex:10000,background:"#5AB584",color:"#fff",padding:"8px 0",paddingTop:"calc(env(safe-area-inset-top, 0px) + 8px)",textAlign:"center",fontSize:13,fontWeight:700,letterSpacing:.5,animation:"bamFadeOut 3s ease forwards"}}>
          Back online
        </div>
      )}
      {showWelcomeBanner&&(
        <div style={{position:"fixed",top:"calc(env(safe-area-inset-top, 0px) + 16px)",left:"50%",transform:"translateX(-50%)",zIndex:10000,
          background:"linear-gradient(180deg,#171717,#111111)",border:`1px solid ${GOLD}60`,borderRadius:14,
          padding:"14px 24px",display:"flex",alignItems:"center",gap:12,
          boxShadow:"0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(226,221,159,0.1)",
          backdropFilter:"blur(12px)",animation:"slideD .4s ease",maxWidth:"min(520px, 92vw)"}}>
          <span style={{fontSize:20}}>👋</span>
          <div style={{flex:1}}>
            <div style={{fontSize:14,fontWeight:800,color:GOLD,marginBottom:2}}>Welcome to BAM Coaches!</div>
            <div style={{fontSize:12,color:"#9A9488"}}>Complete your profile to get the most out of the platform.</div>
          </div>
          <span onClick={()=>setShowWelcomeBanner(false)} className="btn" style={{cursor:"pointer",color:"#666",fontSize:18,lineHeight:1,padding:4}}>×</span>
        </div>
      )}

      {/* Prompt 7: API error toast */}
      {apiError&&(
        <div style={{position:"fixed",bottom:"calc(env(safe-area-inset-bottom, 0px) + 88px)",right:24,zIndex:10001,background:"linear-gradient(180deg,#171717,#111111)",border:`1px solid #E0606060`,borderRadius:14,padding:"14px 20px",fontSize:13,color:"#E06060",display:"flex",alignItems:"center",gap:10,boxShadow:"0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)",animation:"slideD .3s ease",maxWidth:360,backdropFilter:"blur(12px)"}}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#E06060" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
          <span style={{flex:1}}>{apiError}</span>
          <span onClick={()=>setApiError(null)} className="btn" role="button" style={{cursor:"pointer",color:"#999",fontSize:16,lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center",padding:4}}>×</span>
        </div>
      )}

      {notif&&notifOn&&(
        <div style={{position:"fixed",top:"calc(env(safe-area-inset-top, 0px) + 14px)",left:"50%",zIndex:9999,background:"linear-gradient(180deg,#171717,#111111)",border:`1px solid ${GOLD}50`,borderRadius:12,padding:"12px 22px",fontSize:14,color:SBtext,display:"flex",alignItems:"center",gap:10,boxShadow:"0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)",animation:"slideD .3s ease",whiteSpace:"nowrap",backdropFilter:"blur(12px)"}}>
          <div style={{width:7,height:7,borderRadius:"50%",background:GOLD}}/>
          {notif}
          <span className="btn" onClick={()=>setNotif(null)} style={{color:SBdim,marginLeft:6,fontSize:18,lineHeight:1}}>×</span>
        </div>
      )}

      {/* Call notification banner */}
      {/* The reminder used to appear only inside the last thirty minutes, so a
          coach opening the app at 5pm for a 7pm call saw nothing at all. The
          `callBanner` state has always been computed over a 24 hour window;
          only this render gate was narrow. Now it shows for the whole run-up
          and gets loud in the last hour, which is where "in 4h 12m" stops
          being trivia and starts being a plan. */}
      {callBanner&&callBanner.diff<=86400000&&settingsNotifs.callReminder&&(
        <div className="bam-call-overlay" style={{position:"fixed",top:notif&&notifOn?"calc(env(safe-area-inset-top, 0px) + 52px)":"calc(env(safe-area-inset-top, 0px) + 14px)",left:"50%",transform:"translateX(-50%)",maxWidth:"calc(100vw - 24px)",zIndex:9998,background:"linear-gradient(135deg,#171717,#0E0E0E)",border:`1px solid ${callBanner.diff<=3600000?GOLD+"60":"rgba(255,255,255,0.10)"}`,borderRadius:14,padding:"10px 20px",fontSize:13,color:SBtext,display:"flex",alignItems:"center",gap:12,boxShadow:`0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)`,animation:"slideD .3s ease",whiteSpace:"nowrap",backdropFilter:"blur(12px)"}}>
          <Video size={15} color={GOLD}/>
          <span style={{fontWeight:700,color:callBanner.diff<=3600000?GOLD:SBtext}}>{callBanner.title}</span>
          {callBanner.diff>0
            ?<span style={{color:SBmid}}>in {fmtCountdown(callBanner.diff)}</span>
            :<span style={{fontSize:11,fontWeight:800,color:"#fff",background:"#E06060",padding:"3px 10px",borderRadius:20,display:"inline-flex",alignItems:"center",gap:5,animation:"livePulse 1.5s ease-in-out infinite"}}><span style={{width:6,height:6,borderRadius:"50%",background:"#fff",display:"inline-block"}}/>LIVE NOW</span>
          }
          <a href={callBanner.meetLink} target="_blank" rel="noopener noreferrer" onClick={(e)=>{e.preventDefault();openExternal(callBanner.meetLink);}}
            style={{background:GOLD,color:"#111",padding:"5px 14px",borderRadius:6,fontSize:12,fontWeight:700,textDecoration:"none",fontFamily:"'DM Sans',sans-serif"}}>
            Join
          </a>
          <button onClick={()=>dismissCall(callBanner)} aria-label="Dismiss call reminder" className="btn"
            style={{background:"transparent",border:"none",color:SBdim,fontSize:18,lineHeight:1,marginLeft:4,padding:"4px 8px",cursor:"pointer",borderRadius:6}}
            onMouseEnter={(e)=>{e.currentTarget.style.background="rgba(255,255,255,0.06)";e.currentTarget.style.color=SBtext;}}
            onMouseLeave={(e)=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color=SBdim;}}>
            ×
          </button>
        </div>
      )}

      {/* MOBILE SIDEBAR OVERLAY */}
      {mobileMenuOpen&&<div onClick={()=>setMobileMenuOpen(false)} style={{position:"fixed",inset:0,zIndex:9990,background:`rgba(0,0,0,${0.55 * Math.max(0, 1 + drawerOffset / 260)})`,display:"none",transition: drawerSwipeRef.current.active ? "none" : "background .25s ease"}} className="bam-sidebar-mobile"/>}
      {mobileMenuOpen&&(
        <div className="bam-sidebar-mobile" onTouchStart={onDrawerTouchStart} onTouchMove={onDrawerTouchMove} onTouchEnd={onDrawerTouchEnd} onTouchCancel={onDrawerTouchEnd} style={{position:"fixed",top:0,left:0,bottom:0,width:260,zIndex:9991,background:"#0A0A0A",display:"none",flexDirection:"column",animation: drawerOffset===0 ? "bamSlideIn .2s ease" : undefined,transform:`translateX(${drawerOffset}px)`,transition: drawerSwipeRef.current.active ? "none" : "transform .22s cubic-bezier(.2,.8,.2,1)",boxShadow:"4px 0 32px rgba(0,0,0,0.6)",paddingTop:"env(safe-area-inset-top, 0px)",paddingBottom:"env(safe-area-inset-bottom, 0px)",touchAction:"pan-y"}}>
          <div style={{padding:"16px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:`1px solid ${SBborder}`}}>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:20,fontWeight:400,color:GOLD,letterSpacing:2}}>BY ANY MEANS</div>
            <span onClick={()=>setMobileMenuOpen(false)} style={{cursor:"pointer",color:SBdim,fontSize:22,lineHeight:1}}>×</span>
          </div>
          {/* Platform ↔ Certification mode toggle — mirrors the desktop sidebar
              so mobile users can reach the certification track. */}
          <div style={{padding:"10px 14px 4px",flexShrink:0}}>
            <div style={{display:"flex",background:"rgba(255,255,255,0.04)",borderRadius:8,padding:3,position:"relative"}}>
              <div style={{position:"absolute",top:3,left:3,width:"calc(50% - 3px)",height:"calc(100% - 6px)",borderRadius:6,
                background:appMode==="certified"?GOLD:"rgba(226,221,159,0.12)",transition:"transform .2s ease, background .2s ease",
                transform:appMode==="certified"?"translateX(100%)":"translateX(0)"}}/>
              <div onClick={()=>switchMode("platform")}
                style={{flex:1,textAlign:"center",padding:"8px 0",fontSize:11,fontWeight:800,letterSpacing:.5,
                  color:appMode==="platform"?GOLD:"#666",cursor:"pointer",position:"relative",zIndex:1}}>Platform</div>
              <div onClick={()=>{if(!certAccess){go("certified");}else{switchMode("certified");}}}
                style={{flex:1,textAlign:"center",padding:"8px 0",fontSize:11,fontWeight:800,letterSpacing:.5,
                  color:appMode==="certified"?"#111":"#666",cursor:"pointer",position:"relative",zIndex:1,
                  display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
                Certification{!certAccess&&<Lock size={9} color="#666"/>}
              </div>
            </div>
          </div>
          <div className="bam-sidenav-scroll" style={{flex:1,padding:"10px 0",overflowY:"auto"}}>
            {appMode==="platform"&&NAV_SECTIONS.map((section,si)=>(
              <div key={si} style={{marginBottom:2}}>
                {section.label&&<div onClick={section.label==="Toolbox"?()=>go("toolbox"):undefined} style={{fontSize:10,color:section.label==="Toolbox"&&nav==="toolbox"?GOLD:SBdim,letterSpacing:1.5,padding:"10px 18px 3px",fontWeight:700,textTransform:"uppercase",cursor:section.label==="Toolbox"?"pointer":"default"}}>{section.label}</div>}
                {section.items.map(item=>{const active=navIsActive(item.id);const disabled=item.comingSoon;return(
                  <div key={item.id} role="button" onClick={disabled?undefined:()=>go(item.id)} style={{minHeight:44,boxSizing:"border-box",padding:"9px 18px",fontSize:13,color:disabled?"#444":(active?SBtext:SBmid),fontWeight:active?700:500,background:active?"rgba(226,221,159,0.1)":"transparent",borderLeft:active?`3px solid ${GOLD}`:"3px solid transparent",display:"flex",alignItems:"center",gap:11,cursor:disabled?"default":"pointer",opacity:disabled?0.5:1}}>
                    <item.Icon size={16} color={disabled?"#333":(active?GOLD:GOLD+"59")} strokeWidth={active?2.2:1.8}/><span style={{flex:1}}>{item.label}</span>
                    {item.id==="chats" && chatUnread>0 && <span style={{fontSize:10,fontWeight:800,color:"#0A0A0A",background:GOLD,minWidth:18,height:18,borderRadius:9,padding:"0 5px",display:"flex",alignItems:"center",justifyContent:"center"}}>{chatUnread>9?"9+":chatUnread}</span>}
                    <NewDot id={item.id}/>
                    {item.pro && !disabled && <span style={{fontSize:8,fontWeight:800,color:GOLD,background:`${GOLD}15`,padding:"2px 6px",borderRadius:4,letterSpacing:.5}}>PRO</span>}
                    {disabled && <span style={{fontSize:7,fontWeight:800,color:"#555",background:"rgba(255,255,255,0.04)",padding:"2px 6px",borderRadius:4,letterSpacing:.8}}>SOON</span>}
                  </div>);})}
                {section.subs&&section.subs.map(sub=>{const isOpen=groupIsOpen(sub);const subActive=sub.home===nav||sub.items.some(i=>i.id===nav);return(
                  <div key={sub.id}>
                    <div role="button" aria-expanded={isOpen} onClick={()=>sub.home?go(sub.home):toggleCollapse(sub)} style={{minHeight:44,boxSizing:"border-box",padding:"9px 18px",fontSize:13,color:sub.home===nav?SBtext:SBmid,fontWeight:sub.home===nav?700:500,background:sub.home===nav?"rgba(226,221,159,0.1)":"transparent",display:"flex",alignItems:"center",gap:11,cursor:"pointer",borderLeft:sub.home===nav?`3px solid ${GOLD}`:"3px solid transparent"}}>
                      <sub.Icon size={16} color={subActive?GOLD:GOLD+"59"} strokeWidth={subActive?2.2:1.8}/>
                      <span style={{flex:1}}>{sub.label}</span>
                      <span role="button" aria-label={isOpen?`Fold ${sub.label}`:`Unfold ${sub.label}`} onClick={(e)=>{ if(!sub.home) return; e.stopPropagation(); toggleCollapse(sub); }} style={{display:"flex",alignItems:"center",justifyContent:"center",width:32,height:32,margin:"-6px -8px -6px 0"}}><span style={{fontSize:12,color:SBdim,display:"inline-block",transform:isOpen?"rotate(90deg)":"rotate(0deg)",transition:"transform .2s"}}>›</span></span>
                    </div>
                    {isOpen&&sub.items.map(item=>{const active=navIsActive(item.id);const disabled=item.comingSoon;return(
                      <div key={item.id} role="button" onClick={disabled?undefined:()=>go(item.id)} style={{minHeight:40,boxSizing:"border-box",padding:"7px 18px 7px 44px",fontSize:12.5,color:disabled?"#444":(active?SBtext:SBmid),fontWeight:active?700:500,background:active?"rgba(226,221,159,0.08)":"transparent",borderLeft:active?`3px solid ${GOLD}`:"3px solid transparent",display:"flex",alignItems:"center",gap:10,cursor:disabled?"default":"pointer",opacity:disabled?0.5:1}}>
                        <item.Icon size={14} color={disabled?"#333":(active?GOLD:GOLD+"50")} strokeWidth={active?2.2:1.8}/><span style={{flex:1}}>{item.label}</span>
                        <NewDot id={item.id}/>
                        {item.pro && !disabled && <span style={{fontSize:8,fontWeight:800,color:GOLD,background:`${GOLD}15`,padding:"2px 6px",borderRadius:4,letterSpacing:.5}}>PRO</span>}
                        {disabled && <span style={{fontSize:7,fontWeight:800,color:"#555",background:"rgba(255,255,255,0.04)",padding:"2px 6px",borderRadius:4,letterSpacing:.8}}>SOON</span>}
                      </div>);})}
                  </div>);})}
                {section.standalone&&section.standalone.map(item=>{const active=navIsActive(item.id);const disabled=item.comingSoon;return(
                  <div key={item.id} role="button" onClick={disabled?undefined:()=>go(item.id)} style={{minHeight:44,boxSizing:"border-box",padding:"9px 18px",fontSize:13,color:disabled?"#444":(active?SBtext:SBmid),fontWeight:active?700:500,background:active?"rgba(226,221,159,0.1)":"transparent",borderLeft:active?`3px solid ${GOLD}`:"3px solid transparent",display:"flex",alignItems:"center",gap:11,cursor:disabled?"default":"pointer",opacity:disabled?0.5:1}}>
                    <item.Icon size={16} color={disabled?"#333":(active?GOLD:GOLD+"59")} strokeWidth={active?2.2:1.8}/><span style={{flex:1}}>{item.label}</span>
                    {item.id==="chats" && chatUnread>0 && <span style={{fontSize:10,fontWeight:800,color:"#0A0A0A",background:GOLD,minWidth:18,height:18,borderRadius:9,padding:"0 5px",display:"flex",alignItems:"center",justifyContent:"center"}}>{chatUnread>9?"9+":chatUnread}</span>}
                    <NewDot id={item.id}/>
                    {item.pro && !disabled && <span style={{fontSize:8,fontWeight:800,color:GOLD,background:`${GOLD}15`,padding:"2px 6px",borderRadius:4,letterSpacing:.5}}>PRO</span>}
                    {disabled && <span style={{fontSize:7,fontWeight:800,color:"#555",background:"rgba(255,255,255,0.04)",padding:"2px 6px",borderRadius:4,letterSpacing:.8}}>SOON</span>}
                  </div>);})}
              </div>
            ))}
            {appMode==="platform"&&(
              <SidebarLive nav={nav} go={go} callLine={nextCallLine} chatUnread={chatUnread} showChats={hasChats}
                org={tileOrg} onOrg={openMyOrg} showInterns={showInternTile}
                style={{margin:"10px 12px 4px"}}/>
            )}
            {appMode==="platform"&&(
              <div style={{marginTop:6,paddingTop:6,borderTop:`1px solid ${SBborder}`}}>
                <div style={{fontSize:10,color:SBdim,letterSpacing:1.5,padding:"10px 18px 3px",fontWeight:700,textTransform:"uppercase"}}>You</div>
                {PROFILE_MENU_ROWS.map(r=>{const active=nav===r.id;return(
                  <div key={r.id} role="button" onClick={()=>pickProfileMenu(r.id)} style={{minHeight:44,boxSizing:"border-box",padding:"9px 18px",fontSize:13,color:r.accent?GOLD:(active?SBtext:SBmid),fontWeight:active?700:500,background:active?"rgba(226,221,159,0.1)":"transparent",borderLeft:active?`3px solid ${GOLD}`:"3px solid transparent",display:"flex",alignItems:"center",gap:11,cursor:"pointer"}}>
                    <r.Icon size={16} color={r.accent||active?GOLD:GOLD+"59"} strokeWidth={active?2.2:1.8}/><span style={{flex:1}}>{r.label}</span>
                  </div>);})}
              </div>
            )}
            {appMode==="certified"&&(
              <div>
                <div style={{padding:"6px 20px 8px"}}>
                  <div style={{fontSize:10,color:GOLD,letterSpacing:2,fontWeight:700,textTransform:"uppercase"}}>BAM Certification</div>
                </div>
                <div style={{position:"relative"}}>
                  <div style={{position:"absolute",left:27,top:0,bottom:0,width:2,zIndex:0}}>
                    <div style={{width:2,background:GOLD,height:`${Math.max(0,((certCurrentUnit>=0?certCurrentUnit:9)/9)*100)}%`,borderRadius:1,transition:"height .3s ease"}}/>
                    <div style={{width:2,background:"rgba(255,255,255,0.08)",flex:1,borderRadius:1}}/>
                  </div>
                  {CERT_UNITS.map((unit,i)=>{
                    const unlocked=certIsUnitUnlocked(i);
                    const done=certIsUnitComplete(unit)&&certIsQuizPassed(unit.id);
                    const isCurrent=certCurrentUnit===i;
                    const isActiveRoute=isCertRoute(nav)&&nav.includes(`unit/${unit.id}`);
                    const lessonsTotal=unit.lessons.filter(l=>l.type!=="quiz").length;
                    const lessonsDone=unit.lessons.filter(l=>l.type!=="quiz"&&certIsLessonComplete(l.id)).length;
                    return(
                      <div key={unit.id} onClick={()=>{if(unlocked) go(`certified/unit/${unit.id}`);}}
                        style={{padding:"8px 20px 8px 40px",margin:"1px 8px",borderRadius:8,fontSize:12.5,
                          color:done?"#F5F5F5":(isCurrent?"#F5F5F5":(unlocked?"#888":"#444")),fontWeight:isCurrent||isActiveRoute?600:500,
                          display:"flex",alignItems:"center",gap:9,cursor:unlocked?"pointer":"not-allowed",opacity:unlocked?1:.5,position:"relative",
                          background:isCurrent||isActiveRoute?"rgba(226,221,159,0.08)":"transparent"}}>
                        <div style={{position:"absolute",left:22,width:12,height:12,borderRadius:"50%",zIndex:1,background:"#0A0A0A",
                          border:`2px solid ${done?GOLD:(isCurrent?GOLD:"rgba(255,255,255,0.15)")}`,
                          display:"flex",alignItems:"center",justifyContent:"center",boxShadow:done?`0 0 8px ${GOLD}40`:"none"}}>
                          <CompleteCheck on={done} size={7} color="#111" strokeWidth={4}/>
                          {isCurrent&&!done&&<div style={{width:4,height:4,borderRadius:"50%",background:GOLD}}/>}
                        </div>
                        <span style={{fontSize:10,fontWeight:700,color:done?GOLD+"99":(isCurrent?GOLD+"99":"#555"),marginRight:2}}>{i+1}.</span>
                        <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{unit.title}</span>
                        {unlocked&&lessonsTotal>0&&(
                          <span style={{fontSize:9,fontWeight:700,color:done?GOLD:(lessonsDone>0?GOLD+"AA":"#555"),
                            background:done?`${GOLD}20`:(lessonsDone>0?`${GOLD}10`:"rgba(255,255,255,0.04)"),padding:"2px 6px",borderRadius:5,flexShrink:0}}>{lessonsDone}/{lessonsTotal}</span>
                        )}
                      </div>
                    );
                  })}
                  <div style={{height:1,background:"rgba(255,255,255,0.05)",margin:"8px 20px 6px"}}/>
                  {(()=>{
                    const allDone=CERT_UNITS.every((u)=>certIsUnitComplete(u)&&certIsQuizPassed(u.id));
                    const examActive=nav==="certified/exam"||nav==="certified/certificate";
                    return(
                      <div onClick={()=>{if(allDone&&!certIsCertified) go("certified/exam"); else if(certIsCertified) go("certified/certificate");}}
                        style={{padding:"8px 20px 8px 40px",margin:"1px 8px",borderRadius:8,fontSize:12.5,
                          color:allDone||certIsCertified?"#F5F5F5":"#444",fontWeight:examActive?600:500,
                          display:"flex",alignItems:"center",gap:9,cursor:allDone||certIsCertified?"pointer":"not-allowed",opacity:allDone||certIsCertified?1:.5,position:"relative",
                          background:examActive?"rgba(226,221,159,0.08)":"transparent"}}>
                        <div style={{position:"absolute",left:22,width:12,height:12,display:"flex",alignItems:"center",justifyContent:"center"}}>
                          {certIsCertified||allDone?<Shield size={12} color={GOLD}/>:<Lock size={10} color="#555"/>}
                        </div>
                        Final Exam
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* SIDEBAR */}
      <div className="bam-sidebar" style={{width:240,background:appMode==="certified"?"#070707":"#080808",borderRight:`1px solid rgba(255,255,255,0.04)`,display:"flex",flexDirection:"column",flexShrink:0,position:"relative",transition:"background .3s ease"}}>
        <div style={{position:"absolute",top:0,left:0,right:0,height:1,background:`linear-gradient(90deg,${appMode==="certified"?GOLD:GOLD+"66"},${GOLD}11,transparent)`}}/>
        <div style={{padding:"22px 20px 14px",borderBottom:`1px solid rgba(255,255,255,0.05)`,display:"flex",alignItems:"center",gap:12,position:"relative",overflow:"hidden"}}>
          <div style={{position:"absolute",top:-40,left:-20,width:160,height:100,background:GOLD,borderRadius:"50%",opacity:.035,filter:"blur(40px)",pointerEvents:"none"}}/>
          <img src="data:image/png;base64,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" alt="BAM" style={{width:27,height:34,objectFit:"contain",flexShrink:0,filter:"drop-shadow(0 1px 2px rgba(226,221,159,0.28))"}}/>
          <div>
            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:20,fontWeight:400,color:GOLD,letterSpacing:2,lineHeight:1.1}}>BY ANY MEANS</div>
            <div style={{fontSize:9,color:SBdim,letterSpacing:2,marginTop:3,fontWeight:600,textTransform:"uppercase"}}>Coaches Platform</div>
          </div>
        </div>

        {/* Mode Toggle */}
        <div style={{padding:"10px 12px 6px"}}>
          <div style={{display:"flex",background:"rgba(255,255,255,0.04)",borderRadius:8,padding:3,position:"relative"}}>
            <div style={{position:"absolute",top:3,left:3,width:"calc(50% - 3px)",height:"calc(100% - 6px)",borderRadius:6,
              background:appMode==="certified"?GOLD:"rgba(226,221,159,0.12)",
              transition:"transform .2s ease, background .2s ease",
              transform:appMode==="certified"?"translateX(100%)":"translateX(0)"}}/>
            <div onClick={()=>switchMode("platform")}
              style={{flex:1,textAlign:"center",padding:"7px 0",fontSize:11,fontWeight:800,letterSpacing:.5,
                color:appMode==="platform"?GOLD:"#666",cursor:"pointer",position:"relative",zIndex:1,transition:"color .2s ease"}}>
              Platform
            </div>
            <div onClick={()=>{if(!certAccess){go("certified");}else{switchMode("certified");}}}
              style={{flex:1,textAlign:"center",padding:"7px 0",fontSize:11,fontWeight:800,letterSpacing:.5,
                color:appMode==="certified"?"#111":"#666",cursor:"pointer",position:"relative",zIndex:1,transition:"color .2s ease",
                display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
              Certification
              {!certAccess&&<Lock size={9} color="#666"/>}
            </div>
          </div>
        </div>

        <div className="bam-sidenav-scroll" data-tour-target="nav" style={{flex:1,padding:"4px 0",overflowY:"auto",position:"relative"}}>
          {/* One gold highlight that slides to whichever row is active,
              instead of each row lighting up on its own (shared/motion). */}
          <Glide variant="nav" selector=".ni-active"/>
          {/* Platform Nav */}
          {appMode==="platform"&&(
            <div className={modeTransition?"sb-nav-exit":"sb-nav-enter"}>
          {NAV_SECTIONS.map((section, si) => (
            <div key={si}>
              {section.label && (
                <div style={{padding:"16px 20px 8px"}}>
                  <div style={{height:1,background:"rgba(255,255,255,0.05)",marginBottom:14}}/>
                  <div
                    onClick={section.label==="Toolbox"?()=>go("toolbox"):undefined}
                    style={{fontSize:10,color:section.label==="Toolbox"&&nav==="toolbox"?GOLD:"rgba(255,255,255,0.28)",
                      letterSpacing:2,fontWeight:700,textTransform:"uppercase",
                      cursor:section.label==="Toolbox"?"pointer":"default",
                      display:"flex",alignItems:"center",gap:6,transition:"color .15s"}}>
                    {section.label}
                    {section.label==="Toolbox" && <ChevronRight size={10} color={nav==="toolbox"?GOLD:"rgba(255,255,255,0.28)"} style={{opacity:0.6}}/>}
                  </div>
                </div>
              )}
              {section.items.map(item => {
                const active = navIsActive(item.id);
                const disabled = item.comingSoon;
                return (
                  <div key={item.id} className={disabled?"":`ni${active?" ni-active":""}`} onClick={disabled?undefined:()=>go(item.id)}
                    style={{padding:"10px 20px",margin:"1px 8px",borderRadius:8,fontSize:13,
                      color:disabled?"#444":(active?"#F5F5F5":"#888"),fontWeight:active?600:500,
                      display:"flex",alignItems:"center",gap:11,
                      cursor:disabled?"default":"pointer",opacity:disabled?0.5:1}}>
                    <item.Icon size={16} color={disabled?"#333":(active?GOLD:"#666")} strokeWidth={active?2.2:1.6} style={{opacity:disabled?0.4:(active?1:0.6)}}/>
                    <span style={{flex:1}}>{item.label}</span>
                    <NewDot id={item.id}/>
                    {item.pro && !disabled && <span style={{fontSize:8,fontWeight:800,color:GOLD,background:`${GOLD}15`,padding:"2px 6px",borderRadius:4,letterSpacing:.5}}>PRO</span>}
                    {disabled && <span style={{fontSize:7,fontWeight:800,color:"#555",background:"rgba(255,255,255,0.04)",padding:"2px 6px",borderRadius:4,letterSpacing:.8}}>SOON</span>}
                  </div>
                );
              })}
              {section.subs && section.subs.map(sub => {
                const isOpen = groupIsOpen(sub);
                const subActive = sub.home===nav || sub.items.some(i=>i.id===nav) || groupOf(nav)===sub.id;
                return (
                  <div key={sub.id}>
                    {/* Group headings look like every other main row (Coleman:
                        "all the main nav items should look the same"). Learn's
                        opens its page; the others, and the chevron, fold. */}
                    <div className={`ni${sub.home&&nav===sub.home?" ni-active":""}`} onClick={()=>sub.home?go(sub.home):toggleCollapse(sub)}
                      {...(sub.id==="g-learn"?{"data-tour-target":"learn","data-moved":"learn"}:{})}
                      {...(sub.id==="g-drills"?{"data-moved":"nav"}:{})}
                      role="button" aria-expanded={isOpen}
                      style={{padding:"10px 20px",margin:"1px 8px",borderRadius:8,fontSize:13,
                        color:subActive?"#F5F5F5":"#888",fontWeight:subActive?600:500,
                        display:"flex",alignItems:"center",gap:11,cursor:"pointer",userSelect:"none"}}>
                      <sub.Icon size={16} color={subActive?GOLD:"#666"} strokeWidth={subActive?2.2:1.6} style={{opacity:subActive?1:0.6}}/>
                      <span style={{flex:1}}>{sub.label}</span>
                      <span role="button" aria-label={isOpen?`Fold ${sub.label}`:`Unfold ${sub.label}`}
                        onClick={(e)=>{ if(!sub.home) return; e.stopPropagation(); toggleCollapse(sub); }}
                        style={{display:"flex",padding:6,margin:-6}}>
                      <ChevronRight size={12} color={subActive?"rgba(226,221,159,0.6)":"#555"}
                        style={{transition:"transform .2s cubic-bezier(.4,0,.2,1)",
                          transform:isOpen?"rotate(90deg)":"rotate(0deg)"}}/>
                      </span>
                    </div>
                    <div className="sb-fold" data-open={isOpen?"true":"false"} aria-hidden={!isOpen}><div>
                    {sub.items.map(item => {
                      const active = navIsActive(item.id);
                      const disabled = item.comingSoon;
                      return (
                        <div key={item.id} className={disabled?"":`ni${active?" ni-active":""}`} onClick={disabled||!isOpen?undefined:()=>go(item.id)}
                          style={{padding:"7px 20px 7px 47px",margin:"1px 8px",borderRadius:8,fontSize:12.5,
                            color:disabled?"#444":(active?"#F5F5F5":"#777"),fontWeight:active?600:500,
                            display:"flex",alignItems:"center",gap:9,
                            cursor:disabled?"default":"pointer",opacity:disabled?0.5:1}}>
                          <item.Icon size={14} color={disabled?"#333":(active?GOLD:"#555")} strokeWidth={active?2.2:1.6} style={{opacity:disabled?0.4:(active?1:0.5)}}/>
                          <span style={{flex:1}}>{item.label}</span>
                          <NewDot id={item.id}/>
                          {item.pro && !disabled && <span style={{fontSize:8,fontWeight:800,color:GOLD,background:`${GOLD}15`,padding:"2px 6px",borderRadius:4,letterSpacing:.5}}>PRO</span>}
                          {disabled && <span style={{fontSize:7,fontWeight:800,color:"#555",background:"rgba(255,255,255,0.04)",padding:"2px 6px",borderRadius:4,letterSpacing:.8}}>SOON</span>}
                        </div>
                      );
                    })}
                    </div></div>
                  </div>
                );
              })}
              {section.standalone && (<>
                <div style={{height:1,background:"rgba(255,255,255,0.04)",margin:"8px 20px 6px"}}/>
                {section.standalone.map(item => {
                  const active = navIsActive(item.id);
                  return (
                    <div key={item.id} className={`ni${active?" ni-active":""}`} onClick={()=>go(item.id)}
                      style={{padding:"10px 20px",margin:"1px 8px",borderRadius:8,fontSize:13,
                        color:active?"#F5F5F5":"#888",fontWeight:active?600:500,
                        display:"flex",alignItems:"center",gap:11}}>
                      <item.Icon size={16} color={active?GOLD:"#666"} strokeWidth={active?2.2:1.6} style={{opacity:active?1:0.6}}/>
                      <span style={{flex:1}}>{item.label}</span>
                      <NewDot id={item.id}/>
                    </div>
                  );
                })}
              </>)}
            </div>
          ))}
            </div>
          )}

          {/* Certified Nav */}
          {appMode==="certified"&&(
            <div className={modeTransition?"sb-nav-exit":"sb-nav-enter"}>
              <div style={{padding:"12px 20px 8px"}}>
                <div style={{fontSize:10,color:GOLD,letterSpacing:2,fontWeight:700,textTransform:"uppercase"}}>BAM CERTIFICATION</div>
              </div>
              {/* Progress line container */}
              <div style={{position:"relative",padding:"0 0 0 0"}}>
                {/* Vertical progress line */}
                <div style={{position:"absolute",left:27,top:0,bottom:0,width:2,zIndex:0}}>
                  <div style={{width:2,background:GOLD,height:`${Math.max(0,((certCurrentUnit>=0?certCurrentUnit:9)/9)*100)}%`,borderRadius:1,transition:"height .3s ease"}}/>
                  <div style={{width:2,background:"rgba(255,255,255,0.08)",flex:1,borderRadius:1}}/>
                </div>

                {CERT_UNITS.map((unit,i)=>{
                  const unlocked = certIsUnitUnlocked(i);
                  const done = certIsUnitComplete(unit) && certIsQuizPassed(unit.id);
                  const isCurrent = certCurrentUnit === i;
                  const isActiveRoute = isCertRoute(nav) && nav.includes(`unit/${unit.id}`);
                  // Per-unit lesson progress for the sidebar count badge.
                  // Filtered to non-quiz lessons since the quiz is tracked
                  // separately via certIsQuizPassed.
                  const lessonsTotal = unit.lessons.filter(l => l.type !== "quiz").length;
                  const lessonsDone = unit.lessons.filter(l => l.type !== "quiz" && certIsLessonComplete(l.id)).length;

                  return (
                    <div key={unit.id}
                      className={`ni${isCurrent||isActiveRoute?" ni-active":""}`}
                      onClick={()=>{if(unlocked) go(`certified/unit/${unit.id}`);}}
                      style={{padding:"8px 20px 8px 40px",margin:"1px 8px",borderRadius:8,fontSize:12.5,
                        color:done?"#F5F5F5":(isCurrent?"#F5F5F5":(unlocked?"#888":"#444")),
                        fontWeight:isCurrent||isActiveRoute?600:500,
                        display:"flex",alignItems:"center",gap:9,
                        cursor:unlocked?"pointer":"not-allowed",opacity:unlocked?1:.5,position:"relative"}}>
                      {/* Status dot */}
                      <div style={{position:"absolute",left:22,width:12,height:12,borderRadius:"50%",zIndex:1,
                        background:done?GOLD:(isCurrent?"#0A0A0A":"#0A0A0A"),
                        border:`2px solid ${done?GOLD:(isCurrent?GOLD:"rgba(255,255,255,0.15)")}`,
                        display:"flex",alignItems:"center",justifyContent:"center",
                        boxShadow:done?`0 0 8px ${GOLD}40`:(isCurrent?`0 0 6px ${GOLD}20`:"none")}}>
                        <CompleteCheck on={done} size={7} color="#111" strokeWidth={4}/>
                        {isCurrent&&!done&&<div style={{width:4,height:4,borderRadius:"50%",background:GOLD}}/>}
                      </div>
                      <span style={{fontSize:10,fontWeight:700,color:done?GOLD+"99":(isCurrent?GOLD+"99":"#555"),marginRight:2}}>
                        {i+1}.
                      </span>
                      <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{unit.title}</span>
                      {/* Lesson count badge — only when unlocked + has lessons.
                          Shows "X/Y" so coaches see progress at a glance from
                          the sidebar without needing to expand the unit. */}
                      {unlocked && lessonsTotal > 0 && (
                        <span style={{
                          fontSize:9,
                          fontWeight:700,
                          color:done?GOLD:(lessonsDone>0?GOLD+"AA":"#555"),
                          background: done ? `${GOLD}20` : (lessonsDone>0 ? `${GOLD}10` : "rgba(255,255,255,0.04)"),
                          padding:"2px 6px",
                          borderRadius:5,
                          letterSpacing:.2,
                          flexShrink:0,
                        }}>
                          {lessonsDone}/{lessonsTotal}
                        </span>
                      )}
                    </div>
                  );
                })}

                {/* Divider */}
                <div style={{height:1,background:"rgba(255,255,255,0.05)",margin:"8px 20px 6px"}}/>

                {/* Final Exam */}
                {(()=>{
                  const allDone = CERT_UNITS.every((u,i)=>certIsUnitComplete(u)&&certIsQuizPassed(u.id));
                  const examActive = nav==="certified/exam"||nav==="certified/certificate";
                  return (
                    <div className={`ni${examActive?" ni-active":""}`}
                      onClick={()=>{if(allDone&&!certIsCertified) go("certified/exam"); else if(certIsCertified) go("certified/certificate");}}
                      style={{padding:"8px 20px 8px 40px",margin:"1px 8px",borderRadius:8,fontSize:12.5,
                        color:allDone||certIsCertified?"#F5F5F5":"#444",fontWeight:examActive?600:500,
                        display:"flex",alignItems:"center",gap:9,
                        cursor:allDone||certIsCertified?"pointer":"not-allowed",opacity:allDone||certIsCertified?1:.5,position:"relative"}}>
                      <div style={{position:"absolute",left:22,width:12,height:12,display:"flex",alignItems:"center",justifyContent:"center"}}>
                        {certIsCertified?<Shield size={12} color={GOLD}/>:(allDone?<Shield size={12} color={GOLD}/>:<Lock size={10} color="#555"/>)}
                      </div>
                      Final Exam
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
        {appMode==="platform"&&(
          <SidebarLive tourTarget="live" nav={nav} go={go} callLine={nextCallLine} chatUnread={chatUnread} showChats={hasChats}
            org={tileOrg} onOrg={openMyOrg} showInterns={showInternTile}
            style={{margin:"6px 10px 0"}}/>
        )}
        {/* What's here, Submit Content and My Portal live in the menu under
            your name now (ProfileMenu). The tour is still permanent, not an
            onboarding step: it is one tap from the card below. */}
        <div style={{position:"relative"}}>
        <ProfileMenu open={profileMenuOpen} onClose={()=>setProfileMenuOpen(false)} nav={nav} onPick={pickProfileMenu}/>
        <div className="sb-footer" data-profile-anchor="" data-tour-target="profile" data-moved="profile" data-moved-label="My Portal is in here now" role="button" aria-haspopup="menu" aria-expanded={profileMenuOpen}
          onClick={()=>setProfileMenuOpen(o=>!o)}
          style={{margin:"8px 10px 10px",padding:"12px 14px",borderRadius:10,
            background:"rgba(255,255,255,0.025)",border:"1px solid rgba(255,255,255,0.04)",
            display:"flex",alignItems:"center",gap:11,cursor:"pointer"}}>
          <div style={{width:34,height:34,borderRadius:10,background:GOLD,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:"#111",flexShrink:0,position:"relative",boxShadow:`0 2px 12px ${GOLD}44`,overflow:"hidden"}}>
            {appPhoto?<img src={appPhoto} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:appInitials}
            <div style={{position:"absolute",bottom:1,right:1,width:8,height:8,borderRadius:"50%",background:"#5AB584",border:"2px solid #111"}}/>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:13,fontWeight:600,color:"#E8E8E8",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{appProfile.name}</div>
            <div style={{fontSize:10,color:"#666",fontWeight:500,display:"flex",alignItems:"center",gap:6}}>
              {appProfile.role||"Coach"}
              {certPurchased&&<span style={{fontSize:9,fontWeight:700,color:GOLD,background:`${GOLD}15`,padding:"1px 5px",borderRadius:4}}>{certIsCertified?"✓":certCompletedUnits+"/9"}</span>}
            </div>
          </div>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={SBdim} strokeWidth="2" strokeLinecap="round" style={{transform:profileMenuOpen?"rotate(-90deg)":"none",transition:"transform .15s"}}><polyline points="9 18 15 12 9 6"/></svg>
        </div>
        </div>
      </div>

      {/* MAIN */}
      <div onClick={()=>{if(notifPanelOpen)setNotifPanelOpen(false);}} style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        {/* Topbar — paddingTop pulls in env(safe-area-inset-top) so the
            controls render BELOW the iPhone notch / Dynamic Island on
            native. On web (no inset) it collapses to 0. */}
        <div className="bam-topbar-frosted" style={{minHeight:56,background:dark?"rgba(10,10,10,0.88)":C.bgCard,borderBottom:`1px solid ${C.cardBorder}`,display:"flex",alignItems:"center",padding:"env(safe-area-inset-top, 0px) 28px 0",gap:16,flexShrink:0,backdropFilter:"blur(16px)",WebkitBackdropFilter:"blur(16px)",boxShadow:dark?"0 1px 0 0 rgba(255,255,255,0.02)":"none",position:"relative",height:"calc(56px + env(safe-area-inset-top, 0px))"}}>
          {/* Cert mode gold accent line */}
          {appMode==="certified"&&<div className="cert-gold-line" style={{position:"absolute",top:0,left:0,height:2,background:`linear-gradient(90deg,${GOLD},${GOLD}80)`}}/>}
          <div className="bam-hamburger" data-tour-target="menu" data-moved="profile" onClick={()=>{haptic("light");setMobileMenuOpen(true);}} style={{display:"none",alignItems:"center",justifyContent:"center",width:36,height:36,borderRadius:9,background:C.bg,border:`1px solid ${C.border}`,cursor:"pointer",flexShrink:0}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.text} strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </div>
          {/* Shrinks before the buttons do: a long page name truncates
              rather than pushing settings off the right edge of a phone. */}
          <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0,flex:"0 1 auto"}}>
            {appMode==="certified"?(
              <>
                <Shield size={15} color={GOLD} strokeWidth={2}/>
                <span style={{fontSize:13,fontWeight:700,color:GOLD}}>BAM Certification</span>
                {isCertRoute(nav)&&nav!=="certified"&&(
                  <><ChevronRight size={12} color={C.textDim} style={{opacity:0.5}}/>
                  <span style={{fontSize:13,fontWeight:600,color:C.text}}>
                    {nav.includes("/unit/")?CERT_UNITS.find(u=>nav.includes(`/${u.id}`))?.title||"Unit"
                      :nav.includes("exam")?"Final Exam"
                      :nav.includes("certificate")?"Certificate"
                      :nav.includes("quiz")?"Quiz"
                      :nav.includes("lesson")?"Lesson":"Overview"}
                  </span></>
                )}
              </>
            ):(
              <>
                {current?.Icon&&<current.Icon size={15} color={GOLD} strokeWidth={2} style={{flexShrink:0}}/>}
                {/* Parent crumb hidden on phones — at <600px the topbar can't fit
                    "Player Development > Insights" without wrapping to two
                    or three lines. The sidebar drawer already shows the
                    full hierarchy, so dropping the parent on mobile is a
                    safe trade. forceSingleLine clamps the leaf label so a
                    long page name truncates with an ellipsis instead of
                    pushing the right-side controls off-screen. */}
                {current?.parent&&<span className="bam-topbar-parent" style={{display:"inline-flex",alignItems:"center",gap:8,minWidth:0}}><span className="btn" onClick={()=>go(current.parent)} style={{fontSize:13,fontWeight:500,color:C.textDim,cursor:"pointer",whiteSpace:"nowrap"}}>{current.parent==="pd"?"Player Development":"Team Coaching"}</span><ChevronRight size={12} color={C.textDim} style={{opacity:0.5,flexShrink:0}}/></span>}
                {/* Tap title to scroll the current feed back to top — iOS
                    standard. Queries the currently-rendered .bam-content-scroll
                    (only one is mounted at a time per route). */}
                <div onClick={() => {
                  try {
                    const els = document.querySelectorAll(".bam-content-scroll");
                    let target = null;
                    for (const el of els) {
                      // Pick the first visible one (offsetParent is null when
                      // display:none, so this skips hidden routes).
                      if (el.offsetParent !== null) { target = el; break; }
                    }
                    if (target) {
                      target.scrollTo({ top: 0, behavior: "smooth" });
                      haptic("selection");
                    }
                  } catch { /* swallow */ }
                }} style={{fontSize:13,fontWeight:700,color:C.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",minWidth:0,cursor:"pointer"}}>{current?.short?(<><span className="bam-topbar-long">{current.label}</span><span className="bam-topbar-short">{current.short}</span></>):current?.label}</div>
              </>
            )}
          </div>
          <div style={{flex:1}}/>
          <div className="bam-topbar-search"><SearchBar C={C} allItems={ALL_CONTENT_ITEMS} onNavigate={go}/></div>
          {/* Mobile-only search trigger — opens fullscreen SearchBar overlay.
              Mirrors the icon language of the notifications button next to
              it so the topbar reads as two equal action buttons on phones. */}
          <div className="bam-mobile-search-toggle btn" onClick={()=>setMobileSearchOpen(true)} aria-label="Search"
            style={{display:"none",alignItems:"center",justifyContent:"center",width:36,height:36,borderRadius:9,background:C.bg,border:`1px solid ${C.border}`,cursor:"pointer",flexShrink:0}}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.textDim} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7"/><line x1="20" y1="20" x2="16.65" y2="16.65"/>
            </svg>
          </div>
          {/* Chats on phones: next to the bell, with its unread count, the way
              messaging sits in most apps. The sidebar's Chats tile does this
              job on desktop. Same class as the search button, so it only
              shows below the phone breakpoint. */}
          {hasChats&&(
            <div className="bam-mobile-search-toggle btn" data-tour-target="chats" data-moved="live" role="button" tabIndex={0} onClick={()=>go("chats")}
              aria-label={`Chats${chatUnread>0?` (${chatUnread} unread)`:""}`}
              style={{display:"none",alignItems:"center",justifyContent:"center",width:36,height:36,borderRadius:9,background:C.bg,border:`1px solid ${C.border}`,cursor:"pointer",flexShrink:0,position:"relative"}}>
              <MessageCircle size={16} color={nav==="chats"?GOLD:C.textDim}/>
              {chatUnread>0&&<div style={{position:"absolute",top:-2,right:-2,minWidth:16,height:16,padding:"0 4px",borderRadius:8,background:GOLD,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:800,color:"#0A0A0A"}}>{chatUnread>9?"9+":chatUnread}</div>}
            </div>
          )}
          <div style={{position:"relative"}}>
            <div className="btn" role="button" tabIndex={0} aria-label={`Notifications${unreadCount>0?` (${unreadCount} unread)`:""}`} onClick={()=>{haptic("light");setNotifPanelOpen(v=>{ if(!v){ try{ window.dispatchEvent(new Event("bam-notif-open")); }catch{} } return !v; });}} style={{width:36,height:36,borderRadius:9,background:notifPanelOpen?C.goldDim:C.bg,border:`1px solid ${notifPanelOpen?GOLD+"80":C.border}`,display:"flex",alignItems:"center",justifyContent:"center",position:"relative"}}>
              <Ic.Bell c={notifPanelOpen?GOLD:C.textDim} dot={unreadCount>0}/>
              {unreadCount>0&&<div style={{position:"absolute",top:-2,right:-2,width:16,height:16,borderRadius:"50%",background:"#E06060",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:800,color:"#fff"}}>{unreadCount}</div>}
            </div>
            {/* Notification Panel */}
            {notifPanelOpen&&createPortal(
              <>
              {/* Transparent full-screen catcher so any outside tap closes the panel. */}
              <div onClick={()=>setNotifPanelOpen(false)} style={{position:"fixed",inset:0,zIndex:3000}}/>
              {/* Portaled to <body> + position:fixed so the panel escapes the
                  topbar's stacking context — previously the dashboard hero
                  painted over it, making it look transparent / half-hidden. */}
              <div onClick={e=>e.stopPropagation()} className="bam-notif-panel bam-menu-in" style={{position:"fixed",top:"calc(env(safe-area-inset-top, 0px) + 60px)",right:12,width:"min(360px, calc(100vw - 24px))",maxHeight:440,overflow:"auto",background:dark?"#131313":"#fff",border:`1px solid ${C.cardBorder}`,borderRadius:14,boxShadow:"0 16px 48px rgba(0,0,0,0.4)",zIndex:3001}}>
                <div style={{padding:"14px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <span style={{fontSize:14,fontWeight:800,color:C.text}}>Notifications</span>
                  {unreadCount>0&&<div className="btn" onClick={markAllRead} style={{fontSize:11,fontWeight:700,color:GOLD}}>Mark all read</div>}
                </div>
                {/* Empty state — the groups all return null when empty, which
                    left a bare header over blank space (looked half-loaded). */}
                {notifications.length===0&&(
                  <div style={{padding:"36px 20px",textAlign:"center"}}>
                    <Ic.Bell c={C.textDim}/>
                    <div style={{fontSize:13,fontWeight:700,color:C.text,marginTop:10}}>You're all caught up</div>
                    <div style={{fontSize:12,color:C.textDim,marginTop:4,lineHeight:1.5}}>New replies and announcements will show up here.</div>
                  </div>
                )}
                {[{type:"content",label:"New Content",icon:<Zap size={12} color={GOLD}/>},{type:"community",label:"Community",icon:<Users size={12} color="#6EE7B7"/>},{type:"platform",label:"Platform",icon:<Megaphone size={12} color="#93C5FD"/>}].map(group=>{
                  const items=notifications.filter(n=>n.type===group.type);
                  if(items.length===0) return null;
                  return (
                    <div key={group.type}>
                      <div style={{padding:"10px 16px 4px",display:"flex",alignItems:"center",gap:6}}>
                        {group.icon}
                        <span style={{fontSize:10,fontWeight:800,color:C.textDim,letterSpacing:.8,textTransform:"uppercase"}}>{group.label}</span>
                      </div>
                      {items.map(n=>(
                        <div key={n.id} className="btn" onClick={()=>{markRead(n.id);setNotifPanelOpen(false);if(n.postId){setActiveTag("All");setPendingPostScroll(n.postId);go("community");}}}
                          style={{padding:"10px 16px",display:"flex",gap:10,alignItems:"flex-start",borderBottom:`1px solid ${C.border}`,
                            background:n.read?"transparent":(dark?"rgba(226,221,159,0.03)":"rgba(226,221,159,0.06)")}}>
                          {!n.read&&<div style={{width:6,height:6,borderRadius:"50%",background:GOLD,flexShrink:0,marginTop:6}}/>}
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:13,fontWeight:n.read?500:700,color:C.text,lineHeight:1.3}}>{n.title}</div>
                            <div style={{fontSize:11,color:C.textDim,marginTop:2,lineHeight:1.4}}>{n.desc}</div>
                          </div>
                          <span style={{fontSize:10,color:C.textDim,flexShrink:0,marginTop:2}}>{n.time}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
              </>,
              document.body
            )}
          </div>
          <div className="bam-theme-toggle" style={{display:"flex",alignItems:"center",gap:9}}>
            {/* "Dark"/"Light" label hidden on phones via .bam-theme-label
                rule — the toggle visual carries enough meaning on its own
                and the topbar is busy. Desktop keeps the label. */}
            <span className="bam-theme-label" style={{fontSize:12,color:C.textDim,fontWeight:500}}>{dark?"Dark":"Light"}</span>
            <div className="ttgl" onClick={toggleDark} style={{width:44,height:24,borderRadius:12,background:dark?GOLD:C.border,position:"relative"}}>
              <div className="tthm" style={{position:"absolute",top:4,left:4,width:15,height:15,borderRadius:"50%",background:dark?"#0A0A0A":"#fff",transform:dark?"translateX(20px)":"translateX(0)",boxShadow:"0 1px 5px rgba(0,0,0,.3)"}}/>
            </div>
          </div>
          <div className="btn" onClick={()=>go("settings")} style={{width:36,height:36,borderRadius:9,background:nav==="settings"?C.goldDim:C.bg,border:`1px solid ${nav==="settings"?GOLD+"80":C.border}`,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={nav==="settings"?GOLD:C.textDim} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
          </div>
          {/* Subscription badge removed — no plan tiers */}
        </div>

        {/* Mobile fullscreen search overlay. Renders when the topbar's
            mobile-only search-icon button is tapped. Backdrop click closes.
            SearchBar's onNavigate is wrapped to also close the overlay so
            tapping a result feels instant. */}
        {mobileSearchOpen && (
          <div role="dialog" aria-modal="true" aria-label="Search"
            onClick={() => setMobileSearchOpen(false)}
            style={{position:"fixed",inset:0,zIndex:5200,background:dark?"rgba(0,0,0,0.85)":"rgba(255,255,255,0.92)",backdropFilter:"blur(6px)",display:"flex",flexDirection:"column",padding:"max(20px, env(safe-area-inset-top, 0px)) 16px 20px"}}>
            <div onClick={e => e.stopPropagation()} style={{width:"100%",maxWidth:560,margin:"0 auto",display:"flex",flexDirection:"column",gap:12}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                <div style={{fontSize:11,fontWeight:800,color:GOLD,letterSpacing:1.5,textTransform:"uppercase"}}>Search</div>
                <button onClick={() => setMobileSearchOpen(false)} aria-label="Close search"
                  style={{background:"transparent",border:"none",color:C.textDim,fontSize:22,cursor:"pointer",lineHeight:1,padding:8}}>×</button>
              </div>
              <SearchBar
                C={C}
                allItems={ALL_CONTENT_ITEMS}
                onNavigate={(s) => { setMobileSearchOpen(false); go(s); }}
              />
            </div>
          </div>
        )}

        {/* Page — native build swaps the fade for an iOS-style slide-in
            so route changes feel like push/pop transitions. The
            BAMErrorBoundary key changes on every nav, which remounts the
            subtree and replays the animation. */}
        {proArriving && (
          <div className={`bam-pro-overlay bam-pro-overlay-${PRO_TRANSITION}`} aria-hidden="true">
            <div className="bam-pro-overlay-mark">PRO</div>
          </div>
        )}
        {/* Past-due banner. Above <main> and outside the error boundary's
            remounting key, so it survives every navigation and a coach
            cannot page away from it. */}
        <PastDueBanner profile={billingProfile} C={C} dark={dark}/>
        {/* This week's drop, on every page but Home (its card) and the drop itself, until it's opened or closed. */}
        {dropAction && latestDrop && nav !== "dashboard" && nav !== "drop" && !DROP_HIDE_NAVS.has(nav) && (
          <DropBar C={C} drop={latestDrop} go={go} narrow={isMobile} onClose={() => closeDropReminder(dropAction)}/>
        )}
        <BAMErrorBoundary key={pageKey}><main style={{flex:1,overflow:isContentPage?"hidden":"auto",display:"flex",flexDirection:"column",animation: nav === "pro" ? undefined : (isNative() ? "nativePageSlideIn .22s cubic-bezier(.2,.8,.2,1) both" : "bamPageFade .2s ease-out both")}}
          /* The Pro Development collection arrives through its own doorway
             rather than the standard page fade — the class carries the
             second half of the transition the chip started. */
          className={nav === "pro" ? `bam-pro-page-${PRO_TRANSITION}` : undefined}>

          {/* Content pages — X & O has its own dedicated layout
              (compact vocab link + 5-category nav with conditional
              type sub-filter + tag chips); every other section uses
              the generic ContentPage. */}
          {isContentPage && nav === "xo" && <XOPage C={C} dark={dark} newSince={sectionLastSeen?.xo || null} favDrills={favDrills} toggleFav={toggleFav} videoNotes={videoNotes} saveVideoNotes={saveVideoNotes}/>}
          {isContentPage && nav !== "xo" && <ContentPage sectionId={nav} C={C} dark={dark} favDrills={favDrills} toggleFav={toggleFav} videoNotes={videoNotes} saveVideoNotes={saveVideoNotes} planFocusMode={planFocusMode} setPlanFocusMode={setPlanFocusMode} favLists={favLists} favListMembership={favListMembership} toggleFavInList={toggleFavInList} createFavList={createFavList} rosterNames={rosterNames}/>}

          {/* What's here — the whole platform, grouped by what a coach is
              trying to do. Rendered from constants/platformMap.js, which a
              test holds to the sidebar. */}
          {/* Calls. Their own page now, rather than a widget partway down
              the Community feed. */}
          {nav === "calls" && (
            <Suspense fallback={<LazyFallback/>}>
              <CallsPage C={C} configs={visibleCalls} tz={settingsTz}/>
            </Suspense>
          )}

          {nav === "tour" && (
            <Suspense fallback={<LazyFallback/>}>
              <TourPage C={C} joinedAt={sbUser?.created_at} available={availableSurfaces}/>
            </Suspense>
          )}

          {/* Standalone "Our Offensive Vocab" reference module */}
          {nav === "vocab" && <VocabPage C={C} dark={dark}/>}

          {/* Foundations — the concept library. Locked for coaches while the
              entries are in review; admins preview the real experience. */}
          {nav === "foundations" && (
            <Suspense fallback={<LazyFallback/>}>
              <FoundationsPage C={C} dark={dark}/>
            </Suspense>
          )}

          {/* Learn: today's Lab session, then the places to read and watch. */}
          {nav === "learn-home" && <LearnHome C={C} dark={dark} go={go} labOpen={labOpen}
            recentMasterclasses={recentMasterclasses} resumable={resumableMasterclasses} latestQa={latestQa}
            newestMasterclass={newestMasterclass} freshMasterclassCount={learnFreshMasterclasses.length}
            qaIsNew={!!latestQa && qaSeen !== latestQa.id && isFreshQa(latestQa, 10)}
            qaOpened={!latestQa || qaSeen === latestQa.id} onOpenQa={openLatestQa}
            newCounts={newCounts || {}}
            onOpenModule={(key)=>{ try { sessionStorage.setItem("bam_open_module", key); } catch { /* storage blocked */ } go("master"); }}/>}

          {/* The Learning Lab — today's set. Locked while in review. */}
          {nav === "learn" && (
            <Suspense fallback={<LazyFallback/>}>
              <LearningLabPage C={C} dark={dark} certEnrolled={certPurchased}/>
            </Suspense>
          )}

          {/* Chats — cert chat + event/group chats (messaging engine) */}
          {nav === "chats" && (
            <div className="bam-content-scroll" style={{flex:1,overflow:"auto",position:"relative"}}>
              <Suspense fallback={<LazyFallback/>}>
                <ChatsPage dark={dark} onUnreadTotal={setChatUnread}/>
              </Suspense>
            </div>
          )}

          {/* Referrals — coach affiliate link + stats */}
          {nav === "referrals" && (
            <Suspense fallback={<LazyFallback/>}>
              <ReferralPanel C={C} dark={dark}/>
            </Suspense>
          )}

          {/* New-message toast (floats over any surface) */}
          {chatToast && (
            <div onClick={()=>{
              // Land in the THREAD, not the chat list — ChatsPage consumes
              // the stash once its list loads (mirrors bam_open_drill).
              if (chatToast.chatId) { try { sessionStorage.setItem("bam_open_chat", String(chatToast.chatId)); } catch {} }
              setChatToast(null); go("chats");
            }}
              style={{position:"fixed",top:"calc(env(safe-area-inset-top, 0px) + 18px)",left:"50%",transform:"translateX(-50%)",zIndex:9000,animation:"slideD .3s ease",background:"rgba(20,20,20,0.95)",backdropFilter:"blur(10px)",border:`1px solid ${GOLD}44`,borderRadius:14,padding:"10px 16px",display:"flex",alignItems:"center",gap:10,cursor:"pointer",boxShadow:"0 10px 40px rgba(0,0,0,0.5)",maxWidth:340}}>
              <div style={{width:30,height:30,borderRadius:9,background:`${GOLD}22`,color:GOLD,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><MessageCircle size={15} color={GOLD}/></div>
              <div style={{minWidth:0}}>
                <div style={{fontSize:12.5,fontWeight:700,color:GOLD,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{chatToast.name}</div>
                <div style={{fontSize:12,color:"#ccc",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{chatToast.body}</div>
              </div>
            </div>
          )}

          {/* Community */}
          {nav==="community"&&(
            <PullToRefresh onRefresh={async () => {
              try {
                const { fetchCommunityPosts } = await import("./utils/communityApi");
                const posts = await fetchCommunityPosts({ limit: COMMUNITY_FEED_LIMIT });
                setCommunityPosts(posts);
                writeCommunityCache(posts);
              } catch {}
            }}>
            <div ref={scrollRef} className="bam-content-scroll" style={{flex:1,overflow:"auto",padding:28,position:"relative"}}>
              <Globe dark={dark}/>
              <div style={{maxWidth:720,margin:"0 auto",position:"relative",zIndex:1}}>

                <div className={`bam-community-composer ${focused ? "compose-focused" : ""}`} style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:16,padding:0,marginBottom:24,transition:"border-color .2s ease,box-shadow .2s ease",boxShadow:`${C.surfaceShadow}, ${C.innerGlow}`,overflow:"hidden"}}>
                  <input ref={attachRef} type="file" multiple accept="image/*,video/*,.pdf" style={{display:"none"}} onChange={e=>{
                    const files=Array.from(e.target.files||[]);
                    // Per-kind soft size limits so a 5GB clip doesn't
                    // blow up Supabase storage costs. The bucket has a
                    // matching server-side cap (320MB) as defense in
                    // depth. Videos get the largest cap because they're
                    // the legitimate big-file use case; images cap tight.
                    const VIDEO_MAX = 300 * 1024 * 1024; // 300 MB
                    const OTHER_MAX = 25  * 1024 * 1024; // 25 MB
                    const skipped = [];
                    files.slice(0,3-attachments.length).forEach(file=>{
                      const isVideo = (file.type || "").startsWith("video/");
                      const cap = isVideo ? VIDEO_MAX : OTHER_MAX;
                      if (file.size > cap) {
                        skipped.push(`${file.name} (${(file.size/1024/1024).toFixed(0)} MB)`);
                        return;
                      }
                      // We hold onto the File object so the post-submit
                      // handler can upload it to community-uploads. The
                      // object URL is the local preview only.
                      setAttachments(a=>[...a,{file,url:URL.createObjectURL(file),type:file.type,name:file.name,size:file.size}]);
                    });
                    if (skipped.length) {
                      showApiError(`Too large to attach (max 300MB video / 25MB image): ${skipped.join(", ")}`);
                    }
                    e.target.value="";
                  }}/>
                  <div style={{padding:"20px 22px 16px"}}>
                    <div style={{display:"flex",gap:14,alignItems:"flex-start"}}>
                      <div style={{width:42,height:42,borderRadius:"50%",background:`linear-gradient(135deg, ${GOLD}, #C8C47A)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:"#1A1A1A",flexShrink:0,boxShadow:`0 2px 12px ${GOLD}40`,overflow:"hidden"}}>
                        {appPhoto ? <img src={appPhoto} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : appInitials}
                      </div>
                      <div style={{flex:1,position:"relative"}}>
                        {/* Tagged names in gold, as you type. A textarea cannot colour
                            part of its text, so this is a mirror behind it with the same
                            font and metrics; while a tag is present the textarea's own
                            text goes transparent and the caret stays gold. The names are
                            "bold" by a hairline text stroke rather than font-weight,
                            which would widen them and pull the mirror out of line. */}
                        {(()=>{
                          const live=taggedCoaches.filter(t=>t.token&&compose.includes(t.token));
                          if(!live.length) return null;
                          const esc=x=>x.replace(/[.*+?^${}()|[\]\\]/g,"\\$&");
                          const re=new RegExp(`(${live.map(t=>esc(t.token)).sort((a,b)=>b.length-a.length).join("|")})`,"g");
                          const parts=compose.split(re);
                          return (
                            <div ref={composeMirrorRef} aria-hidden="true" style={{position:"absolute",inset:0,pointerEvents:"none",overflow:"hidden",
                              fontSize:16,color:C.text,fontFamily:"'DM Sans',sans-serif",lineHeight:1.6,whiteSpace:"pre-wrap",wordBreak:"break-word",padding:0}}>
                              {parts.map((part,i)=> i%2===1
                                ? <span key={i} style={{color:GOLD,WebkitTextStroke:`0.45px ${GOLD}`}}>{part}</span>
                                : <span key={i}>{part}</span>)}
                              {"\n"}
                            </div>
                          );
                        })()}
                        <textarea ref={composeRef} value={compose} onScroll={e=>{ if(composeMirrorRef.current) composeMirrorRef.current.scrollTop=e.target.scrollTop; }} onChange={e=>{
                          const val=e.target.value;setCompose(val);
                          // Detect @ or / trigger for content mention
                          const cursor=e.target.selectionStart;
                          const before=val.slice(0,cursor);
                          const atMatch=before.match(/([@/])(\w*)$/);
                          if(atMatch){setMentionOpen(true);setMentionTrigger(atMatch[1]);setMentionQuery(atMatch[2]);}
                          else{setMentionOpen(false);setMentionQuery("");}
                        }} onFocus={()=>setFocused(true)} onBlur={()=>{setTimeout(()=>{if(!compose&&!attachments.length&&!embeddedContent.length&&!showDrillPicker&&!showFilmPicker&&!showFeaturePicker){setFocused(false);setComposePlaceholder("Share a drill, win, question, or insight...");}setMentionOpen(false);},200);}} onKeyDown={e=>{if(e.key==="Escape")setMentionOpen(false);}} placeholder={composePlaceholder}
                          style={{width:"100%",background:"transparent",border:"none",outline:"none",fontSize:16,
                            color:taggedCoaches.some(t=>t.token&&compose.includes(t.token))?"transparent":C.text,
                            resize:"none",fontFamily:"'DM Sans',sans-serif",lineHeight:1.6,minHeight:focused?86:36,transition:"min-height .22s",caretColor:GOLD,
                            position:"relative",padding:0,margin:0,display:"block"}}/>
                        {/* @-mention dropdown */}
                        {mentionOpen&&(()=>{
                          const q=mentionQuery.toLowerCase();
                          const results=ALL_CONTENT_ITEMS.filter(i=>i.title.toLowerCase().includes(q)).slice(0,6);
                          const coaches=mentionTrigger==="@"?coachMatches:[];
                          if(results.length===0&&coaches.length===0) return null;
                          const pickCoach=(p)=>{
                            // Their name, as written, with no @ (Coleman: "I
                            // just want their name"). The post carries who
                            // was tagged as a mention embed, so PostCard
                            // highlights it and notify-mentions can reach them.
                            const token=(p.name||[p.first_name,p.last_name].filter(Boolean).join(" ")||p.handle||"").trim();
                            if(!token) return;
                            const cursor=composeRef.current?.selectionStart||compose.length;
                            const before=compose.slice(0,cursor).replace(/@\w*$/,`${token} `);
                            setCompose(before+compose.slice(cursor));
                            setTaggedCoaches(prev=>prev.some(x=>x.id===p.id)?prev:[...prev,{id:p.id,token}]);
                            setMentionOpen(false);
                            requestAnimationFrame(()=>{ const el=composeRef.current; if(el){ el.focus(); el.setSelectionRange(before.length,before.length); } });
                          };
                          return (
                            <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:100,background:dark?"#151515":"#fff",border:`1px solid ${GOLD}40`,borderRadius:12,boxShadow:"0 12px 40px rgba(0,0,0,0.4)",maxHeight:320,overflow:"auto",marginTop:4}}>
                              {coaches.length>0&&(<>
                                <div style={{padding:"8px 12px",fontSize:10,fontWeight:800,color:C.textDim,letterSpacing:.8,textTransform:"uppercase",borderBottom:`1px solid ${C.border}`}}>Tag a coach</div>
                                {coaches.map(p=>{
                                  const nm=p.name||[p.first_name,p.last_name].filter(Boolean).join(" ")||p.handle||"Coach";
                                  return (
                                    <div key={p.id} className="btn" onMouseDown={e=>{e.preventDefault();pickCoach(p);}}
                                      style={{display:"flex",alignItems:"center",gap:10,padding:"9px 14px",borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
                                      <div style={{width:28,height:28,borderRadius:"50%",overflow:"hidden",flexShrink:0,background:getAC(nm),display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:"#fff"}}>
                                        {p.avatarUrl?<img src={p.avatarUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:nm.split(/\s+/).map(w=>w[0]).join("").slice(0,2).toUpperCase()}
                                      </div>
                                      <div style={{flex:1,minWidth:0,fontSize:13,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{nm}</div>
                                      {p.handle&&<div style={{fontSize:11,color:C.textDim,flexShrink:0}}>@{p.handle}</div>}
                                    </div>
                                  );
                                })}
                              </>)}
                              {results.length>0&&<div style={{padding:"8px 12px",fontSize:10,fontWeight:800,color:C.textDim,letterSpacing:.8,textTransform:"uppercase",borderBottom:`1px solid ${C.border}`}}>Embed Content</div>}
                              {results.map(item=>(
                                <div key={`${item.sectionId}-${item.id}`} className="btn" onMouseDown={e=>{
                                  e.preventDefault();
                                  // Remove the @query from compose text
                                  const cursor=composeRef.current?.selectionStart||compose.length;
                                  const before=compose.slice(0,cursor).replace(/[@/]\w*$/,"");
                                  const after=compose.slice(cursor);
                                  setCompose(before+after);
                                  setEmbeddedContent(prev=>[...prev,contentEmbed(item)]);
                                  setMentionOpen(false);
                                }} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
                                  <div style={{width:8,height:8,borderRadius:2,background:GOLD,flexShrink:0}}/>
                                  <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontSize:13,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.title}</div>
                                    <div style={{fontSize:11,color:C.textDim}}>{item.tag} · {item.duration}</div>
                                  </div>
                                  <div style={{fontSize:10,fontWeight:700,color:GOLD,background:GOLD+"15",padding:"2px 8px",borderRadius:8,flexShrink:0}}>{item.sectionId==="pd"?"Drill":item.sectionId==="team"?"Team":item.sectionId==="master"?"Masterclass":"Content"}</div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                        {!compose&&attachments.length===0&&!focused&&(<div className="bam-composer-extras" style={{display:"flex",alignItems:"center",gap:9,paddingTop:11,borderTop:`1px solid ${C.border}`,opacity:pVis?1:0,transition:"opacity .36s"}}>
                          <Lightbulb size={13} color={dark?GOLD:"#555"} style={{flexShrink:0,opacity:0.7}}/>
                          <div style={{fontSize:13,color:C.textDim,fontStyle:"italic",flex:1}}>{PROMPTS[pIdx]}</div>
                          <span className="btn" onClick={cyclePrompt} style={{display:"flex",alignItems:"center",gap:5,fontSize:12,color:dark?GOLD:"#555",fontWeight:700}}><Ic.Spin c={dark?GOLD:"#555"} s={12}/> Next</span>
                        </div>)}
                        {attachments.length>0&&(
                          <div style={{display:"flex",gap:8,marginTop:10,flexWrap:"wrap"}}>
                            {attachments.map((a,idx)=>{
                              const isImg=a.type.startsWith("image/");
                              const isVid=a.type.startsWith("video/");
                              return(
                                <div key={idx} style={{position:"relative",borderRadius:10,overflow:"hidden",border:`1px solid ${C.border}`,flexShrink:0}}>
                                  {isImg&&<img src={a.url} style={{width:90,height:72,objectFit:"cover",display:"block"}}/>}
                                  {isVid&&<div style={{width:110,height:72,background:"#111",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:3}}>
                                    <svg width="18" height="18" viewBox="0 0 18 18"><circle cx="9" cy="9" r="8" fill={GOLD} opacity=".15" stroke={GOLD} strokeWidth="1"/><path d="M7 6.5l5 2.5-5 2.5V6.5z" fill={GOLD}/></svg>
                                    <span style={{fontSize:9,color:"#888",maxWidth:98,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",padding:"0 6px"}}>{a.name}</span>
                                  </div>}
                                  {!isImg&&!isVid&&<div style={{width:110,height:72,background:dark?"#2A1A10":"#FFF5F0",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:3}}>
                                    <span style={{fontSize:20}}>📄</span>
                                    <span style={{fontSize:9,color:"#888",maxWidth:98,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",padding:"0 6px"}}>{a.name}</span>
                                  </div>}
                                  <div className="btn" role="button" aria-label="Remove attachment" onClick={()=>setAttachments(p=>p.filter((_,j)=>j!==idx))} style={{position:"absolute",top:2,right:2,width:28,height:28,borderRadius:"50%",background:"rgba(0,0,0,0.65)",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
                                    <X size={9} color="#fff"/>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Embedded content previews — chip per attached embed.
                      film_clip embeds carry a timestamp pill; legacy
                      drill-shape embeds (with .tag / .duration) render
                      with their own metadata. Generic fallback for any
                      future embed type. */}
                  {embeddedContent.length>0&&(
                    <div style={{padding:"0 22px 12px",display:"flex",gap:8,flexWrap:"wrap"}}>
                      {embeddedContent.map((ec,idx)=>{
                        const onRemove = ()=>setEmbeddedContent(prev=>prev.filter((_,i)=>i!==idx));
                        if (ec.type === "film_clip") {
                          const ts = ec.timestamp || 0;
                          const mm = Math.floor(ts / 60), ss = String(ts % 60).padStart(2,"0");
                          return (
                            <div key={idx} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",borderRadius:10,background:dark?"rgba(226,221,159,0.06)":"rgba(226,221,159,0.1)",border:`1px solid ${GOLD}30`}}>
                              {ec.thumbnail_url
                                ? <img src={ec.thumbnail_url} alt="" style={{width:40,height:24,objectFit:"cover",borderRadius:4,background:"#000",flexShrink:0}}/>
                                : <FilmIcon size={14} color={GOLD}/>}
                              <div style={{fontSize:12,fontWeight:600,color:C.text,maxWidth:240,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ec.title}</div>
                              <div style={{fontSize:10,fontWeight:800,color:"#111",background:GOLD,padding:"2px 7px",borderRadius:5,fontFamily:"'JetBrains Mono', monospace"}}>{mm}:{ss}</div>
                              <div className="btn" onClick={onRemove} style={{marginLeft:2,cursor:"pointer"}}><X size={11} color={C.textDim}/></div>
                            </div>
                          );
                        }
                        if (ec.type === "drill") {
                          return (
                            <div key={idx} style={{display:"flex",alignItems:"center",gap:9,padding:"6px 10px 6px 6px",borderRadius:10,background:dark?"rgba(226,221,159,0.06)":"rgba(226,221,159,0.1)",border:`1px solid ${GOLD}30`}}>
                              {ec.thumbnail_url
                                ? <img src={ec.thumbnail_url} alt="" style={{width:44,height:28,objectFit:"cover",borderRadius:5,background:"#000",flexShrink:0}}/>
                                : <Dumbbell size={14} color={GOLD} style={{marginLeft:4}}/>}
                              <div style={{minWidth:0}}>
                                <div style={{fontSize:12,fontWeight:700,color:C.text,maxWidth:220,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ec.title}</div>
                                {(ec.section||ec.category)&&(
                                  <div style={{fontSize:10,color:C.textDim,marginTop:1}}>{[sectionLabel(ec.section),ec.category].filter(Boolean).join(" · ")}</div>
                                )}
                              </div>
                              <div className="btn" onClick={onRemove} style={{marginLeft:2,cursor:"pointer"}}><X size={11} color={C.textDim}/></div>
                            </div>
                          );
                        }
                        if (ec.type === "feature") {
                          return (
                            <div key={idx} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",borderRadius:10,background:dark?"rgba(226,221,159,0.06)":"rgba(226,221,159,0.1)",border:`1px solid ${GOLD}30`}}>
                              <Link2 size={13} color={GOLD}/>
                              <div style={{fontSize:12,fontWeight:700,color:C.text,maxWidth:240,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ec.title}</div>
                              <div className="btn" onClick={onRemove} style={{marginLeft:2,cursor:"pointer"}}><X size={11} color={C.textDim}/></div>
                            </div>
                          );
                        }
                        // Legacy drill / generic shape
                        return (
                          <div key={idx} className="btn" style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:10,background:dark?"rgba(226,221,159,0.06)":"rgba(226,221,159,0.1)",border:`1px solid ${GOLD}30`,cursor:"default"}}>
                            <Play size={12} color={GOLD}/>
                            <div style={{fontSize:12,fontWeight:600,color:C.text}}>{ec.title}</div>
                            {ec.tag && <div style={{fontSize:10,color:GOLD,fontWeight:700,background:GOLD+"18",padding:"1px 6px",borderRadius:6}}>{ec.tag}</div>}
                            {ec.duration && <div style={{fontSize:10,color:C.textDim}}>{ec.duration}</div>}
                            <div className="btn" onClick={onRemove} style={{marginLeft:4}}><X size={11} color={C.textDim}/></div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* Quick action bar — chips horizontal-scroll on mobile */}
                  <div className="bam-composer-extras bam-chip-row" style={{padding:"0 22px 16px",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    {[
                      {label:"Drill Drop",prompt:"Share a drill you ran this week. What worked? What would you change?",icon:<Target size={13} color={GOLD}/>,bg:"rgba(226,221,159,0.06)"},
 {label:"Win",prompt:"Share a coaching win: big or small. What happened?",icon:<Zap size={13} color="#6EE7B7"/>,bg:"rgba(110,231,183,0.06)"},
                      {label:"Question",prompt:"Ask the community anything about coaching, drills, or development.",icon:<MessageCircle size={13} color="#93C5FD"/>,bg:"rgba(147,197,253,0.06)"},
                      {label:"Insight",prompt:"Share something you learned recently that changed how you coach.",icon:<Brain size={13} color="#C4B5FD"/>,bg:"rgba(196,181,253,0.06)"}
                    ].map(btn=>(
                      <div key={btn.label} title={btn.prompt} className="compose-action"
                        onClick={()=>{
                          setFocused(true);
                          // Toggle: clicking the active chip clears the type.
                          setComposeTag(prev => prev === btn.label ? null : btn.label);
                          setComposePlaceholder(btn.prompt);
                          if(composeRef.current)composeRef.current.focus();
                        }}
                        style={{display:"flex",alignItems:"center",gap:6,fontSize:12,
                          fontWeight:composeTag===btn.label?800:600,
                          color:composeTag===btn.label?"#111":C.textMid,
                          padding:"7px 14px",borderRadius:20,
                          border:`1px solid ${composeTag===btn.label?GOLD:C.border}`,
                          background:composeTag===btn.label?GOLD:btn.bg,
                          transition:"all .15s"}}>
                        {btn.icon}<span>{btn.label}</span>
                      </div>
                    ))}
                    <div style={{flex:1}}/>
                    {/* Point at a drill instead of describing one. Same embed
                        array the film moment uses, so the feed renders both
                        with one component. */}
                    <div className="compose-action" onClick={()=>setShowDrillPicker(true)}
                      title="Attach a drill from the library"
                      style={{display:"flex",alignItems:"center",gap:6,fontSize:12,fontWeight:600,color:C.textDim,padding:"7px 12px",borderRadius:20,border:`1px solid ${C.border}`,background:"transparent",cursor:"pointer"}}>
                      <Dumbbell size={13} color={C.textDim}/>
                      <span>Drill</span>
                    </div>
                    <div className="compose-action" onClick={()=>setShowFeaturePicker(true)}
                      title="Link a page on the platform"
                      style={{display:"flex",alignItems:"center",gap:6,fontSize:12,fontWeight:600,color:C.textDim,padding:"7px 12px",borderRadius:20,border:`1px solid ${C.border}`,background:"transparent",cursor:"pointer"}}>
                      <Link2 size={13} color={C.textDim}/>
                      <span>Page</span>
                    </div>
                    <div className="compose-action" onClick={()=>setShowFilmPicker(true)}
                      title="Attach a moment from one of your films"
                      style={{display:"flex",alignItems:"center",gap:6,fontSize:12,fontWeight:600,color:C.textDim,padding:"7px 12px",borderRadius:20,border:`1px solid ${C.border}`,background:"transparent",cursor:"pointer"}}>
                      <FilmIcon size={13} color={C.textDim}/>
                      <span>Film moment</span>
                    </div>
                    {attachments.length<3&&(
                      <div className="compose-action" onClick={()=>attachRef.current.click()} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,fontWeight:600,color:C.textDim,padding:"7px 12px",borderRadius:20,border:`1px solid ${C.border}`,background:"transparent"}}>
                        <Plus size={13} color={C.textDim}/>
                        <span>{attachments.length>0?`(${attachments.length}/3)`:"Media"}</span>
                      </div>
                    )}
                  </div>
                  {/* Post actions bar */}
                  {focused&&(
                    <div style={{padding:"12px 22px",borderTop:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:8,background:dark?"rgba(255,255,255,0.015)":"rgba(0,0,0,0.02)"}}>
                      <div style={{flex:1}}/>
                      <div className="btn" onClick={()=>{setFocused(false);setCompose("");setComposeTag(null);setAttachments([]);}} style={{fontSize:13,color:C.textMid,padding:"7px 18px",borderRadius:8,border:`1px solid ${C.border}`,fontWeight:600}}>Cancel</div>
                      <div className="btn" onClick={async()=>{
                        if(posting||(!compose.trim()&&!attachments.length)) return;
                        // The intro's starter lines, untouched, aren't an intro yet.
                        if(composeTag===INTRO_TAG&&compose.trim()===INTRO_STARTER.trim()&&!attachments.length) return;
                        const now=Date.now();
                        if(compose.trim()===lastPostRef.current.text&&now-lastPostRef.current.time<30000) return;
                        setPosting(true);
                        lastPostRef.current={text:compose.trim(),time:now};
                        try {
                          const text = compose.trim();
                          // Post type comes from the chip the coach picked
                          // (composeTag); fall back to a legacy "[Tag]" prefix
                          // for posts created before the chip-based composer
                          // shipped, then default to a plain insight.
                          let tag = composeTag || "Insight", content = text;
                          if (!composeTag) {
                            const prefixMatch = text.match(/^\[(Drill Drop|Win|Question|Insight)\]\s*(.*)$/s);
                            if (prefixMatch) { tag = prefixMatch[1]; content = prefixMatch[2]; }
                          }
                          const type = tag.toLowerCase().replace(" ","_");
                          const api = await import("./utils/communityApi");
                          // Upload any attached files to Storage now and
                          // turn them into embeds. The composer collected
                          // these as previews; previously they vanished
                          // on submit because createPost dropped them.
                          const attachmentEmbeds = [];
                          let attachFailed = 0;
                          for (const a of attachments) {
                            if (!a.file) continue;
                            try {
                              const { url, name, type: mime, size } = await api.uploadCommunityAttachment(a.file);
                              const m = (mime || a.type || "");
                              const embedType = m.startsWith("image/") ? "image"
                                : m.startsWith("video/") ? "video"
                                : "document";
                              attachmentEmbeds.push({ type: embedType, url, name, size, mime: m });
                            } catch (e) {
                              console.warn("Attachment upload failed:", e);
                              attachFailed++;
                            }
                          }
                          // Don't silently publish an empty post when the
                          // attachment(s) were the whole point and all failed.
                          if (attachFailed > 0 && attachmentEmbeds.length === 0 && !content.trim()) {
                            haptic("error");
 showApiError("Couldn't upload your attachment: check your connection and try again.");
                            setPosting(false);
                            return;
                          }
                          // Only coaches whose name survived editing.
                          const tagged = taggedCoaches.filter(t => (content || "").includes(t.token));
                          const created = await api.createPost({
                            type, content, tag,
                            embeds: [
                              ...(Array.isArray(embeddedContent) ? embeddedContent : []),
                              ...attachmentEmbeds,
                              ...tagged.map(t => ({ type: "mention", userId: t.id, name: t.token })),
                            ],
                            mentionIds: tagged.map(t => t.id),
                          });
                          haptic("success");
                          afterPost(created ? { ...created, tag: created.tag || tag } : null);
                          // Posted, but warn if some media didn't make it in.
                          if (attachFailed > 0) {
 showApiError(`Posted: but ${attachFailed} attachment${attachFailed>1?"s":""} failed to upload.`);
                          }
                          // Prepend the new post instead of refetching the whole
                          // feed — the full reload remounted every card (losing
                          // expanded state) and jumped the scroll position right
                          // after the satisfying "Post" moment.
                          if (created?.id) {
                            const newPost = {
                              id: created.id,
                              type: created.type || "post",
                              title: created.title,
                              content: created.content,
                              tag: created.tag,
                              media: [],
                              embeds: Array.isArray(created.embeds) ? created.embeds : [],
                              likes: 0,
                              replies: [],
                              author: appProfile.name || "You",
                              authorHandle: "",
                              authorRole: "",
                              authorAvatar: appPhoto || null,
                              userId: sbUser?.id || null,
                              time: "now",
                              pinnedAt: null,
                              hiddenAt: null,
                            };
                            setCommunityPosts(prev => [newPost, ...prev]);
                          } else {
                            const { fetchCommunityPosts } = await import("./utils/communityApi");
                            const posts = await fetchCommunityPosts({ limit: COMMUNITY_FEED_LIMIT });
                            setCommunityPosts(posts);
                            writeCommunityCache(posts);
                          }
 } catch(e) { haptic("error"); console.error("Post failed:", e); showApiError("Failed to post: try again."); }
                        setPosting(false);setFocused(false);setCompose("");setComposeTag(null);setAttachments([]);setEmbeddedContent([]);setTaggedCoaches([]);
                      }} style={{fontSize:13,color:"#1A1A1A",background:GOLD,padding:"7px 22px",borderRadius:8,fontWeight:800,boxShadow:`0 4px 14px ${GOLD}40`,opacity:(compose.trim()||attachments.length)&&!posting?1:0.45,pointerEvents:posting||(!compose.trim()&&!attachments.length)?"none":"auto"}}>
                        {posting?"Posting...":"Post"}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:22,flexWrap:"wrap"}}>
                  <div className="bam-chip-row" style={{display:"flex",gap:7,flexWrap:"wrap",flex:1}}>
                    {[...ALL_TAGS_COMM, "Mine"].map(tag=>(<div key={tag} className="tag-pill" onClick={()=>setActiveTag(tag)} style={{fontSize:12,fontWeight:activeTag===tag?800:600,padding:"6px 16px",borderRadius:20,border:`1px solid ${activeTag===tag?GOLD+"80":C.border}`,background:activeTag===tag?GOLD:"transparent",color:activeTag===tag?"#111":C.textMid,transition:"background .15s ease, color .15s ease, border-color .15s ease, box-shadow .15s ease",boxShadow:activeTag===tag?`0 2px 10px ${GOLD}30`:"none"}}>{tag}</div>))}
                  </div>
                  {/* Member Map — different concept from the post-type
                      filters next to it, so it visually separates itself
                      with the gold tint + map-pin icon. On phones the
                      label drops to just the icon to free row space; on
                      desktop the full "Member Map" label stays. */}
                  <div className="btn bam-member-map-chip"
                    onClick={memberMapOn ? ()=>setMapOpen(true) : undefined}
                    title={memberMapOn ? "Member Map" : "Member Map opens once we have enough plotted coaches"}
                    aria-label="Member Map"
                    style={{display:"flex",alignItems:"center",gap:7,padding:"6px 14px",borderRadius:20,
                      border:`1px solid ${GOLD}${memberMapOn?"55":"30"}`,
                      background:dark?"rgba(226,221,159,0.08)":"rgba(226,221,159,0.15)",
                      cursor:memberMapOn?"pointer":"default",
                      opacity:memberMapOn?1:0.55,
                      flexShrink:0,transition:"all .2s"}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="10" r="3"/><path d="M12 2a8 8 0 0 0-8 8c0 5.25 8 13 8 13s8-7.75 8-13a8 8 0 0 0-8-8z"/>
                    </svg>
                    <span className="bam-member-map-label" style={{fontSize:12,fontWeight:700,color:GOLD,letterSpacing:.4}}>Member Map</span>
                    {!memberMapOn && <span className="bam-member-map-label" style={{fontSize:8,fontWeight:800,color:GOLD,background:`${GOLD}15`,padding:"2px 6px",borderRadius:4,letterSpacing:.8,marginLeft:2}}>SOON</span>}
                  </div>
                </div>
                {/* Trending — only renders when there's something to rank.
                    Previously we showed a "No trending posts yet" empty
                    card here, which doubled the dead-vibe with the empty
                    Feed below it. Hiding the section entirely makes the
                    page feel intentionally light rather than broken. */}
                {trendingPosts.length >= 2 && (<>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
                  <div style={{width:28,height:28,borderRadius:8,background:"rgba(226,221,159,0.1)",display:"flex",alignItems:"center",justifyContent:"center"}}><Flame size={14} color={GOLD}/></div>
                  <span style={{fontSize:16,fontWeight:800,color:C.text,letterSpacing:.3}}>Trending</span>
                  <div style={{flex:1,height:1,background:C.border}}/>
                  <span style={{fontSize:12,color:C.textDim,fontWeight:500}}>Most active lately</span>
                </div>
                <div className="bam-trending-row" style={{marginBottom:28}}>
                  {trendingPosts.map((p,idx)=>{
                    const ac=getAC(p.author);
                    const initials=(p.author||"").split(/\s+/).filter(Boolean).slice(0,2).map(s=>s[0]).join("").toUpperCase()||"?";
                    const rankColors=["linear-gradient(135deg,#E2DD9F,#C8C47A)","linear-gradient(135deg,#C0C0C0,#9CA3AF)","linear-gradient(135deg,#CD7F32,#A0522D)"];
                    return (
                      <div key={p.id} className="tc btn bam-trending-card" onClick={()=>openPostInFeed(p.id)} style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:16,padding:20,boxShadow:`${C.surfaceShadow}, ${C.innerGlow}`,position:"relative",minWidth:0,cursor:"pointer",textAlign:"left"}}>
                        <div style={{position:"absolute",top:-1,right:18,width:28,height:34,borderRadius:"0 0 8px 8px",background:rankColors[idx]||rankColors[2],display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 4px 12px rgba(0,0,0,0.25)`}}>
                          <span style={{fontSize:13,fontWeight:900,color:"#111"}}>#{idx+1}</span>
                        </div>
                        <div style={{display:"flex",gap:10,marginBottom:12}}>
                          <div style={{width:34,height:34,borderRadius:"50%",background:ac,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"#fff",boxShadow:`0 2px 8px ${ac}55`,overflow:"hidden",flexShrink:0}}>
                            {p.authorAvatar?<img src={p.authorAvatar} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>:initials}
                          </div>
                          <div style={{minWidth:0}}><div style={{fontSize:14,fontWeight:700,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.author}</div><div style={{fontSize:11,color:C.textDim}}>{p.time}</div></div>
                        </div>
                        <div style={{fontSize:13,color:C.textMid,lineHeight:1.65,marginBottom:14,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{p.content}</div>
                        <div style={{display:"flex",gap:14,alignItems:"center",paddingTop:12,borderTop:`1px solid ${C.border}`}}>
                          <span style={{fontSize:12,color:dark?GOLD:"#111",display:"flex",alignItems:"center",gap:5,fontWeight:700}}><Ic.Heart c={dark?GOLD:"#111"} s={13} f/>{p.likes}</span>
                          <span style={{fontSize:12,color:C.textDim,display:"flex",alignItems:"center",gap:5}}><Ic.Msg c={C.textDim} s={12}/>{Array.isArray(p.replies)?p.replies.length:0}</span>
                          <div style={{marginLeft:"auto",fontSize:10,fontWeight:700,color:"#111",background:GOLD,padding:"3px 10px",borderRadius:12}}>{p.tag}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                </>)}

                {/* ── Group Calls Widget ── */}
                {(()=>{
                  const calls=getAllUpcomingCallsMulti(visibleCalls);
                  const next=calls[0];
                  if(!next) return null;
                  const nextDate=new Date(next.dateTime);
                  const diff=next.dateTime-now;
                  const calMonth=nextDate.getMonth();
                  const calYear=nextDate.getFullYear();
                  const firstDay=new Date(calYear,calMonth,1).getDay();
                  const daysInMonth=new Date(calYear,calMonth+1,0).getDate();
                  const callDays=new Set(calls.filter(c=>{const d=new Date(c.dateTime);return d.getMonth()===calMonth&&d.getFullYear()===calYear;}).map(c=>new Date(c.dateTime).getDate()));
                  const today=new Date();
                  const todayDate=today.getMonth()===calMonth&&today.getFullYear()===calYear?today.getDate():-1;
                  return (
                    <div style={{marginBottom:20}}>
                      {/* Collapsed row */}
                      <div onClick={()=>setCallsOpen(o=>!o)} style={{background:C.cardGradient,border:`1px solid ${callsOpen?GOLD+"40":C.cardBorder}`,borderRadius:14,padding:"10px 16px",display:"flex",alignItems:"center",gap:10,cursor:"pointer",transition:"border-color .15s",boxShadow:`${C.surfaceShadow}, ${C.innerGlow}`}}>
                        <CalendarDays size={14} color={GOLD}/>
                        <span style={{fontSize:13,fontWeight:600,color:C.text,flex:1}}>Next Call: {fmtDayInTz(next.dateTime,settingsTz)} {fmtTimeInTz(next.dateTime,settingsTz)}</span>
                        {diff>0
                          ?<span style={{fontSize:12,color:GOLD,fontWeight:700}}>{fmtCountdown(diff)}</span>
                          :<span style={{fontSize:11,fontWeight:800,color:"#fff",background:"#E06060",padding:"3px 10px",borderRadius:20,display:"inline-flex",alignItems:"center",gap:5,animation:"livePulse 1.5s ease-in-out infinite"}}>
                            <span style={{width:6,height:6,borderRadius:"50%",background:"#fff",display:"inline-block"}}/>LIVE NOW
                          </span>
                        }
                        <ChevronDown size={14} color={C.textDim} style={{transform:callsOpen?"rotate(180deg)":"none",transition:"transform .2s"}}/>
                      </div>
                      {/* Expanded panel */}
                      {callsOpen&&(
                        <div style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderTop:"none",borderRadius:"0 0 14px 14px",padding:16,marginTop:-1}}>
                          {/* Next call details + Join */}
                          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14,padding:"10px 14px",background:`${GOLD}0A`,borderRadius:8,border:`1px solid ${GOLD}20`}}>
                            <Video size={16} color={GOLD}/>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:13,fontWeight:700,color:C.text}}>{next.title}</div>
                              <div style={{fontSize:11,color:C.textDim}}>
                                {fmtFullDateInTz(nextDate,settingsTz)} at {fmtTimeInTz(nextDate,settingsTz)}
                              </div>
                            </div>
                            <a href={next.meetLink} target="_blank" rel="noopener noreferrer" onClick={e=>{e.stopPropagation();e.preventDefault();openExternal(next.meetLink);}}
                              style={{display:"inline-flex",alignItems:"center",gap:5,background:GOLD,color:"#111",padding:"6px 14px",borderRadius:7,fontSize:12,fontWeight:700,textDecoration:"none",fontFamily:"'DM Sans',sans-serif",flexShrink:0}}>
                              Join <Video size={11}/>
                            </a>
                          </div>
                          {/* Mini calendar */}
                          <div style={{fontSize:11,fontWeight:700,color:C.textDim,marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
                            <CalendarDays size={11} color={C.textDim}/>
                            {new Date(calYear,calMonth).toLocaleDateString("en-US",{month:"long",year:"numeric"})}
                          </div>
                          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:1,textAlign:"center"}}>
                            {["S","M","T","W","T","F","S"].map((d,i)=>(
                              <div key={i} style={{fontSize:9,color:C.textDim,fontWeight:700,padding:2}}>{d}</div>
                            ))}
                            {Array.from({length:firstDay}).map((_,i)=><div key={`e${i}`}/>)}
                            {Array.from({length:daysInMonth}).map((_,i)=>{
                              const day=i+1;
                              const hasCall=callDays.has(day);
                              const isToday=day===todayDate;
                              return (
                                <div key={day} style={{padding:"4px 0",fontSize:11,borderRadius:6,fontWeight:hasCall||isToday?700:400,
                                  color:hasCall?"#111":isToday?GOLD:C.textMid,
                                  background:hasCall?GOLD:isToday?`${GOLD}18`:"transparent"}}>
                                  {day}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Quiet section header — the old sparkle box + "Live" pulse
                    read as debug chrome, not product. */}
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
                  <span style={{fontSize:16,fontWeight:800,color:C.text,letterSpacing:.3}}>Feed</span>
                  <div style={{flex:1,height:1,background:C.border}}/>
                </div>
                {(()=>{
                  // Render branches:
                  //   1. Loading + nothing yet → globe loader (so the empty
                  //      state doesn't flash before posts arrive).
                  //   2. Loaded + has posts → the feed.
                  //   3. Loaded + empty → "THE FLOOR IS YOURS" empty state.
                  if (communityLoading && communityPosts.length === 0) {
                    return <CommunityLoader/>;
                  }
                  // Fetch failed (vs. genuinely empty) → distinct retry state.
                  if (communityError && communityPosts.length === 0) {
                    return (
                      <div style={{background:C.cardGradient,border:`1px dashed ${C.cardBorder}`,borderRadius:16,padding:"48px 28px",textAlign:"center"}}>
                        <MessageCircle size={32} color={GOLD} style={{margin:"0 auto 14px",opacity:0.35}}/>
                        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:22,color:C.text,letterSpacing:2,marginBottom:6}}>COULDN'T LOAD THE FEED</div>
                        <div style={{fontSize:13,color:C.textDim,maxWidth:380,margin:"0 auto 18px",lineHeight:1.6}}>
                          Check your connection and try again.
                        </div>
                        <button onClick={loadCommunity} style={{padding:"10px 24px",borderRadius:9,background:GOLD,color:"#111",border:"none",fontFamily:"'Bebas Neue',sans-serif",fontSize:15,letterSpacing:1.2,cursor:"pointer"}}>RETRY</button>
                      </div>
                    );
                  }
                  // "Mine" filters by author rather than by tag — a different
                  // axis to the rest of the pills, and the fastest way back to
                  // a post you wrote and the replies on it.
                  const byTag = activeTag==="All"
                    ? communityPosts
                    : activeTag==="Mine"
                      ? communityPosts.filter(p=>p.userId && p.userId===sbUser?.id)
                      : communityPosts.filter(p=>p.tag===activeTag);
                  const filtered = byTag.filter(p=>!communityBlocked.includes(p.userId));
                  return filtered.length > 0 ? <div className="bam-stagger" style={{animation:"bamScreenIn .25s ease"}}>{filtered.map((p,i)=><div key={p.id} id={`bam-post-${p.id}`}><PostCard p={p} C={C} dark={dark} me={meForPost} onTagClick={onPostTagClick} activeTag={activeTag==="All"?null:activeTag} onProfileClick={setProfileName} showLikeHint={i===0} onDelete={p.userId && p.userId===sbUser?.id ? onPostDelete : undefined} onAdminChanged={onPostAdminChanged} initiallyLiked={myLikedIds.has(p.id)} initiallyBookmarked={myBookmarkIds.has(p.id)}/></div>)}
                    {communityHasMore && (
                      <div style={{display:"flex",justifyContent:"center",padding:"6px 0 18px"}}>
                        <button type="button" onClick={loadMoreCommunity} disabled={communityLoadingMore}
                          style={{padding:"10px 22px",borderRadius:9,border:`1px solid ${C.border}`,background:"transparent",color:communityLoadingMore?C.textDim:C.text,
                            fontFamily:"'Bebas Neue',sans-serif",fontSize:15,letterSpacing:1.2,cursor:communityLoadingMore?"default":"pointer"}}>
                          {communityLoadingMore ? "LOADING…" : "OLDER POSTS"}
                        </button>
                      </div>
                    )}
                  </div> : (
                    <div style={{background:C.cardGradient,border:`1px dashed ${C.cardBorder}`,borderRadius:16,padding:"48px 28px",textAlign:"center"}}>
                      <MessageCircle size={32} color={GOLD} style={{margin:"0 auto 14px",opacity:0.35}}/>
                      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:22,color:C.text,letterSpacing:2,marginBottom:6}}>THE FLOOR IS YOURS</div>
                      <div style={{fontSize:13,color:C.textDim,maxWidth:380,margin:"0 auto",lineHeight:1.6}}>
                        No posts yet. Share a drill you ran this week, a win from practice, or a question you're working through. This community is built by coaches like you.
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            </PullToRefresh>
          )}

          {/* Dashboard */}
          {nav==="dashboard"&&(
            <PullToRefresh onRefresh={async () => {
              // Re-pull session (refresh token) and let DashIntro / cards
              // re-render. The visual feedback + haptic + 350ms hold gives
              // the coach the sense that something refreshed even when
              // the underlying data hasn't changed.
              try { await supabase.auth.getSession(); } catch {}
              await new Promise(r => setTimeout(r, 350));
            }}>
            <div className="bam-content-scroll" style={{flex:1,overflow:"auto",overflowX:"hidden",padding:"28px 28px 40px",position:"relative"}}>
              {dashIntro&&<DashIntro dark={dark}/>}

              {/* ── Band one: wide. One decision. ──────────────────────────
                  Greeting, the one thing to do next, then three doors.

                  What used to be here: StartHere, the pick-up row (lab nudge
                  + unexplored suggestion + certification), a Learning Lab
                  challenge, the greeting, the weekly Q and A strip, an
                  inline call strip, a trial celebration banner, the "how do
                  you want to get better today" headline over four category
                  cards, a complete-your-profile banner and an add-your-
                  location nudge. Eighteen blocks could render on this page
                  and eight of them were notices; three sat ABOVE the coach's
                  own name. The feedback that started this was the word
                  "overwhelming".

                  Every one of those notices still exists. They are ranked
                  candidates for a single slot now (constants/nextAction.js),
                  so the page shows one, with a deck behind it when there are
                  others. The four category cards became three doors whose
                  items are the destination, so "Build a plan" is one tap
                  rather than two. */}
              <div style={{maxWidth:1120,margin:"0 auto",display:"flex",flexDirection:"column",gap:isMobile?20:28}}>

                {/* THE GREETING. It was 16px of grey text floating above
                    everything, an orphan. Now it is the headline of the page,
                    with one line under it of things that are actually true
                    today: the date, and the next call if there is one this
                    week. Nothing invented, nothing that needs a query this
                    page was not already making. */}
                <div className={dashIntro?"dash-intro-item":""} style={{animationDelay:"0.45s"}}>
                  {userName===null?(
                    <div style={{height:isMobile?34:44,display:"flex",alignItems:"center",gap:8}}>
                      <svg width="32" height="32" viewBox="0 0 32 32" style={{opacity:0.7}}>
                        <style>{`@keyframes dnaL{0%{transform:translateY(0)}50%{transform:translateY(-6px)}100%{transform:translateY(0)}}`}</style>
                        {[0,1,2,3,4].map(i=>(
                          <circle key={i} cx={4+i*6} cy={16} r="2" fill={GOLD}
                            style={{animation:`dnaL 1.2s ease-in-out ${i*0.15}s infinite`}}/>
                        ))}
                      </svg>
                    </div>
                  ):(
                    <>
                      <div style={{fontSize:isMobile?27:38,fontWeight:800,color:C.text,
                        letterSpacing:isMobile?-0.6:-1,lineHeight:1.1}}>
                        {getGreeting()}, {userName}.
                      </div>
                      <div style={{display:"flex",flexWrap:"wrap",alignItems:"center",gap:"4px 10px",
                        marginTop:isMobile?7:9,fontSize:isMobile?13.5:14.5,color:C.textMid}}>
                        <span>{todayLine}</span>
                        {nextCallLine&&(<>
                          <span aria-hidden="true" style={{opacity:.5}}>&middot;</span>
                          <span>{nextCallLine}</span>
                        </>)}
                      </div>
                    </>
                  )}
                </div>

                {/* This week's drop is the front card of Next Up until it's opened or
                    closed with its X, the rest of the deck tucked under it (Coleman,
                    2026-10-22). Once it's opened or closed, a quiet "next drop" line. */}
                {!dropAction && latestDrop && (
                  <div className={dashIntro?"dash-intro-item":""} style={{animationDelay:"0.48s"}}>
                    <DropNextLine C={C} drop={latestDrop} go={go} opened={dropSeenId === latestDrop.id} thisWeek={!!currentDrop([latestDrop])}/>
                  </div>
                )}

                {nextActions.length>0&&(
                  <div className={dashIntro?"dash-intro-item":""} style={{animationDelay:"0.5s"}}>
                    <NextUp actions={nextActions} C={C} dark={dark} compact={isMobile}
                      onGo={doNextAction} onSkip={skipAction}
                      renderFront={(a) => a.kind === "drop" && latestDrop
                        ? <DropCard C={C} drop={latestDrop} go={go} narrow={isMobile} pending flush thisWeek={!!currentDrop([latestDrop])} onClose={() => closeDropReminder(a)}/>
                        : null}/>
                  </div>
                )}

                <div className={dashIntro?"dash-intro-item":""} style={{animationDelay:"0.55s"}}>
                  <ActionPaths C={C} dark={dark} go={go} narrow={isMobile}
                    community={{
                      // `content` and `author`, which is what a post is. The
                      // first version read `title` and `author_name`, neither
                      // of which exists, so the card said "3 new today" over
                      // an empty space.
                      count: trendingPosts.length,
                      top: trendingPosts[0]?.content || null,
                      by: trendingPosts[0]?.author || null,
                      avatar: trendingPosts[0]?.authorAvatar || null,
                      // What the peek rotates through, and opens on a tap.
                      posts: trendingPosts.slice(0, 5).filter(p => p && p.content).map(p => ({
                        id: p.id, text: p.content, by: p.author || null, avatar: p.authorAvatar || null,
                      })),
                      onOpen: openPostInFeed,
                    }}/>
                </div>
              </div>

              {/* ── Band two: today. ─────────────────────────────────────────
                  It was a narrower 820 column, so its left edge sat 150px in
                  from the bands above and below and the page read as three
                  pieces. Now the same 1120 and the same three columns as the
                  doors above: the week's workout across two, the day's set
                  and drill stacked in the third (Coleman, from the canvas,
                  2026-10-07). On a phone the workout leads and the two
                  singles follow it. */}
              <div style={{maxWidth:1120,margin:`${isMobile?48:88}px auto 0`}}>

              {/* ONE HEADER GRAMMAR. Every section on the home page names
                  itself the same way: Bebas, then a hairline. */}
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
                <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:isMobile?22:26,
                  letterSpacing:1.4,color:C.text,lineHeight:1}}>Today's selection</span>
                <div style={{flex:1,height:1,background:C.border}}/>
              </div>


              <div className={`bam-today ${dashIntro?"dash-intro-item":""}`} style={{animationDelay:"0.52s",display:"grid",
                gridTemplateColumns:isMobile?"minmax(0,1fr)":"repeat(3, minmax(0, 1fr))",gap:isMobile?14:18,alignItems:"stretch"}}>
                <div style={{minWidth:0,gridColumn:isMobile?undefined:"span 2"}}>
                  <WorkoutOfWeek C={C} dark={dark} narrow={isMobile}
                    onOpenDrill={openDrillItem}
                    onBuildPlan={() => go("plans")}
                    onBuildCustom={() => setShowWorkoutBuilder(true)}/>
                </div>
                <div className="bam-today-side" style={{minWidth:0,display:"grid",gridAutoRows:"1fr",gap:isMobile?14:18}}>
                  {/* Set of the day: team coaches, or anyone who switched it
                      on. Renders nothing otherwise, and the drill then stands
                      up to fill the column (.bam-dotd:only-child). */}
                  <SetOfDayCard C={C} go={go}/>
                  <DrillOfDay C={C} dark={dark} onOpen={openDrillItem}/>
                </div>
              </div>

              <div className={dashIntro?"dash-intro-item":""} style={{margin:`${isMobile?32:40}px 0 28px`,animationDelay:"0.56s"}}>
                <ThisWeeksDrops C={C} dark={dark} go={go} onOpen={openDrillItem} narrow={isMobile}/>
              </div>

              {/* Prompt 6: Announcements — hidden if none */}
              {visibleAnnouncements.length>0&&(
                <div className={dashIntro?"dash-intro-item":""}  style={{marginBottom:28,animationDelay:"0.62s"}}>
                  <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:14}}>
                    <span style={{fontSize:16}}>📢</span>
                    <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:isMobile?22:26,letterSpacing:1.4,color:C.text,lineHeight:1}}>Announcements</span>
                    <div style={{flex:1,height:1,background:C.border}}/>
                  </div>
                  <div style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:14,overflow:"hidden",boxShadow:`${C.surfaceShadow}, ${C.innerGlow}`}}>
                    {visibleAnnouncements.map((a,i)=>{
                      const isOpen=expandedAnn===a.id;
                      return (
                        <div key={a.id} style={{borderBottom:i<visibleAnnouncements.length-1?`1px solid ${C.border}`:"none"}}>
                          <div onClick={()=>setExpandedAnn(isOpen?null:a.id)}
                            style={{display:"flex",alignItems:"center",gap:10,padding:"14px 18px",cursor:"pointer",minHeight:44}}>
                            <div style={{flex:1,fontSize:14,fontWeight:700,color:C.text}}>{a.title}</div>
                            <span style={{fontSize:11,color:C.textDim,flexShrink:0}}>{a.date}</span>
                            <ChevronDown size={14} color={C.textDim} style={{transition:"transform .2s",transform:isOpen?"rotate(180deg)":"rotate(0)"}}/>
                          </div>
                          {isOpen&&(
                            <div style={{padding:"0 18px 14px",display:"flex",alignItems:"flex-start",gap:12}}>
                              <div style={{flex:1,fontSize:13,color:C.textMid,lineHeight:1.6}}>{a.body}</div>
                              <button onClick={(e)=>{e.stopPropagation();dismissAnnouncement(a.id);}}
                                style={{padding:"5px 12px",borderRadius:7,border:`1px solid ${C.border}`,background:"transparent",
                                  color:C.textDim,fontSize:11,fontWeight:700,cursor:"pointer",flexShrink:0,minHeight:44,display:"flex",alignItems:"center"}}>
                                Dismiss
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              </div>{/* ── end band two ── */}

              {/* ── Band three: two-up, and quieter again. ─────────────────
                  Community and Bookmarks are both "browse when you have
                  time". Neither is urgent, so they share a row rather than
                  each taking a full-width turn at the bottom of the page. */}
              <div style={{maxWidth:1120,margin:`${isMobile?48:88}px auto 0`}}>
              {/* ── Community, one block ─────────────────────────────────
                  It used to be three: a full-width Trending row, then Recent
                  Community Posts, then a Leaderboard beside them, each with
                  its own header at the same size. Three headers for one
                  place. Now one header, one list (the posts with the most
                  going on first, marked, then the newest), and the
                  leaderboard alongside. */}
              {(() => {
                const trendingIds = new Set(trendingPosts.slice(0, 2).map(p => p.id));
                const posts = [
                  ...trendingPosts.slice(0, 2),
                  ...communityPosts.filter(p => !trendingIds.has(p.id)),
                ].slice(0, 4);
                return (
              <div className={`bam-dash-bottom bam-dash-community ${dashIntro?"dash-intro-item":""}`} style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) 302px",gap:24,animationDelay:"0.85s"}}>
                <div style={{minWidth:0}}>
                  {/* The header is the door: tapping it opens the feed, and
                      each post opens itself there (2026-05-05: the preview
                      cards looked clickable and went nowhere). */}
                  <div onClick={() => go("community")}
                    style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,cursor:"pointer"}}>
                    <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:isMobile?22:26,letterSpacing:1.4,color:C.text,lineHeight:1}}>Community</span>
                    <div style={{flex:1,height:1,background:C.border}}/>
                    <span style={{fontSize:11,fontWeight:700,color:GOLD,letterSpacing:.6,display:"inline-flex",alignItems:"center",gap:3}}>
                      View all <ArrowRight size={11}/>
                    </span>
                  </div>
                  {posts.length > 0 ? posts.map(p => (
                    <div key={p.id} onClick={() => openPostInFeed(p.id)} style={{cursor:"pointer",position:"relative"}}>
                      {trendingIds.has(p.id) && (
                        <div style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:10,fontWeight:800,
                          letterSpacing:1,textTransform:"uppercase",color:C.textMid,margin:"0 0 6px 2px"}}>
                          <Flame size={11} color={GOLD}/> Trending
                        </div>
                      )}
                      <PostCard p={p} C={C} dark={dark} compact me={meForPost} onProfileClick={setProfileName}/>
                    </div>
                  )) : (
                    <div onClick={() => go("community")}
                      style={{background:C.cardGradient,border:`1px dashed ${C.cardBorder}`,borderRadius:14,padding:"32px 20px",textAlign:"center",cursor:"pointer"}}>
                      <MessageCircle size={22} color={GOLD} style={{margin:"0 auto 8px",opacity:0.4}}/>
                      <div style={{fontSize:13,fontWeight:600,color:C.text,marginBottom:4}}>No posts yet</div>
                      <div style={{fontSize:11,color:C.textDim}}>Be the first to share a drill drop, win, or insight.</div>
                    </div>
                  )}
                </div>
                <div>
                  {/* Leaderboard — empty state when no coaches */}
                  <div ref={ptsRef} style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:14,padding:20,marginBottom:16,boxShadow:`${C.surfaceShadow}, ${C.innerGlow}`}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}><div style={{width:26,height:26,borderRadius:7,background:"rgba(226,221,159,0.08)",display:"flex",alignItems:"center",justifyContent:"center"}}><Trophy size={13} color={GOLD}/></div><span style={{fontSize:13,fontWeight:800,color:C.textMid,letterSpacing:.8,textTransform:"uppercase"}}>Leaderboard</span></div>
                    {communityLeaderboard.length > 0 ? communityLeaderboard.slice(0,5).map((c,i)=>{ const ac=getAC(c.name); return (<div key={c.id} style={{display:"flex",alignItems:"center",gap:11,padding:"12px 0",borderBottom:i<Math.min(communityLeaderboard.length,5)-1?`1px solid ${C.border}`:"none"}}><div style={{fontSize:13,color:i===0?GOLD:C.textDim,width:16,textAlign:"center",fontWeight:800}}>{i===0?"★":i+1}</div><div style={{width:30,height:30,borderRadius:"50%",background:ac,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:"#fff",boxShadow:`0 2px 6px ${ac}55`}}>{(c.name||"").split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2)}</div><div style={{flex:1,minWidth:0,cursor:"pointer"}} onClick={()=>setProfileName(c.name)}><UserNameWithBadges profile={c} size="sm" style={{fontSize:14,fontWeight:600,color:C.text}}/><div style={{fontSize:11,color:C.textDim,marginTop:1}}>{c.posts} posts</div></div></div>); }) : (
                      <div style={{textAlign:"center",padding:"16px 0",color:C.textDim,fontSize:12}}>
                        <Trophy size={20} color={GOLD} style={{margin:"0 auto 6px",opacity:0.3}}/>
                        <div>Leaderboard populates as coaches engage.</div>
                      </div>
                    )}
                  </div>
                  <div style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:14,padding:20,boxShadow:`${C.surfaceShadow}, ${C.innerGlow}`}}>
                    <div style={{fontSize:13,fontWeight:800,marginBottom:16,color:C.textMid,letterSpacing:.8,textTransform:"uppercase"}}>👤 Your Stats</div>
                    {[
                      ["Posts",String(userStats.posts),()=>{ if(meForPost?.name) setProfileName(meForPost.name); else go("community"); }],
                      ["Training Plans Made",String(userStats.plans),()=>go("plans")],
                      ["Comments Left",String(userStats.comments),()=>go("community")],
                      ["Time on Platform",formatTimeOnPlatform(userStats.timeSeconds),null],
                      ["Day Streak",String(userStats.streak),null],
                    ].map(([l,v,onTap],i,a)=>(
                      <div key={l} onClick={onTap||undefined} className={onTap?"btn":undefined}
                        title={onTap?`Open ${l==="Posts"?"your posts":l==="Training Plans Made"?"your plans":"the community"}`:undefined}
                        style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"11px 0",minHeight:44,cursor:onTap?"pointer":"default",borderBottom:i<a.length-1?`1px solid ${C.border}`:"none"}}>
                        <span style={{fontSize:14,color:C.textMid}}>{l}</span>
                        {statsLoaded?(
                          <span style={{fontSize:24,fontWeight:800,color:dark?GOLD:"#111"}}>{v||"0"}</span>
                        ):(
                          <div style={{width:40,height:24,borderRadius:6,background:dark?"rgba(255,255,255,0.06)":"#e8e8e8",overflow:"hidden",position:"relative"}}>
                            <div style={{position:"absolute",inset:0,background:`linear-gradient(90deg,transparent,${dark?"rgba(255,255,255,0.06)":"rgba(255,255,255,0.7)"},transparent)`,animation:"bamShimmer 1.5s ease-in-out infinite"}}/>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
                );
              })()}

              {/* ── My Bookmarks ─────────────────────────────────── */}
              <div className={dashIntro?"dash-intro-item":""} style={{marginTop:24,animationDelay:"1.25s"}}>
                <div style={{display:"flex",alignItems:"center",gap:9,marginBottom:14}}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill={GOLD} stroke={GOLD} strokeWidth="1.2">
                    <path d="M3.5 2.5h9a.5.5 0 0 1 .5.5v11l-5-3-5 3v-11a.5.5 0 0 1 .5-.5z" strokeLinejoin="round"/>
                  </svg>
                  <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:isMobile?22:26,letterSpacing:1.4,color:C.text,lineHeight:1}}>My Bookmarks</div>
                </div>
                {favDrills.length===0?(
                  <div style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,borderRadius:16,padding:"36px 28px",textAlign:"center",boxShadow:`${C.surfaceShadow}, ${C.innerGlow}`}}>
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" style={{margin:"0 auto 16px",display:"block"}}>
                      <path d="M14 6h20a2 2 0 0 1 2 2v34l-12-8-12 8V8a2 2 0 0 1 2-2z" stroke={GOLD+"66"} strokeWidth="1.5" fill={GOLD+"10"} strokeLinejoin="round"/>
                    </svg>
                    <div style={{fontSize:15,fontWeight:700,color:C.text,marginBottom:6}}>Build your practice prep sheet</div>
 <div style={{fontSize:13,color:C.textDim,lineHeight:1.7,maxWidth:360,margin:"0 auto 18px"}}>Bookmark drills as you browse: they'll show up here on your dashboard and inside the practice-plan builder for quick access. Organize into Collections by focus, team, or week.</div>
                    <div className="btn" onClick={()=>go("pd")} style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:13,fontWeight:700,color:"#111",background:GOLD,padding:"9px 20px",borderRadius:8,boxShadow:`0 4px 14px ${GOLD}40`}}>
                      Browse Drills <ChevronRight size={14}/>
                    </div>
                  </div>
                ):(
                  <>
                    {/* Practice / Learning bucket toggle. Practice = drills
                        & games + movement + workouts (usable in plan
                        builder). Learning = insights + X&O + masterclasses. */}
                    {(()=>{
                      const PRACTICE_SECTIONS = new Set(["pd","team","movement","workouts"]);
                      const practiceCount = favDrills.filter(f => PRACTICE_SECTIONS.has(f.sectionId)).length;
                      const learningCount = favDrills.length - practiceCount;
                      return (
                        <div style={{display:"flex",gap:6,marginBottom:12}}>
                          {[
                            {id:"practice",label:`Practice drills (${practiceCount})`},
                            {id:"learning",label:`Learning (${learningCount})`},
                          ].map(t => {
                            const on = favBucket === t.id;
                            return (
                              <button key={t.id} onClick={()=>{setFavBucket(t.id);setFavListFilter("all");}}
                                style={{padding:"7px 14px",borderRadius:9,fontSize:11.5,fontWeight:800,letterSpacing:.3,cursor:"pointer",
                                  border:`1px solid ${on?GOLD:C.border}`,
                                  background:on?`${GOLD}18`:"transparent",
                                  color:on?GOLD:C.textMid,transition:"all .15s"}}>
                                {t.label}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}
                    <FavListsToolbar
                      C={C} dark={dark}
                      lists={favLists}
                      filter={favListFilter} setFilter={setFavListFilter}
                      sort={favSort} setSort={setFavSort}
                      onCreateList={(name)=>createFavList(name)}
                      onRenameList={renameFavList}
                      onDeleteList={deleteFavList}
                      favCount={favDrills.length}
                      membership={favListMembership}
                    />
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:12}}>
                      {(() => {
                        const PRACTICE_SECTIONS = new Set(["pd","team","movement","workouts"]);
                        // Bucket filter (practice vs learning)
                        let arr = favDrills.filter(f => favBucket === "practice"
                          ? PRACTICE_SECTIONS.has(f.sectionId)
                          : !PRACTICE_SECTIONS.has(f.sectionId));
                        // Filter by collection
                        if (favListFilter !== "all") {
                          arr = arr.filter(f => (favListMembership[f.key] || []).includes(favListFilter));
                        }
                        // Sort
                        const by = favSort;
                        const sorted = [...arr].sort((a,b) => {
                          if (by === "title") return (a.title||"").localeCompare(b.title||"");
                          if (by === "section") return (a.sectionId||"").localeCompare(b.sectionId||"");
                          const ta = new Date(a.savedAt||0).getTime();
                          const tb = new Date(b.savedAt||0).getTime();
                          return by === "oldest" ? ta - tb : tb - ta;
                        });
                        return sorted;
                      })().map(item => {
                        const thumbColors = SECTION_COLORS[item.sectionId]||SECTION_COLORS.pd;
                        const tc = thumbColors[item.id % thumbColors.length];
                        const memberLists = favListMembership[item.key] || [];
                        return (
                          <FavCard
                            key={item.key}
                            item={item}
                            tc={tc}
                            C={C}
                            dark={dark}
                            memberLists={memberLists}
                            lists={favLists}
                            onRemove={()=>setFavDrills(p=>{const next=p.filter(f=>f.key!==item.key);import("./utils/userData").then(({saveUserData})=>saveUserData("fav_drills",next)).catch(()=>{});return next;})}
                            onToggleList={(listId)=>toggleFavInList(item.key, listId)}
                            onCreateList={(name)=>{const id=createFavList(name); if(id) toggleFavInList(item.key, id);}}
                            onPlay={()=>{
                              // Deep-link into the item's section and auto-open
                              // it there. ContentPage consumes the key once its
                              // drill list hydrates (same pattern as
                              // bam_open_module). XO has its own page with no
                              // detail panel — just navigate.
                              logEvent("bookmark_open",{contentId:item.id,section:item.sectionId,title:item.title});
                              if(item.sectionId !== "xo"){
                                try{sessionStorage.setItem("bam_open_drill",JSON.stringify({sectionId:item.sectionId,id:item.id}));}catch{}
                              }
                              go(item.sectionId);
                            }}
                          />
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
              </div>{/* ── end band three ── */}
            </div>
            </PullToRefresh>
          )}

          {/* Resources */}
          {nav==="resources"&&(
            <Suspense fallback={<LazyFallback/>}>
              <ResourcesPage C={C} dark={dark} dismissedBanners={dismissedBanners} dismissBanner={dismissBanner} go={go} certPurchased={certPurchased} certProgress={certProgress} switchMode={switchMode}/>
            </Suspense>
          )}

          {/* The Wednesday Drop: this week's, one by id, and the archive. */}
          {(nav==="drop"||nav==="drops"||nav.startsWith("drops/"))&&(
            <Suspense fallback={<LazyFallback/>}>
              <DropRoute C={C} nav={nav} go={go} openPost={openPostInFeed}/>
            </Suspense>
          )}

          {/* Research collections. One route prefix, three views: the
              index, a collection's landing page, and one study with the
              rest of the collection either side of it. */}
          {nav.startsWith("research")&&(()=>{
            const route = parseResearchRoute(nav);
            if (!route) return null;
            return (
              <Suspense fallback={<LazyFallback/>}>
                {route.view === "index" && <ResearchCollectionsPage C={C} dark={dark}/>}
                {route.view === "collection" && <ResearchCollectionPage slug={route.slug} C={C} dark={dark}/>}
                {route.view === "study" && (
                  <ResearchStudyPage slug={route.slug} displayIndex={route.displayIndex} C={C} dark={dark}/>
                )}
              </Suspense>
            );
          })()}

          {/* Playbook Builder */}
          {nav==="playbook"&&(
            <Suspense fallback={<LazyFallback/>}>
              <PlaybookBuilder C={C} dark={dark} favDrills={favDrills} toggleFav={toggleFav} videoNotes={videoNotes} saveVideoNotes={saveVideoNotes}/>
            </Suspense>
          )}

          {/* Settings */}
          {nav==="settings"&&(
            <SettingsPage C={C} dark={dark} toggleDark={toggleDark}
              notifs={settingsNotifs} updateNotif={updateNotifSetting}
              tz={settingsTz} updateTz={updateTz}
              mapVisible={mapVisible} toggleMapVisible={toggleMapVisible}
              dismissBanner={dismissBanner} dismissedBanners={dismissedBanners}
              emailPrefs={emailPrefs} updateEmailPref={updateEmailPref}
              onSignOut={handleSignOut}
              onStartTour={()=>{ setTourActive(true); go("dashboard"); }}/>
          )}

          {/* Toolbox (App Store) */}
          {nav==="toolbox"&&(
            <Suspense fallback={<LazyFallback/>}>
              <ToolboxPage C={C} dark={dark} go={go}/>
            </Suspense>
          )}

          {/* BAM Film Studio — beta-gated to allowlist. Sub-routes
              (/film/watch/<id>, /film/collection/<id>) all mount the
              same FilmStudio router; the router parses the hash itself
              and switches between Library / Watch / Collection views. */}
          {(nav==="film" || nav.startsWith("film/"))&&(
            filmStudioOn ? (
              <Suspense fallback={<LazyFallback/>}>
                <FilmStudio C={C} dark={dark} go={go} sbUser={sbUser}/>
              </Suspense>
            ) : (
              <ComingSoonScreen
                name="BAM Film Studio"
                blurb="Tag plays, build clip libraries, and break down film with the BAM tagging system. Opening to all coaches in the next update."
                eta="Coming this month"
                dark={dark} C={C} onBack={()=>go("dashboard")}/>
            )
          )}

          {/* My Coaching Portal — unified hub for plans, films, voice
              notes, and bookmarks. Lazy because not every session opens
              it; the page itself fetches in parallel from user_data,
              films, and film_notes. */}
          {nav==="portal"&&(
            <Suspense fallback={<LazyFallback/>}>
              <PortalPage C={C} dark={dark} go={go} sbUser={sbUser}/>
            </Suspense>
          )}

          {/* Parked — full-video Auto-AFS Lab + Compare (model-picker
              "analyze the video" pipeline). Not a current direction; coaches
              tag manually with the Co-pilot in AFS Mode. Components, routes,
              lazy imports, and the parseHash afs-compare clause are all
              disabled together — re-enable them plus this render block (all
              tagged "Parked — full-video Auto-AFS") to bring the surface back. */}

          {/* ShotFlow Lab — shooting-fluidity analyzer (private beta). */}
          {nav==="lab/shotflow"&&(
            <Suspense fallback={<LazyFallback/>}>
              <ShotflowLab C={C} dark={dark} go={go} sbUser={sbUser}/>
            </Suspense>
          )}

          {/* AFS Co-pilot accuracy — scores the feedback corpus (private beta). */}
          {nav==="lab/afs-copilot"&&(
            <Suspense fallback={<LazyFallback/>}>
              <AfsCopilotAccuracy C={C} dark={dark} go={go} sbUser={sbUser}/>
            </Suspense>
          )}

          {/* Advantage Flow: the dashboard, definitions, and a game being narrated (afs/game/<film id>).
              Web only; the page checks access itself. */}
          {(nav==="afs"||nav.startsWith("afs/"))&&!isNative()&&(
            <Suspense fallback={<LazyFallback/>}>
              <AFSPilot C={C} go={go} sbUser={sbUser} nav={nav}/>
            </Suspense>
          )}

          {/* BAM IQ's page. Staff only, web only (bamiqAvailable); anyone else is sent home. */}
          {nav==="iq"&&bamiqOn&&(
            <div style={{flex:1,minHeight:0,display:"flex",flexDirection:"column"}}>
              <BamIQWorkspace C={C} go={go} isMobile={isMobile}/>
            </div>
          )}

          {/* My Players */}
          {nav==="dev-studio"&&(
            devStudioOn ? (
              <Suspense fallback={<LazyFallback/>}>
                <PlayerDevStudio C={C} dark={dark}/>
              </Suspense>
            ) : (
              <ComingSoonScreen
                name="My Players"
                blurb="One-on-one player development planning, session logging, and progression tracking. Rolling out shortly after launch."
                eta="Coming this month"
                dark={dark} C={C} onBack={()=>go("dashboard")}/>
            )
          )}

          {/* BAM Consulting — landing + org dashboard. Both beta-gated
              to the allowlist (constants/consulting.js:
              consultingLockedToAllowlist) for the public landing; the
              org dashboard is gated by simply having a membership row,
              which is admin-only to grant in Phase 1. */}
          {nav==="consulting"&&(
            <Suspense fallback={<LazyFallback/>}>
              <ConsultingLanding C={C} dark={dark} sbUser={sbUser} go={go}/>
            </Suspense>
          )}
          {nav==="org"&&(
            <Suspense fallback={<LazyFallback/>}>
              <OrgDashboard C={C} dark={dark} go={go}/>
            </Suspense>
          )}
          {nav==="interns"&&!internHubAvailable()&&(
            <div style={{padding:"40px 24px",maxWidth:520,margin:"0 auto",color:C.textMid,fontSize:14,lineHeight:1.6}}>
              The Intern Hub is on the web for now. Open platform.byanymeanscoaches.com in your browser and go to Intern Hub.
            </div>
          )}
          {nav==="interns"&&internHubAvailable()&&(
            <Suspense fallback={<LazyFallback/>}>
              <InternHub C={C} dark={dark} go={go} sbUser={sbUser} onUpload={()=>setShowUpload(true)}/>
            </Suspense>
          )}
          {nav==="filmdesk"&&!filmdeskAvailable()&&(
            <div style={{padding:"40px 24px",maxWidth:520,margin:"0 auto",color:C.textMid,fontSize:14,lineHeight:1.6}}>
              Film Desk is on the web for now. Open platform.byanymeanscoaches.com in your browser.
            </div>
          )}
          {nav==="filmdesk"&&filmdeskAvailable()&&(
            <Suspense fallback={<LazyFallback/>}>
              <FilmDesk C={C} dark={dark} go={go} sbUser={sbUser} isAdmin={isAdmin}/>
            </Suspense>
          )}
          {/* (Admin/* routes are handled by the early return at the top
              of the render fn — the section components render the
              entire layout themselves, including AdminLayout sidebar
              and the in-section tab strip.) */}

          {/* BAM Certified */}
          {isCertRoute(nav)&&(
            <Suspense fallback={<LazyFallback/>}>
              <CertificationPage C={C} dark={dark} go={go}
                certRoute={nav==="certified"?(certAccess?"units":"landing"):nav.replace("certified/","")}
                purchased={certAccess} setPurchased={setCertPurchased}
                progress={certProgress} saveProgress={saveCertProgress}
                userName={appProfile.name}/>
            </Suspense>
          )}

          {/* 404 fallback for unmatched nav */}
          {!isContentPage&&!isCertRoute(nav)&&nav!=="dashboard"&&nav!=="iq"&&nav!=="filmdesk"&&nav!=="afs"&&!nav.startsWith("afs/")&&nav!=="community"&&nav!=="calls"&&nav!=="chats"&&nav!=="referrals"&&nav!=="resources"&&nav!=="playbook"&&nav!=="settings"&&nav!=="dev-studio"&&nav!=="toolbox"&&nav!=="vocab"&&nav!=="foundations"&&nav!=="learn"&&nav!=="learn-home"&&nav!=="tour"&&nav!=="film"&&!nav.startsWith("film/")&&!nav.startsWith("lab/")&&nav!=="portal"&&nav!=="consulting"&&nav!=="org"&&!nav.startsWith("research")&&nav!=="drop"&&nav!=="drops"&&!nav.startsWith("drops/")&&!nav.startsWith("admin/")&&(
            <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",padding:40,textAlign:"center"}}>
              <img src={BAM_LOGO_PNG} alt="BAM" style={{width:56,height:56,objectFit:"contain",marginBottom:24,opacity:.6}} />
              <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:72,color:GOLD,letterSpacing:4,lineHeight:1}}>404</div>
              <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:22,color:C.text,letterSpacing:3,marginTop:8}}>PAGE NOT FOUND</div>
              <div style={{fontSize:14,color:C.textDim,marginTop:12,maxWidth:340,lineHeight:1.6}}>Looks like this play didn't draw up right.</div>
              <button onClick={()=>go("dashboard")}
                style={{marginTop:24,padding:"10px 28px",borderRadius:8,fontSize:14,fontWeight:800,background:GOLD,color:"#111",border:"none",cursor:"pointer",fontFamily:"'Bebas Neue',sans-serif",letterSpacing:1}}>
                Go Back
              </button>
            </div>
          )}
        </main></BAMErrorBoundary>
      {/* One-time launch note for the set of the day. Waits until the coach
          is signed in, subscribed and past profile setup. */}
      <SetsLaunchModal C={C} go={go} ready={!!sbUser && isSubscribed===true && !showProfileSetup && nav!=="tour"}/>
      {profileName&&<ProfilePanel key={profileName} name={profileName} dark={dark} C={C} onClose={()=>setProfileName(null)} go={go}
        onOpenPost={(id)=>{ setProfileName(null); openPostInFeed(id); }}
        onSaved={(u)=>{
          setAppProfile(p=>({ ...p, name:u.name||p.name, role:u.role, org:u.org, location:u.location, bio:u.bio, ig:u.ig, x:u.x, website:u.website, handle:u.handle }));
          if(u.photo) setAppPhoto(u.photo);
          try{ localStorage.setItem("bam_user_name", (u.name||"").split(" ")[0]||"Coach"); }catch{}
          if(u.name && u.name!==profileName) setProfileName(u.name);
        }}/>}
      {myProfileOpen&&<MyProfilePanel C={C} dark={dark} onClose={()=>setMyProfileOpen(false)}
        profile={appProfile} onProfileUpdate={setAppProfile} photo={appPhoto} onPhotoUpdate={setAppPhoto}
        dismissedBanners={dismissedBanners} dismissBanner={dismissBanner}
        sbUser={sbUser} onError={showApiError} userStats={userStats} go={go}
        onEditProfile={()=>{ setMyProfileOpen(false); if(appProfile?.name) setProfileName(appProfile.name); }}/>}
      {mapOpen&&(
        <Suspense fallback={<LazyFallback/>}>
          {/* Profile click keeps the map open underneath. ProfilePanel
              z-index (5000) sits above the map overlay (4000), so the
              sidebar slides in over the map; closing it returns the
              user to the still-open network map instead of bouncing
              them back to community. */}
          <MemberMapOverlay C={C} dark={dark} onClose={()=>setMapOpen(false)} onProfileClick={(name)=>setProfileName(name)}/>
        </Suspense>
      )}
      {showUpload&&<UploadPortal C={C} dark={dark} onClose={()=>setShowUpload(false)} profile={appProfile}/>}
      {showFeaturePicker&&(
        <Suspense fallback={null}>
          <FeaturePicker C={C} onClose={()=>setShowFeaturePicker(false)}
            onPick={(e)=>{
              setEmbeddedContent(prev=>[...(prev||[]),e]);
              setShowFeaturePicker(false);
              setFocused(true);
            }}/>
        </Suspense>
      )}
      {showDrillPicker&&(
        <Suspense fallback={null}>
          <DrillPicker C={C} onClose={()=>setShowDrillPicker(false)}
            onPick={(e)=>{
              setEmbeddedContent(prev=>[...(prev||[]),e]);
              setShowDrillPicker(false);
              // The composer's expanded state is `focused`, and the chip row
              // and Post button live inside it. Without this the drill attaches
              // to a collapsed composer and nothing appears to have happened —
              // which is exactly what the film picker already knew.
              setFocused(true);
              // The picker can be opened from anywhere on the page. Put the
              // composer back on screen so the coach sees what they just did.
              setTimeout(()=>{
                try {
                  document.querySelector(".bam-community-composer")
                    ?.scrollIntoView({ behavior:"smooth", block:"center" });
                } catch {}
              }, 60);
            }}/>
        </Suspense>
      )}
      {/* Rendered over everything: this is a step in the checkout flow, not a
          banner competing with the dashboard behind it. */}
      {promptForApp&&(
        <Suspense fallback={null}>
          <GetTheApp C={C} onClose={()=>setPromptForApp(false)}/>
        </Suspense>
      )}
      {tourActive&&<TourOverlay onComplete={()=>{
        setTourActive(false);
        try { localStorage.setItem("bam_tour_completed","1"); } catch {}
        import("./utils/userData").then(({ saveUserData }) => saveUserData("tour_completed", true)).catch(()=>{});
        // Tick the Start Here step too, through state, so the notice card
        // moves on now rather than the next time something else re-ranks it.
        setStartHereState(prev => {
          if (!prev || prev.tour) return prev;
          const next = { ...prev, tour: new Date().toISOString() };
          saveUserData("start_here", next).catch(() => {});
          return next;
        });
        logEvent("tour_completed", {});
      }} onNavigate={go}/>}
      {filmIntroOpen && (
        <FilmStudioIntroModal onClose={() => setFilmIntroOpen(false)}/>
      )}
      <CommandPalette open={cmdPaletteOpen} onClose={()=>setCmdPaletteOpen(false)} go={go} dark={dark}/>
      <MobileBottomNav nav={nav} onNavigate={go} groups={phoneGroups}/>
      {/* AskBAM is hidden in the native app for v1 (Coleman's call) but stays
          on web, incl. mobile web. Drop the !isNative() guard to bring it back.
          Chats and Calls were briefly behind this button too; they are tiles
          in the sidebar now (SidebarLive), which Coleman found more obvious. */}
      {askbamOn && !isNative() && !bamiqOn && <AskBAM dark={dark}/>}
      {/* BAM IQ: the orb in the corner is the one way in, and replaces Ask BAM's bubble
          for anyone BAM IQ is open to. Off unless bamiqAvailable(). */}
      <BamIQOrb C={C} isMobile={isMobile} hidden={nav==="iq" || nav==="afs" || nav.startsWith("afs/")}/>
      <BamIQPanel C={C} go={go} isMobile={isMobile}/>
      {/* The new-layout tour for coaches who knew the old sidebar: a card,
          Show me's four stops, and a week of gold dots (LayoutTour). */}
      {/* Never on Advantage Flow (2026-10-25): its taggers and AFS-only coaches aren't platform members. */}
      <LayoutTour enabled={appMode==="platform" && !!sbUser?.id && onboarded && !tourActive && !authLoading && nav!=="afs" && !nav.startsWith("afs/")}
        joinedAt={sbUser?.created_at} nav={nav} isMobile={isMobile} profileMenuOpen={profileMenuOpen}/>
      {/* One-time "this is new" pop-ups (constants/featureIntros.js). */}
      <FeatureIntro enabled={appMode==="platform" && !!sbUser?.id && onboarded && !tourActive && !authLoading && nav!=="afs" && !nav.startsWith("afs/")}
        joinedAt={sbUser?.created_at}
        onAction={(a)=>{ if(a==="map") setMapOpen(true); else if(a==="film") go("film"); }}/>
      <UserAdminPanel C={C} dark={dark}/>
      {showFilmPicker && (
        <FilmMomentPicker
          onAttach={(embed)=>{
            setEmbeddedContent(prev => [...(prev || []), embed]);
            setShowFilmPicker(false);
            setFocused(true);
          }}
          onClose={()=>setShowFilmPicker(false)}
        />
      )}
      <CreateYourWorkoutModal
        C={C} dark={dark}
        open={showWorkoutBuilder}
        onClose={()=>setShowWorkoutBuilder(false)}
        onOpenDrill={(d)=>{setShowWorkoutBuilder(false); openDrillItem(d);}}
        onBuildPlan={()=>{setShowWorkoutBuilder(false); go("plans");}}
      />
      </div>
    </div>
    </BamIQProvider>
  );
}
